import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./styles/global.css";
import "./housemind.css";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ToastProvider, useToast } from "./components/shared/Toast";
import VersionAutoUpdater from "./components/shared/VersionAutoUpdater";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PeoplePage from "./pages/PeoplePage";
import PersonDetail from "./pages/PersonDetail";
import GiftSuggestPage from "./pages/GiftSuggestPage";
import SettingsPage from "./pages/SettingsPage";
import FamilyPage from "./pages/FamilyPage";
import FamilyInvitePage from "./pages/FamilyInvitePage";
import SharedContentViewer from "./pages/SharedContentViewer";
import GuidePage from "./pages/GuidePage";
import SavingNotice from "./components/shared/SavingNotice";
import ConfirmModal from "./components/shared/ConfirmModal";
import CenteredModal from "./components/shared/CenteredModal";
import SheetChrome, { SheetLogoRow } from "./components/shared/SheetChrome";
import useMinLoad from "./hooks/useMinLoad";
import useFormPresentation from "./hooks/useFormPresentation";
import useBackButton from "./hooks/useBackButton";
import {
  pantryApi,
  mealsApi,
  shoppingApi,
  affiliateApi,
  dashboardApi,
  notificationApi,
  sharingApi,
  familyApi,
  eventsApi,
  importCandidatesApi,
  petsApi,
  setImpersonationAuthToken,
} from "./services/api";
import { API_BASE } from "./utils/apiBase";
import HomeVaultPage from "./pages/HomeVaultPage";
import VehiclesPage from "./pages/VehiclesPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import UtilitiesPage from "./pages/UtilitiesPage";
import PublicEventPage from "./pages/PublicEventPage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboard from "./pages/AdminDashboard";
import SmartHomePage from "./pages/SmartHomePage";
import ModuleHeader from "./components/shared/ModuleHeader";
import LandingPage from "./pages/LandingPage";
import WelcomePage from "./pages/WelcomePage";
import EventsPage from "./pages/EventsPage";
import TripPage from "./pages/TripPage";
import EventPlanPage from "./pages/EventPlanPage";
import TimelinePage from "./pages/TimelinePage";
import ConnectionsPage from "./pages/ConnectionsPage";
import NavigationAreaPage from "./pages/NavigationAreaPage";
import HomeCarePage from "./pages/HomeCarePage";
import MoneyOverviewPage from "./pages/MoneyOverviewPage";
import PropertyProfilePage from "./pages/PropertyProfilePage";
import EmergencySafetyPage from "./pages/EmergencySafetyPage";
import PetsPage from "./pages/PetsPage";
import LostPetPublicPage from "./pages/LostPetPublicPage";
import { usePwaInstall } from "./utils/pwaInstall";

const IMG_FOOD =
  "https://images.unsplash.com/photo-1543353071-10c8ba85a904?w=1400&q=75&auto=format&fit=crop";
const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const APP_SHARE_PATH = "/app";
const FOOD_RECENT_AVOID_KEY = "hm_food_recent_avoid_meals";
const FOOD_LOVED_MEALS_KEY = "hm_food_loved_meals";
const PORTRAIT_TABLET_QUERY =
  "(min-width: 769px) and (max-width: 1024px) and (orientation: portrait)";

const SHEET_SCROLL_LOCK_SELECTOR = [
  ".hm-global-ask.is-open",
  ".hm-gifts-sheet-overlay",
  ".hm-sheet-overlay",
  ".fd-mobile-sheet-backdrop",
  ".hm-vault-mobile-sheet-shell",
  ".fd-clear-overlay",
  ".gf-edit-cover-overlay",
  ".gf-people-overlay",
  ".gf-gift-ideas-overlay",
  ".gf-full-profile-overlay",
  ".hm-shared-sheet-overlay",
].join(",");

const SHEET_SCROLL_SURFACE_SELECTOR = [
  ".gf-add-person-sheet",
  ".gf-edit-cover-sheet",
  ".gf-edit-cover-body",
  ".gf-people-sheet",
  ".gf-sheet-body",
  ".gf-profile-body",
  ".hm-gifts-person-detail",
  ".hm-gifts-sheet-actions",
  ".gift-suggest-page",
  ".hm-gifts-action-sheet",
  ".hm-gifts-confirm-sheet",
  ".hm-action-sheet",
  ".hm-veh-service-sheet",
  ".hm-veh-add-sheet",
  ".hm-veh-action-modal",
  ".hm-vault-mobile-sheet",
  ".hm-util-drawer",
  ".hm-util-scan-sheet",
  ".fd-mobile-sheet",
  ".hm-global-ask-panel",
  ".hm-shared-decision-sheet",
].join(",");

function askContextStorageKey(context = null) {
  const moduleKey =
    String(context?.module || context?.module_label || "global")
      .trim()
      .toLowerCase() || "global";
  const surfaceKey = String(context?.surface || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `hm_ask_thread:${moduleKey}${surfaceKey ? `:${surfaceKey}` : ""}`;
}

function legacyAskContextStorageKey(context = null) {
  const moduleKey =
    String(context?.module || context?.module_label || "global")
      .trim()
      .toLowerCase() || "global";
  const surfaceKey = String(context?.surface || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `hm_ask_last_result:${moduleKey}${surfaceKey ? `:${surfaceKey}` : ""}`;
}

function assistantMessageFromAskResult(result, context = null) {
  if (!result?.answer) return null;
  return {
    id: result.id || `assistant-${result.saved_at || Date.now()}`,
    role: "assistant",
    text: result.answer,
    result,
    context: result.context || context || null,
    created_at: result.saved_at || new Date().toISOString(),
  };
}

function userMessageFromQuestion(question, context = null, createdAt = null) {
  const text = String(question || "").trim();
  if (!text) return null;
  return {
    id: `user-${createdAt || Date.now()}`,
    role: "user",
    text,
    context: context || null,
    created_at: createdAt || new Date().toISOString(),
  };
}

function normalizeAskMessages(value, context = null) {
  if (Array.isArray(value))
    return value.filter((message) => message?.role && message?.text);
  if (value?.messages && Array.isArray(value.messages)) {
    return value.messages.filter((message) => message?.role && message?.text);
  }
  if (value?.answer) {
    const messages = [];
    const userMessage = userMessageFromQuestion(
      value.question,
      context,
      value.saved_at,
    );
    const assistantMessage = assistantMessageFromAskResult(value, context);
    if (userMessage) messages.push(userMessage);
    if (assistantMessage) messages.push(assistantMessage);
    return messages;
  }
  return [];
}

function readAskMessages(context = null) {
  try {
    const stored = JSON.parse(
      localStorage.getItem(askContextStorageKey(context)) || "null",
    );
    const messages = normalizeAskMessages(stored, context);
    if (messages.length) return messages;

    const legacy = JSON.parse(
      localStorage.getItem(legacyAskContextStorageKey(context)) || "null",
    );
    const legacyMessages = normalizeAskMessages(legacy, context);
    if (legacyMessages.length) writeAskMessages(context, legacyMessages);
    return legacyMessages;
  } catch {
    return [];
  }
}

function writeAskMessages(context = null, messages = []) {
  try {
    localStorage.setItem(
      askContextStorageKey(context),
      JSON.stringify({
        version: 1,
        messages: normalizeAskMessages(messages, context).slice(-20),
        updated_at: new Date().toISOString(),
      }),
    );
  } catch {}
}

function clearAskMessages(context = null) {
  try {
    localStorage.removeItem(askContextStorageKey(context));
    localStorage.removeItem(legacyAskContextStorageKey(context));
  } catch {}
}

function askHistoryPayload(messages = []) {
  return normalizeAskMessages(messages)
    .slice(-8)
    .map((message) => ({
      role: message.role,
      text: String(message.text || "").slice(0, 800),
    }));
}

const GLOBAL_ASK_CONTEXTS = [
  { key: "all", label: "All", module: null, module_label: "Dashboard" },
  { key: "food", label: "Food", module: "food", module_label: "Food" },
  {
    key: "people",
    label: "Gifts",
    module: "people",
    module_label: "People & Gifts",
  },
  {
    key: "utilities",
    label: "Utilities",
    module: "utilities",
    module_label: "Utilities",
  },
  {
    key: "vehicles",
    label: "Vehicles",
    module: "vehicles",
    module_label: "Vehicles",
  },
  {
    key: "events",
    label: "Trips",
    module: "events",
    module_label: "Events & Trips",
  },
  {
    key: "subscriptions",
    label: "Subs",
    module: "subscriptions",
    module_label: "Subscriptions",
  },
  {
    key: "home_care",
    label: "Home Care",
    module: "home_care",
    module_label: "Home Care",
  },
  {
    key: "property_profile",
    label: "Property",
    module: "property_profile",
    module_label: "Property Profile",
  },
  {
    key: "safety",
    label: "Safety",
    module: "emergency_safety",
    module_label: "Emergency & Safety",
  },
  { key: "pets", label: "Pets", module: "pets", module_label: "Pets" },
  {
    key: "budget",
    label: "Budget",
    module: "budget",
    module_label: "Household Budget",
  },
];

function globalAskContextFromKey(key = "all") {
  const item =
    GLOBAL_ASK_CONTEXTS.find((context) => context.key === key) ||
    GLOBAL_ASK_CONTEXTS[0];
  if (!item.module) return null;
  return {
    module: item.module,
    module_label: item.module_label,
    surface: "Dashboard Ask",
    global_filter: item.label,
  };
}

function useGlobalSheetScrollLock() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const body = document.body;
    const root = document.documentElement;
    let locked = false;
    let scrollY = 0;
    let previousBody = {};
    let previousRootOverflow = "";
    let previousScrollContainers = [];

    const hasOpenSheet = () =>
      Boolean(document.querySelector(SHEET_SCROLL_LOCK_SELECTOR));
    const scrollContainers = () =>
      Array.from(
        document.querySelectorAll(".hm-app, .hm-main, .hm-sidebar, .layout"),
      );

    const lock = () => {
      if (locked) return;
      locked = true;
      scrollY = window.scrollY || root.scrollTop || 0;
      previousBody = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
      };
      previousRootOverflow = root.style.overflow;
      previousScrollContainers = scrollContainers().map((node) => ({
        node,
        overflow: node.style.overflow,
        overflowY: node.style.overflowY,
        touchAction: node.style.touchAction,
      }));
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
      body.style.overflow = "hidden";
      root.style.overflow = "hidden";
      previousScrollContainers.forEach(({ node }) => {
        node.style.overflow = "hidden";
        node.style.overflowY = "hidden";
        node.style.touchAction = "none";
      });
    };

    const unlock = () => {
      if (!locked) return;
      locked = false;
      body.style.position = previousBody.position || "";
      body.style.top = previousBody.top || "";
      body.style.left = previousBody.left || "";
      body.style.right = previousBody.right || "";
      body.style.width = previousBody.width || "";
      body.style.overflow = previousBody.overflow || "";
      root.style.overflow = previousRootOverflow || "";
      previousScrollContainers.forEach(
        ({ node, overflow, overflowY, touchAction }) => {
          node.style.overflow = overflow || "";
          node.style.overflowY = overflowY || "";
          node.style.touchAction = touchAction || "";
        },
      );
      previousScrollContainers = [];
      window.scrollTo(0, scrollY);
    };

    const sync = () => {
      if (hasOpenSheet()) lock();
      else unlock();
    };

    const observer = new MutationObserver(sync);
    observer.observe(body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    const preventBackdropTouchMove = (event) => {
      if (!locked) return;
      const target = event.target;
      if (target?.closest?.(SHEET_SCROLL_SURFACE_SELECTOR)) return;
      event.preventDefault();
    };
    document.addEventListener("touchmove", preventBackdropTouchMove, {
      passive: false,
    });
    sync();

    return () => {
      observer.disconnect();
      document.removeEventListener("touchmove", preventBackdropTouchMove);
      unlock();
    };
  }, []);
}

const MODULES = [
  {
    name: "Food",
    path: "/app/food",
    icon: "◌",
    detail: "Pantry, meal plans, shopping",
  },
  {
    name: "Timeline",
    path: "/app/timeline",
    icon: "▤",
    detail: "Unified household agenda",
  },
  {
    name: "Connections",
    path: "/app/connections",
    icon: "⌁",
    detail: "Discovery coverage and sync health",
  },
  {
    name: "Events & Trips",
    path: "/app/events",
    icon: "✈",
    detail: "Trips, events, packing, AI planning",
  },
  {
    name: "People & Gifts",
    path: "/app/people",
    icon: "◎",
    detail: "People, occasions, gift ideas",
  },
  {
    name: "Home Vault",
    path: "/app/home-vault",
    icon: "□",
    detail: "Warranties, receipts, manuals",
  },
  {
    name: "Home Care",
    path: "/app/home-care",
    icon: "🛠️",
    detail: "Maintenance, repairs, projects",
  },
  {
    name: "Property Profile",
    path: "/app/property-profile",
    icon: "🏠",
    detail: "Rooms, systems, materials, and home facts",
  },
  {
    name: "Emergency & Safety",
    path: "/app/emergency-safety",
    icon: "🛡️",
    detail: "Plans, shutoffs, checks, and contacts",
  },
  {
    name: "Pets",
    path: "/app/pets",
    icon: "🐾",
    detail: "Profiles, routines, appointments, and care",
  },
  {
    name: "Vehicles",
    path: "/app/vehicles",
    icon: "✦",
    detail: "Mileage, service, quote checks",
  },
  {
    name: "Subscriptions",
    path: "/app/subscriptions",
    icon: "◇",
    detail: "Renewals and cancellation nudges",
  },
  {
    name: "Utilities",
    path: "/app/utilities",
    icon: "▱",
    detail: "Usage spikes, due dates, savings",
  },
];

// ── Food module (rebuilt) ──────────────────────────────────────────────────
// Tabs: Pantry | Meal Plan | Recipes | Shopping | Preferences
// Allergy hard-filter runs server-side; frontend just shows what's safe.

const FOOD_TABS = ["Pantry", "Meal Plan", "Recipes", "Shopping", "Preferences"];

const CATEGORY_OPTS = [
  "produce",
  "dairy",
  "meat",
  "seafood",
  "frozen",
  "fridge",
  "pantry",
  "beverages",
  "snacks",
  "bakery",
  "other",
];
const LOCATION_OPTS = ["fridge", "freezer", "pantry", "counter", "cellar"];

// Category → emoji fallback
const CATEGORY_EMOJI = {
  produce: "🥦",
  dairy: "🥛",
  meat: "🥩",
  seafood: "🐟",
  frozen: "🧊",
  fridge: "🧊",
  pantry: "🫙",
  beverages: "🧃",
  snacks: "🍿",
  bakery: "🍞",
  other: "📦",
};

// Name-based emoji (checks item name first, falls back to category)
function pantryEmoji(item) {
  const n = String(item.name || "").toLowerCase();
  if (/egg/i.test(n)) return "🥚";
  if (/milk powder|formula|nido/i.test(n)) return "🍼";
  if (/\bmilk\b/i.test(n)) return "🥛";
  if (/butter/i.test(n)) return "🧈";
  if (/cheese/i.test(n)) return "🧀";
  if (/yogurt|yoghurt/i.test(n)) return "🫙";
  if (/cream/i.test(n)) return "🥛";
  if (/chicken/i.test(n)) return "🍗";
  if (/beef|steak/i.test(n)) return "🥩";
  if (/fish|salmon|tuna|tilapia/i.test(n)) return "🐟";
  if (/shrimp|prawn/i.test(n)) return "🦐";
  if (/apple/i.test(n)) return "🍎";
  if (/banana|plantain/i.test(n)) return "🍌";
  if (/orange/i.test(n)) return "🍊";
  if (/lemon/i.test(n)) return "🍋";
  if (/tomato/i.test(n)) return "🍅";
  if (/sweet potato/i.test(n)) return "🍠";
  if (/potato/i.test(n)) return "🥔";
  if (/carrot/i.test(n)) return "🥕";
  if (/onion/i.test(n)) return "🧅";
  if (/garlic/i.test(n)) return "🧄";
  if (/pepper|chilli|chili/i.test(n)) return "🌶️";
  if (/corn|maize/i.test(n)) return "🌽";
  if (/broccoli/i.test(n)) return "🥦";
  if (/lettuce|spinach|kale/i.test(n)) return "🥬";
  if (/mushroom/i.test(n)) return "🍄";
  if (/avocado/i.test(n)) return "🥑";
  if (/strawberry/i.test(n)) return "🍓";
  if (/grape/i.test(n)) return "🍇";
  if (/watermelon/i.test(n)) return "🍉";
  if (/pineapple/i.test(n)) return "🍍";
  if (/mango/i.test(n)) return "🥭";
  if (/rice/i.test(n)) return "🍚";
  if (/pasta|spaghetti|noodle/i.test(n)) return "🍝";
  if (/bread/i.test(n)) return "🍞";
  if (/flour/i.test(n)) return "🌾";
  if (/sugar/i.test(n)) return "🍬";
  if (/salt/i.test(n)) return "🧂";
  if (/oil|olive/i.test(n)) return "🫒";
  if (/sauce|ketchup/i.test(n)) return "🥫";
  if (/soup|stock|broth/i.test(n)) return "🍲";
  if (/beans|lentil|pea\b/i.test(n)) return "🫘";
  if (/cereal|oat/i.test(n)) return "🥣";
  if (/coffee/i.test(n)) return "☕";
  if (/tea\b/i.test(n)) return "🍵";
  if (/juice/i.test(n)) return "🧃";
  if (/water\b/i.test(n)) return "💧";
  if (/wine/i.test(n)) return "🍷";
  if (/beer/i.test(n)) return "🍺";
  if (/chocolate|cocoa/i.test(n)) return "🍫";
  if (/honey/i.test(n)) return "🍯";
  if (/jam|jelly/i.test(n)) return "🫙";
  if (/nut|almond|cashew|walnut/i.test(n)) return "🥜";
  if (/cake|cookie|biscuit/i.test(n)) return "🍪";
  if (/pizza/i.test(n)) return "🍕";
  if (/ice cream|gelato/i.test(n)) return "🍦";
  return CATEGORY_EMOJI[item.category] || "📦";
}

// Common pantry items — {name, location, category, unit?}
const QUICK_ITEMS = [
  // Fridge — Dairy
  { name: "Milk", location: "fridge", category: "dairy", unit: "gallon" },
  { name: "Eggs", location: "fridge", category: "dairy", unit: "dozen" },
  { name: "Butter", location: "fridge", category: "dairy", unit: "lb" },
  { name: "Cheddar cheese", location: "fridge", category: "dairy" },
  { name: "Mozzarella", location: "fridge", category: "dairy" },
  { name: "Parmesan", location: "fridge", category: "dairy" },
  { name: "Greek yogurt", location: "fridge", category: "dairy", unit: "oz" },
  { name: "Sour cream", location: "fridge", category: "dairy" },
  { name: "Heavy cream", location: "fridge", category: "dairy", unit: "cup" },
  { name: "Cream cheese", location: "fridge", category: "dairy" },
  { name: "Cottage cheese", location: "fridge", category: "dairy" },
  // Fridge — Produce
  { name: "Spinach", location: "fridge", category: "produce" },
  { name: "Lettuce", location: "fridge", category: "produce" },
  { name: "Kale", location: "fridge", category: "produce" },
  { name: "Broccoli", location: "fridge", category: "produce" },
  { name: "Carrots", location: "fridge", category: "produce", unit: "lb" },
  { name: "Celery", location: "fridge", category: "produce" },
  { name: "Bell peppers", location: "fridge", category: "produce" },
  { name: "Cucumber", location: "fridge", category: "produce" },
  { name: "Zucchini", location: "fridge", category: "produce" },
  { name: "Mushrooms", location: "fridge", category: "produce" },
  { name: "Tomatoes", location: "counter", category: "produce" },
  { name: "Onions", location: "pantry", category: "produce", unit: "lb" },
  { name: "Garlic", location: "pantry", category: "produce", unit: "head" },
  { name: "Potatoes", location: "pantry", category: "produce", unit: "lb" },
  {
    name: "Sweet potatoes",
    location: "pantry",
    category: "produce",
    unit: "lb",
  },
  { name: "Avocado", location: "counter", category: "produce" },
  { name: "Lemons", location: "fridge", category: "produce" },
  { name: "Limes", location: "fridge", category: "produce" },
  { name: "Apples", location: "fridge", category: "produce" },
  { name: "Bananas", location: "counter", category: "produce" },
  { name: "Blueberries", location: "fridge", category: "produce" },
  { name: "Strawberries", location: "fridge", category: "produce" },
  // Fridge — Meat
  { name: "Chicken breast", location: "fridge", category: "meat", unit: "lb" },
  { name: "Ground beef", location: "fridge", category: "meat", unit: "lb" },
  { name: "Bacon", location: "fridge", category: "meat" },
  { name: "Deli turkey", location: "fridge", category: "meat" },
  { name: "Deli ham", location: "fridge", category: "meat" },
  {
    name: "Salmon fillet",
    location: "fridge",
    category: "seafood",
    unit: "lb",
  },
  // Freezer
  { name: "Chicken breast", location: "freezer", category: "meat", unit: "lb" },
  { name: "Ground beef", location: "freezer", category: "meat", unit: "lb" },
  { name: "Shrimp", location: "freezer", category: "seafood", unit: "lb" },
  { name: "Salmon", location: "freezer", category: "seafood", unit: "lb" },
  { name: "Frozen peas", location: "freezer", category: "frozen" },
  { name: "Frozen corn", location: "freezer", category: "frozen" },
  { name: "Frozen spinach", location: "freezer", category: "frozen" },
  { name: "Frozen broccoli", location: "freezer", category: "frozen" },
  { name: "Edamame", location: "freezer", category: "frozen" },
  { name: "Ice cream", location: "freezer", category: "frozen" },
  { name: "Frozen pizza", location: "freezer", category: "frozen" },
  { name: "Frozen waffles", location: "freezer", category: "frozen" },
  // Pantry — Grains / Pasta
  { name: "White rice", location: "pantry", category: "pantry", unit: "lb" },
  { name: "Brown rice", location: "pantry", category: "pantry", unit: "lb" },
  { name: "Pasta", location: "pantry", category: "pantry", unit: "lb" },
  { name: "Spaghetti", location: "pantry", category: "pantry", unit: "lb" },
  { name: "Bread", location: "pantry", category: "bakery" },
  { name: "Tortillas", location: "pantry", category: "pantry" },
  { name: "Oats", location: "pantry", category: "pantry", unit: "cup" },
  { name: "Flour", location: "pantry", category: "pantry", unit: "lb" },
  { name: "Cornmeal", location: "pantry", category: "pantry", unit: "cup" },
  { name: "Quinoa", location: "pantry", category: "pantry", unit: "cup" },
  // Pantry — Canned / Jarred
  { name: "Canned tomatoes", location: "pantry", category: "pantry" },
  { name: "Tomato paste", location: "pantry", category: "pantry" },
  { name: "Tomato sauce", location: "pantry", category: "pantry" },
  { name: "Black beans", location: "pantry", category: "pantry" },
  { name: "Chickpeas", location: "pantry", category: "pantry" },
  { name: "Lentils", location: "pantry", category: "pantry", unit: "cup" },
  { name: "Chicken broth", location: "pantry", category: "pantry", unit: "oz" },
  {
    name: "Vegetable broth",
    location: "pantry",
    category: "pantry",
    unit: "oz",
  },
  { name: "Coconut milk", location: "pantry", category: "pantry" },
  { name: "Tuna", location: "pantry", category: "pantry" },
  { name: "Peanut butter", location: "pantry", category: "pantry" },
  { name: "Almond butter", location: "pantry", category: "pantry" },
  { name: "Honey", location: "pantry", category: "pantry" },
  { name: "Maple syrup", location: "pantry", category: "pantry" },
  { name: "Jam", location: "fridge", category: "pantry" },
  // Pantry — Oils / Condiments
  { name: "Olive oil", location: "pantry", category: "pantry", unit: "oz" },
  { name: "Vegetable oil", location: "pantry", category: "pantry", unit: "oz" },
  { name: "Soy sauce", location: "pantry", category: "pantry" },
  { name: "Hot sauce", location: "fridge", category: "pantry" },
  { name: "Ketchup", location: "fridge", category: "pantry" },
  { name: "Mustard", location: "fridge", category: "pantry" },
  { name: "Mayonnaise", location: "fridge", category: "pantry" },
  { name: "Salsa", location: "fridge", category: "pantry" },
  { name: "Sriracha", location: "fridge", category: "pantry" },
  { name: "Balsamic vinegar", location: "pantry", category: "pantry" },
  { name: "Apple cider vinegar", location: "pantry", category: "pantry" },
  // Pantry — Baking / Spices
  { name: "Sugar", location: "pantry", category: "pantry", unit: "cup" },
  { name: "Brown sugar", location: "pantry", category: "pantry", unit: "cup" },
  { name: "Salt", location: "pantry", category: "pantry" },
  { name: "Black pepper", location: "pantry", category: "pantry" },
  { name: "Cumin", location: "pantry", category: "pantry" },
  { name: "Paprika", location: "pantry", category: "pantry" },
  { name: "Garlic powder", location: "pantry", category: "pantry" },
  { name: "Onion powder", location: "pantry", category: "pantry" },
  { name: "Chili powder", location: "pantry", category: "pantry" },
  { name: "Oregano", location: "pantry", category: "pantry" },
  { name: "Basil", location: "pantry", category: "pantry" },
  { name: "Cinnamon", location: "pantry", category: "pantry" },
  { name: "Baking powder", location: "pantry", category: "pantry" },
  { name: "Baking soda", location: "pantry", category: "pantry" },
  { name: "Vanilla extract", location: "pantry", category: "pantry" },
  // Beverages
  {
    name: "Orange juice",
    location: "fridge",
    category: "beverages",
    unit: "oz",
  },
  {
    name: "Apple juice",
    location: "fridge",
    category: "beverages",
    unit: "oz",
  },
  { name: "Coffee", location: "pantry", category: "beverages" },
  { name: "Tea", location: "pantry", category: "beverages" },
  { name: "Sparkling water", location: "pantry", category: "beverages" },
  // Snacks
  { name: "Almonds", location: "pantry", category: "snacks", unit: "oz" },
  { name: "Cashews", location: "pantry", category: "snacks", unit: "oz" },
  { name: "Walnuts", location: "pantry", category: "snacks", unit: "oz" },
  { name: "Tortilla chips", location: "pantry", category: "snacks" },
  { name: "Crackers", location: "pantry", category: "snacks" },
  { name: "Protein bars", location: "pantry", category: "snacks" },
  { name: "Dark chocolate", location: "pantry", category: "snacks" },
  { name: "Granola", location: "pantry", category: "snacks" },
];

// Popular items shown when field is empty and focused
const POPULAR_ITEMS = [
  "Milk",
  "Eggs",
  "Butter",
  "Chicken breast",
  "Ground beef",
  "Spinach",
  "Bread",
  "Rice",
  "Pasta",
  "Onions",
  "Garlic",
  "Tomatoes",
  "Cheddar cheese",
  "Greek yogurt",
  "Olive oil",
  "Black beans",
];
const POPULAR_QUICK = POPULAR_ITEMS.map((n) =>
  QUICK_ITEMS.find((q) => q.name === n),
).filter(Boolean);

// Quick-add combo-box component
function ItemComboBox({
  value,
  onChange,
  onSelect,
  placeholder = "Item name — type or pick below *",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const query = value.trim().toLowerCase();
  const matches =
    query.length > 0
      ? QUICK_ITEMS.filter((i) => i.name.toLowerCase().includes(query)).slice(
          0,
          10,
        )
      : POPULAR_QUICK; // show popular on empty focus

  // Close on outside click
  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <div ref={ref} className="fd-combo-wrap">
      <input
        className="hm-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <ul className="fd-combo-list">
          {!query && (
            <li className="fd-combo-hint">Popular items — or type to search</li>
          )}
          {matches.map((item) => (
            <li
              key={item.name + item.location}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
                setOpen(false);
              }}
              className="fd-combo-option"
            >
              <span className="fd-combo-name">{item.name}</span>
              <span className="fd-combo-loc">{item.location}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
const DIETARY_OPTS = [
  "omnivore",
  "vegetarian",
  "vegan",
  "pescatarian",
  "keto",
  "paleo",
  "gluten-free",
];
const SKILL_OPTS = ["beginner", "intermediate", "advanced"];
const CUISINE_OPTS = [
  // Americas
  "American",
  "Mexican",
  "Caribbean",
  "Brazilian",
  "Peruvian",
  // Europe
  "Italian",
  "French",
  "Greek",
  "Spanish",
  "Mediterranean",
  // Africa
  "West African",
  "East African",
  "Ethiopian",
  "North African",
  "South African",
  // Middle East & Asia
  "Middle Eastern",
  "Indian",
  "Japanese",
  "Thai",
  "Chinese",
  "Korean",
  "Vietnamese",
];

const COMMON_ALLERGENS = [
  "Peanuts",
  "Tree nuts",
  "Dairy / Milk",
  "Eggs",
  "Wheat / Gluten",
  "Soy",
  "Shellfish",
  "Fish",
  "Sesame",
  "Corn",
  "Mustard",
  "Sulfites",
  "Lupin",
];

const COMMON_DISLIKES = [
  "Cilantro",
  "Olives",
  "Mushrooms",
  "Raw onions",
  "Blue cheese",
  "Anchovies",
  "Liver",
  "Tofu",
  "Brussels sprouts",
  "Beets",
  "Celery",
  "Coconut",
  "Eggplant",
  "Lamb",
  "Raisins",
  "Bitter melon",
];

function expiryPill(item) {
  if (!item.expiry_date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(item.expiry_date + "T00:00:00");
  const diff = Math.round((exp - today) / 86400000);
  if (diff < 0)
    return (
      <span className="fd-exp-pill fd-exp-pill--urgent">
        {diff === -1 ? "Expired yesterday" : `Expired ${Math.abs(diff)} days ago`}
      </span>
    );
  if (diff === 0)
    return <span className="fd-exp-pill fd-exp-pill--urgent">Exp today</span>;
  if (diff === 1)
    return (
      <span className="fd-exp-pill fd-exp-pill--urgent">Exp tomorrow</span>
    );
  if (diff <= 7)
    return (
      <span className="fd-exp-pill fd-exp-pill--soon">Exp {diff} days</span>
    );
  const mon = exp.toLocaleString("en-US", { month: "short" });
  return <span className="fd-exp-pill">{`Exp ${mon} ${exp.getDate()}`}</span>;
}

// ── Pantry add-item auto-fill ─────────────────────────────────────────────
const CATEGORY_AUTOFILL = {
  egg: "dairy",
  eggs: "dairy",
  milk: "dairy",
  butter: "dairy",
  cheese: "dairy",
  cheddar: "dairy",
  mozzarella: "dairy",
  yogurt: "dairy",
  cream: "dairy",
  "milk powder": "dairy",
  nido: "dairy",
  "condensed milk": "dairy",
  chicken: "meat",
  beef: "meat",
  turkey: "meat",
  pork: "meat",
  lamb: "meat",
  bacon: "meat",
  sausage: "meat",
  "ground beef": "meat",
  steak: "meat",
  fish: "seafood",
  salmon: "seafood",
  tuna: "seafood",
  shrimp: "seafood",
  tilapia: "seafood",
  crab: "seafood",
  sardines: "seafood",
  spinach: "produce",
  tomato: "produce",
  onion: "produce",
  garlic: "produce",
  avocado: "produce",
  plantain: "produce",
  lettuce: "produce",
  carrot: "produce",
  pepper: "produce",
  mushroom: "produce",
  broccoli: "produce",
  banana: "produce",
  apple: "produce",
  orange: "produce",
  mango: "produce",
  strawberry: "produce",
  lemon: "produce",
  lime: "produce",
  cucumber: "produce",
  celery: "produce",
  kale: "produce",
  cabbage: "produce",
  pineapple: "produce",
  grapes: "produce",
  rice: "pantry",
  pasta: "pantry",
  bread: "bakery",
  flour: "pantry",
  oats: "pantry",
  tortilla: "pantry",
  noodles: "pantry",
  quinoa: "pantry",
  cornmeal: "pantry",
  salt: "pantry",
  sugar: "pantry",
  honey: "pantry",
  oil: "pantry",
  sauce: "pantry",
  ketchup: "pantry",
  vinegar: "pantry",
  mustard: "pantry",
  "soy sauce": "pantry",
  cumin: "pantry",
  paprika: "pantry",
  cinnamon: "pantry",
  turmeric: "pantry",
  oregano: "pantry",
  "curry powder": "pantry",
  bouillon: "pantry",
  seasoning: "pantry",
  beans: "pantry",
  chickpeas: "pantry",
  lentils: "pantry",
  "coconut milk": "pantry",
  chips: "snacks",
  cookies: "snacks",
  crackers: "snacks",
  nuts: "snacks",
  chocolate: "snacks",
  popcorn: "snacks",
  granola: "snacks",
  peanuts: "snacks",
  water: "beverages",
  juice: "beverages",
  coffee: "beverages",
  tea: "beverages",
  soda: "beverages",
  wine: "beverages",
  beer: "beverages",
  "ice cream": "frozen",
  "frozen peas": "frozen",
  "frozen corn": "frozen",
};
const LOCATION_AUTOFILL = {
  egg: "fridge",
  eggs: "fridge",
  milk: "fridge",
  butter: "fridge",
  cheese: "fridge",
  yogurt: "fridge",
  cream: "fridge",
  chicken: "fridge",
  beef: "fridge",
  fish: "fridge",
  salmon: "fridge",
  shrimp: "fridge",
  turkey: "fridge",
  pork: "fridge",
  spinach: "fridge",
  lettuce: "fridge",
  carrot: "fridge",
  mushroom: "fridge",
  avocado: "fridge",
  strawberry: "fridge",
  bacon: "fridge",
  steak: "fridge",
  tofu: "fridge",
  hummus: "fridge",
  mayonnaise: "fridge",
  ketchup: "fridge",
  mustard: "fridge",
  broccoli: "fridge",
  celery: "fridge",
  grapes: "fridge",
  rice: "pantry",
  pasta: "pantry",
  flour: "pantry",
  sugar: "pantry",
  salt: "pantry",
  oil: "pantry",
  honey: "pantry",
  oats: "pantry",
  bread: "pantry",
  tortilla: "pantry",
  coffee: "pantry",
  tea: "pantry",
  water: "pantry",
  nuts: "pantry",
  peanuts: "pantry",
  chips: "pantry",
  cookies: "pantry",
  chocolate: "pantry",
  beans: "pantry",
  lentils: "pantry",
  sauce: "pantry",
  vinegar: "pantry",
  cumin: "pantry",
  nido: "pantry",
  "milk powder": "pantry",
  "powdered milk": "pantry",
  "ice cream": "freezer",
  "frozen peas": "freezer",
  "frozen corn": "freezer",
};
const EXPIRY_OPTS = [
  { label: "3 days", days: 3 },
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
];
function autoFillCat(name) {
  const n = (name || "").toLowerCase().trim();
  if (n.length < 2) return "";
  for (const [k, v] of Object.entries(CATEGORY_AUTOFILL)) {
    if (n === k || n.includes(k) || (k.includes(n) && n.length >= 3)) return v;
  }
  return "";
}

const SHOPPING_EXPIRY_BY_CATEGORY = {
  produce: 5,
  dairy: 10,
  meat: 2,
  seafood: 1,
  frozen: 90,
  bakery: 5,
  pantry: 180,
  beverages: 120,
  snacks: 90,
};

const SHOPPING_EXPIRY_BY_NAME = [
  {
    days: 2,
    terms: [
      "chicken",
      "beef",
      "pork",
      "turkey",
      "steak",
      "ground meat",
      "sausage",
      "bacon",
    ],
  },
  { days: 1, terms: ["fish", "salmon", "tuna", "shrimp", "prawn", "seafood"] },
  {
    days: 4,
    terms: [
      "spinach",
      "lettuce",
      "salad greens",
      "spring mix",
      "berries",
      "strawberry",
      "raspberry",
    ],
  },
  { days: 5, terms: ["bread", "bagel", "bun", "roll", "tortilla", "pita"] },
  { days: 7, terms: ["milk", "cream", "hummus", "tofu", "deli"] },
  { days: 10, terms: ["yogurt", "yoghurt", "cheese", "eggs"] },
  {
    days: 14,
    terms: [
      "apple",
      "carrot",
      "celery",
      "broccoli",
      "potato",
      "onion",
      "lemon",
      "lime",
    ],
  },
  { days: 90, terms: ["frozen", "ice cream"] },
  {
    days: 180,
    terms: [
      "rice",
      "pasta",
      "flour",
      "sugar",
      "beans",
      "lentils",
      "oats",
      "cereal",
      "sauce",
      "oil",
      "vinegar",
    ],
  },
];

// Bought items should not enter Pantry already looking urgent. These dates are
// only checkout estimates (not package dates), so keep the nearest suggestion
// at least a week away. Users can still replace it with the actual label date.
const MIN_SHOPPING_EXPIRY_DAYS = 7;

function suggestedShoppingExpiryDate(name, category, location) {
  const text = String(name || "").toLowerCase();
  const match = SHOPPING_EXPIRY_BY_NAME.find((rule) =>
    rule.terms.some((term) => text.includes(term)),
  );
  const normalizedCategory = String(category || "").toLowerCase();
  const normalizedLocation = String(location || "").toLowerCase();
  const estimatedDays =
    match?.days ||
    (normalizedLocation === "freezer" ? 90 : null) ||
    SHOPPING_EXPIRY_BY_CATEGORY[normalizedCategory] ||
    null;
  if (!estimatedDays) return "";
  const days = Math.max(estimatedDays, MIN_SHOPPING_EXPIRY_DAYS);
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + days);
  return localISODate(expiry);
}

function stableRestockExpiryDate(dateValue) {
  if (!dateValue) return dateValue;
  const picked = parseDateOnly(dateValue);
  if (!picked) return dateValue;
  const stableMinimum = new Date();
  stableMinimum.setDate(stableMinimum.getDate() + 7);
  stableMinimum.setHours(0, 0, 0, 0);
  return picked < stableMinimum ? localISODate(stableMinimum) : dateValue;
}

const SHOPPING_RECIPE_DAY_OFFSETS = {
  mon: 0,
  monday: 0,
  tue: 1,
  tues: 1,
  tuesday: 1,
  wed: 2,
  weds: 2,
  wednesday: 2,
  thu: 3,
  thur: 3,
  thurs: 3,
  thursday: 3,
  fri: 4,
  friday: 4,
  sat: 5,
  saturday: 5,
  sun: 6,
  sunday: 6,
};

function shoppingItemFirstUseDate(forRecipes = [], weekStartIso = "") {
  const weekStart = parseDateOnly(weekStartIso);
  if (!weekStart || !Array.isArray(forRecipes)) return "";
  const offsets = forRecipes
    .map((label) =>
      String(label || "")
        .trim()
        .split(/\s+/)[0]
        ?.toLowerCase(),
    )
    .map((day) => SHOPPING_RECIPE_DAY_OFFSETS[day])
    .filter((offset) => Number.isInteger(offset));
  if (!offsets.length) return "";
  const firstUse = new Date(weekStart);
  firstUse.setDate(firstUse.getDate() + Math.min(...offsets));
  return localISODate(firstUse);
}

function shoppingExpiryPlanWarning(item) {
  if (!item?.expiry_date || !item?.planned_use_date) return "";
  const expiry = parseDateOnly(item.expiry_date);
  const planned = parseDateOnly(item.planned_use_date);
  if (!expiry || !planned || expiry >= planned) return "";
  const plannedLabel = planned.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const expiryLabel = expiry.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `Expires ${expiryLabel}, before planned use ${plannedLabel}. Cook or freeze early, or adjust the plan.`;
}

const FOOD_SHEET_MOVE_THRESHOLD = 3;
const FOOD_SHEET_CLOSE_THRESHOLD = 18;

function FoodMobileSheet({
  title,
  label,
  onClose,
  children,
  disableDrag = false,
  disableBackdropClose = true,
  dragHandleOnly = true,
}) {
  const sheetRef = useRef(null);
  const dragHandleRef = useRef(null);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const dragMovedRef = useRef(false);
  const handleTouchActiveRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setSheetOffset = useCallback((value) => {
    dragOffsetRef.current = value;
    setDragOffset(value);
  }, []);

  const closeWithSlide = useCallback(() => {
    setDragging(false);
    setSheetOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }, [onClose, setSheetOffset]);
  useBackButton(closeWithSlide);

  const beginDrag = useCallback(
    (clientY) => {
      if (disableDrag) return;
      dragStartY.current = clientY;
      dragOffsetRef.current = 0;
      dragMovedRef.current = false;
      setDragging(true);
    },
    [disableDrag],
  );

  const moveDrag = useCallback(
    (clientY, event) => {
      if (disableDrag) return;
      if (dragStartY.current == null) return;
      const nextOffset = Math.max(0, clientY - dragStartY.current);
      if (nextOffset > FOOD_SHEET_MOVE_THRESHOLD) dragMovedRef.current = true;
      setSheetOffset(nextOffset);
      if (nextOffset > FOOD_SHEET_MOVE_THRESHOLD) event?.preventDefault?.();
    },
    [disableDrag, setSheetOffset],
  );

  const endDrag = useCallback(() => {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > FOOD_SHEET_CLOSE_THRESHOLD) {
      closeWithSlide();
      return;
    }
    setDragging(false);
    setSheetOffset(0);
  }, [closeWithSlide, setSheetOffset]);

  useEffect(() => {
    const dragTarget = dragHandleOnly
      ? dragHandleRef.current
      : sheetRef.current;
    if (!dragTarget || disableDrag) return undefined;

    function handleTouchStart(event) {
      const touch = event.touches?.[0];
      if (!touch) return;
      handleTouchActiveRef.current = true;
      beginDrag(touch.clientY);
    }
    function handleTouchMove(event) {
      const touch = event.touches?.[0];
      if (touch) moveDrag(touch.clientY, event);
    }
    function handleTouchEnd() {
      if (!handleTouchActiveRef.current) return;
      handleTouchActiveRef.current = false;
      const shouldTapClose = dragHandleOnly && !dragMovedRef.current;
      endDrag();
      if (shouldTapClose) closeWithSlide();
    }
    function handleTouchCancel() {
      handleTouchActiveRef.current = false;
      endDrag();
    }

    dragTarget.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchCancel);

    return () => {
      dragTarget.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [
    beginDrag,
    closeWithSlide,
    disableDrag,
    dragHandleOnly,
    endDrag,
    moveDrag,
  ]);

  return (
    <div
      className="fd-mobile-sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={label || title}
    >
      <button
        type="button"
        className="fd-mobile-sheet-scrim"
        onClick={disableBackdropClose ? undefined : closeWithSlide}
        aria-label={disableBackdropClose ? undefined : `Close ${title}`}
        tabIndex={disableBackdropClose ? -1 : 0}
      />
      <aside
        ref={sheetRef}
        className={`fd-mobile-sheet fd-mobile-sheet--food${disableDrag ? " fd-mobile-sheet--locked" : ""}${dragging ? " is-dragging" : ""}`}
        style={{
          transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
        }}
        onPointerDown={(event) => {
          if (disableDrag || dragHandleOnly) return;
          if (event.pointerType === "mouse" && event.button !== 0) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          beginDrag(event.clientY);
        }}
        onPointerMove={(event) =>
          !disableDrag && !dragHandleOnly && moveDrag(event.clientY, event)
        }
        onPointerUp={!disableDrag && !dragHandleOnly ? endDrag : undefined}
        onPointerCancel={!disableDrag && !dragHandleOnly ? endDrag : undefined}
      >
        <SheetChrome
          title={title}
          onClose={closeWithSlide}
          handleRef={dragHandleRef}
          handleProps={{
            onPointerDown: (event) => {
              if (disableDrag) return;
              if (event.pointerType === "mouse" && event.button !== 0) return;
              event.currentTarget.setPointerCapture?.(event.pointerId);
              beginDrag(event.clientY);
            },
            onPointerMove: (event) =>
              !disableDrag && moveDrag(event.clientY, event),
            onPointerUp: !disableDrag
              ? (event) => {
                  event.currentTarget.releasePointerCapture?.(event.pointerId);
                  const shouldTapClose = !dragMovedRef.current;
                  endDrag();
                  if (shouldTapClose) closeWithSlide();
                }
              : undefined,
            onPointerCancel: !disableDrag
              ? (event) => {
                  event.currentTarget.releasePointerCapture?.(event.pointerId);
                  endDrag();
                }
              : undefined,
          }}
        >
          {children}
        </SheetChrome>
      </aside>
    </div>
  );
}

function PantryEditForm({
  editDraft,
  setEditDraft,
  saving,
  error,
  onSave,
  onCancel,
  onRemove,
}) {
  return (
    <div className="fd-item-edit">
      <input
        className="hm-input fd-edit-name"
        placeholder="Name"
        value={editDraft.name}
        onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
      />
      {error && (
        <div className="fd-add-inline-error" role="alert">
          <strong>⚠ {error.message}</strong>
          {error.hint && <span>{error.hint}</span>}
        </div>
      )}
      <div className="fd-edit-row">
        <input
          className="hm-input"
          placeholder="Qty"
          value={editDraft.quantity}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, quantity: e.target.value }))
          }
        />
        <input
          className="hm-input"
          placeholder="Unit"
          value={editDraft.unit}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, unit: e.target.value }))
          }
        />
        <select
          className="hm-select"
          value={editDraft.location}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, location: e.target.value }))
          }
        >
          {LOCATION_OPTS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="fd-edit-row">
        <label className="fd-edit-label">Expiry</label>
        <input
          className="hm-input"
          type="date"
          value={editDraft.expiry_date}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, expiry_date: e.target.value }))
          }
          style={{ flex: 1 }}
        />
      </div>
      <div className="fd-edit-actions">
        <button
          className="hm-btn hm-btn--primary fd-edit-save"
          type="button"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <div className="fd-edit-secondary">
          <button
            className="hm-btn hm-btn--ghost fd-edit-cancel"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="hm-btn hm-btn--danger fd-edit-remove"
            type="button"
            onClick={onRemove}
            disabled={saving}
          >
            🗑 Remove
          </button>
        </div>
      </div>
    </div>
  );
}
function autoFillLoc(name) {
  const n = (name || "").toLowerCase().trim();
  if (n.length < 2) return "";
  for (const [k, v] of Object.entries(LOCATION_AUTOFILL)) {
    if (n === k || n.includes(k) || (k.includes(n) && n.length >= 3)) return v;
  }
  return "";
}

// ── Pantry Tab ─────────────────────────────────────────────────────────────
function PantryTab({
  pantry,
  loading,
  onAdd,
  onRemove,
  onScan,
  onUpdate,
  onShare,
  canShare,
  sharedResourceKeys,
  onToast,
  focusFilter,
  tripCallout,
  onTripCalloutOpen,
  onTripCalloutDismiss,
}) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("");
  const [cat, setCat] = useState("dairy");
  const [loc, setLoc] = useState("fridge");
  const [exp, setExp] = useState("");
  const [brand, setBrand] = useState("");
  const [barcode, setBarcode] = useState("");
  const [barcodeMsg, setBarcodeMsg] = useState("");
  const [barcodeState, setBarcodeState] = useState("");
  const [barcodeBusy, setBarcodeBusy] = useState(false);
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const [catAuto, setCatAuto] = useState(false);
  const [locAuto, setLocAuto] = useState(false);
  const [expiryQuick, setExpiryQuick] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [addNotice, setAddNotice] = useState("");
  const [addError, setAddError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showAllItems, setShowAllItems] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [selectedPantryIds, setSelectedPantryIds] = useState(() => new Set());
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const presentation = useFormPresentation();
  const barcodeScanInputRef = useRef(null);
  const pantryLongPressRef = useRef(null);
  const pantrySuppressClickRef = useRef(false);
  const didSetDefaultFilterRef = useRef(false);
  const lastFocusFilterRef = useRef(null);
  const showPantryWorking = useMinLoad(
    adding ||
      saving ||
      removing ||
      bulkRemoving ||
      barcodeBusy ||
      barcodeScanning,
    700,
  );
  const editItem = editId ? pantry.find((item) => item.id === editId) : null;
  const selectionMode = selectedPantryIds.size > 0;

  useEffect(() => {
    if (loading || didSetDefaultFilterRef.current) return;
    const hasExpiringItems = pantry.some((item) => item.level !== "stable");
    setFilter(hasExpiringItems ? "expiring" : "all");
    didSetDefaultFilterRef.current = true;
  }, [loading, pantry]);

  useEffect(() => {
    if (!focusFilter || lastFocusFilterRef.current === focusFilter.key) return;
    lastFocusFilterRef.current = focusFilter.key;
    selectFilter(focusFilter.filter || "all");
  }, [focusFilter]);

  function startEdit(item) {
    setEditId(item.id);
    setEditError(null);
    setEditDraft({
      name: item.name || "",
      brand: item.brand || "",
      quantity: item.quantity || "",
      unit: item.unit || "",
      category: item.category || "other",
      location: item.location || "fridge",
      expiry_date: item.expiry_date || "",
    });
  }

  async function saveEdit(id) {
    setSaving(true);
    setEditError(null);
    try {
      await pantryApi.update(id, {
        ...editDraft,
        expiry_date: editDraft.expiry_date || null,
      });
      setEditId(null);
      onUpdate();
    } catch (e) {
      if (e.status === 409) {
        setEditError({
          message:
            e.message ||
            `${editDraft.name || "This item"} is already in your pantry with that expiry date.`,
          hint: e.hint || e.data?.hint || "",
        });
        return;
      }
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function removePantryItem(id) {
    setRemoving(true);
    try {
      await onRemove(id);
    } finally {
      setRemoving(false);
    }
  }

  function togglePantrySelection(id) {
    setSelectedPantryIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function startPantryLongPress(item) {
    if (editId) return;
    window.clearTimeout(pantryLongPressRef.current);
    pantrySuppressClickRef.current = false;
    pantryLongPressRef.current = window.setTimeout(() => {
      pantrySuppressClickRef.current = true;
      setEditId(null);
      setSelectedPantryIds((current) => {
        const next = new Set(current);
        next.add(item.id);
        return next;
      });
    }, 420);
  }

  function stopPantryLongPress() {
    window.clearTimeout(pantryLongPressRef.current);
  }

  async function removeSelectedPantryItems() {
    const ids = Array.from(selectedPantryIds);
    if (!ids.length) return;
    setBulkRemoving(true);
    try {
      await Promise.all(ids.map((id) => onRemove(id)));
      setSelectedPantryIds(new Set());
      setShowBulkRemoveConfirm(false);
      onToast?.(
        `${ids.length} pantry item${ids.length === 1 ? "" : "s"} removed`,
      );
    } finally {
      setBulkRemoving(false);
    }
  }

  const sorted = [...pantry].sort((a, b) => {
    const lvl = { urgent: 0, soon: 1, stable: 2 };
    return (lvl[a.level] ?? 2) - (lvl[b.level] ?? 2);
  });
  const filtered =
    filter === "all"
      ? sorted
      : sorted.filter((i) =>
          filter === "expiring" ? i.level !== "stable" : i.location === filter,
        );
  const allViewLimit = 50;
  const isAllLimited =
    filter === "all" && !showAllItems && filtered.length > allViewLimit;
  const visibleItems = isAllLimited
    ? filtered.slice(0, allViewLimit)
    : filtered;
  const filterLabels = {
    all: "pantry",
    expiring: "expiring",
    fridge: "fridge",
    freezer: "freezer",
    pantry: "pantry shelf",
  };
  const emptyCopy = {
    all: {
      title: "Your pantry is empty",
      body: "Add a few basics so HouseMind can plan meals and build smarter shopping lists.",
      action: "Use + Add item or Scan receipt above.",
    },
    expiring: {
      title: "Nothing expiring soon",
      body: "You're clear for now. Items will appear here when they need attention.",
      action:
        "Add expiry dates to fresh items so HouseMind can surface them here.",
    },
    fridge: {
      title: "No fridge items yet",
      body: "Fresh foods, dairy, leftovers, and drinks will show here.",
      action: "Add one from your fridge or scan a receipt.",
    },
    freezer: {
      title: "No freezer items yet",
      body: "Frozen meals, frozen vegetables, meat, and ice cream will show here.",
      action: "Add freezer staples when you stock up.",
    },
    pantry: {
      title: "No pantry shelf items yet",
      body: "Dry goods, cans, spices, snacks, and grains will show here.",
      action: "Add shelf-stable basics to make meal planning stronger.",
    },
  };
  const activeEmpty = emptyCopy[filter] || emptyCopy.all;
  const showLowResultNote =
    filter !== "all" && filtered.length > 0 && filtered.length <= 2;

  function resetDraft({ keepDefaults = true } = {}) {
    setName("");
    setQty("");
    setUnit("");
    setExp("");
    setBrand("");
    setBarcode("");
    setBarcodeMsg("");
    setBarcodeState("");
    setCatAuto(false);
    setLocAuto(false);
    setExpiryQuick(null);
    if (!keepDefaults) {
      setCat("dairy");
      setLoc("fridge");
    }
  }

  function handleNameChange(val) {
    if (addError) setAddError(null);
    setName(val);
    const sugCat = autoFillCat(val);
    if (sugCat) {
      setCat(sugCat);
      setCatAuto(true);
    } else if (catAuto) {
      setCat("other");
      setCatAuto(false);
    }
    const sugLoc = autoFillLoc(val);
    if (sugLoc) {
      setLoc(sugLoc);
      setLocAuto(true);
    } else if (locAuto) {
      setLoc("fridge");
      setLocAuto(false);
    }
  }
  function handleExpiryQuick(days, label) {
    if (addError) setAddError(null);
    const d = new Date();
    d.setDate(d.getDate() + days);
    setExp(d.toISOString().slice(0, 10));
    setExpiryQuick(label);
  }

  function currentDraft() {
    return {
      name: name.trim(),
      category: cat,
      quantity: qty,
      unit,
      location: loc,
      expiry_date: exp || null,
      brand,
      barcode,
    };
  }

  function showAddNotice(message) {
    setAddNotice(message);
    setTimeout(() => setAddNotice(""), 2200);
  }

  async function saveSingleItem() {
    const draft = currentDraft();
    if (!draft.name) return;
    if (barcodeDuplicate) {
      setAddError({
        message: "This barcode is already in your pantry.",
        hint: "Edit the existing item instead of adding a duplicate.",
      });
      return;
    }
    setAdding(true);
    try {
      const added = await onAdd(draft);
      if (!added) return;
      setAddError(null);
      setName("");
      setQty("");
      setBrand("");
      setBarcode("");
      setBarcodeMsg("");
      setBarcodeState("");
      setCatAuto(false);
      setLocAuto(false);
      setExpiryQuick(null);
      showAddNotice("✓ Added — type another or tap Cancel when done");
    } catch (e) {
      if (e.status === 409) {
        setAddError({
          message: e.message || `${draft.name} is already in your pantry.`,
          hint: e.hint || e.data?.hint || "",
        });
        return;
      }
      throw e;
    } finally {
      setAdding(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    await saveSingleItem();
  }

  function splitBarcodeQuantity(value) {
    const text = String(value || "").trim();
    if (!text) return ["", ""];
    const match = text.match(/^([\d.]+)\s*([a-zA-Z]+)$/);
    if (match) return [match[1], match[2]];
    return [text, ""];
  }

  async function lookupBarcode(rawCode = barcode) {
    const code = String(rawCode || "").trim();
    if (!code) return;
    setBarcode(code);
    setBarcodeBusy(true);
    setBarcodeMsg("");
    setBarcodeState("");
    setAddError(null);
    try {
      const res = await pantryApi.barcode(code);
      if (!res.found) {
        setBarcodeMsg(
          res.message || "No match found. Add item details manually.",
        );
        setBarcodeState("missing");
        return;
      }
      const item = res.item || {};
      if (res.already_in_pantry) {
        setBarcodeMsg(
          `${item.name || "This item"} is already in your pantry. Edit the existing item instead of adding a duplicate.`,
        );
        setBarcodeState("duplicate");
        return;
      }
      const [nextQty, nextUnit] = splitBarcodeQuantity(item.quantity);
      handleNameChange(item.name || "");
      setBrand(item.brand || "");
      setQty(nextQty);
      setUnit(nextUnit || item.unit || "");
      setCat(item.category || "other");
      setLoc(item.location || "pantry");
      setCatAuto(Boolean(item.category));
      setLocAuto(Boolean(item.location));
      setBarcode(item.barcode || code);
      setBarcodeMsg("Product found. Review and edit details before saving.");
      setBarcodeState("found");
    } catch (e) {
      setBarcodeMsg(
        e.message || "Barcode lookup failed. Add item details manually.",
      );
      setBarcodeState("error");
    } finally {
      setBarcodeBusy(false);
    }
  }

  const barcodeDuplicate = barcodeState === "duplicate";

  async function handleBarcodeScanFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBarcodeScanning(true);
    setBarcodeMsg("");
    setBarcodeState("");
    setAddError(null);
    try {
      if (!("BarcodeDetector" in window)) {
        setBarcodeMsg(
          "Barcode camera scanning is not supported in this browser. Type the package barcode instead.",
        );
        setBarcodeState("missing");
        return;
      }
      const image = await createImageBitmap(file);
      const formats = [
        "ean_13",
        "ean_8",
        "upc_a",
        "upc_e",
        "code_128",
        "code_39",
      ];
      const detector = new window.BarcodeDetector({ formats });
      const codes = await detector.detect(image);
      const productCode = codes
        .map((code) => String(code.rawValue || "").replace(/\D/g, ""))
        .find((value) => value.length >= 6 && value.length <= 32);
      if (!productCode) {
        setBarcodeMsg(
          "No product barcode found. Try a closer photo of the package barcode, or type it manually.",
        );
        setBarcodeState("missing");
        return;
      }
      await lookupBarcode(productCode);
    } catch (error) {
      setBarcodeMsg(
        `Could not scan barcode: ${error.message || "try typing it manually."}`,
      );
      setBarcodeState("error");
    } finally {
      setBarcodeScanning(false);
      event.target.value = "";
    }
  }

  function selectFilter(nextFilter) {
    setFilter(nextFilter);
    setShowAllItems(false);
    setSelectedPantryIds(new Set());
  }

  return (
    <div className="fd-tab-content fd-pantry-tab">
      {showPantryWorking && (
        <SavingNotice
          title={
            barcodeScanning
              ? "Scanning barcode"
              : barcodeBusy
                ? "Looking up barcode"
                : adding
                  ? "Adding item"
                  : bulkRemoving || removing
                    ? "Removing item"
                    : "Saving item"
          }
          message={
            barcodeBusy
              ? "Checking product details..."
              : barcodeScanning
                ? "Reading package barcode..."
                : adding
                  ? "Adding item..."
                  : bulkRemoving
                    ? "Removing selected pantry items..."
                    : removing
                      ? "Removing item..."
                      : "Saving pantry item..."
          }
        />
      )}
      <div className="fd-toolbar">
        <div className="fd-filter-pills">
          {["all", "expiring", "fridge", "freezer", "pantry"].map((f) => (
            <button
              key={f}
              className={`fd-pill${filter === f ? " fd-pill--active" : ""}`}
              onClick={() => selectFilter(f)}
            >
              {f === "all"
                ? `All (${pantry.length})`
                : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="fd-toolbar-actions">
          {canShare && (
            <button
              className="hm-btn hm-btn--ghost"
              onClick={() => onShare?.("pantry", 0, "Pantry")}
              disabled={sharedResourceKeys?.has("pantry:0")}
            >
              {sharedResourceKeys?.has("pantry:0")
                ? "👥 Shared"
                : "👥 Share pantry"}
            </button>
          )}
          <button className="hm-btn hm-btn--ghost" onClick={onScan}>
            🧾 Scan receipt
          </button>
          <button
            className="hm-btn hm-btn--primary"
            onClick={() => setShowForm((s) => !s)}
          >
            {showForm ? "Cancel" : "+ Add item"}
          </button>
        </div>
      </div>

      {tripCallout && (
        <div
          className="fd-trip-pantry-callout"
          role="button"
          tabIndex={0}
          onClick={onTripCalloutOpen}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ")
              onTripCalloutOpen?.();
          }}
        >
          <span className="fd-trip-pantry-callout__icon" aria-hidden="true">
            🧳
          </span>
          <div>
            <strong>
              {tripCallout.title} trip in {tripCallout.daysUntil} day
              {tripCallout.daysUntil === 1 ? "" : "s"}
            </strong>
            <span>
              Use these {tripCallout.itemCount} item
              {tripCallout.itemCount === 1 ? "" : "s"} before you leave →
            </span>
          </div>
          <button
            type="button"
            className="fd-inline-close"
            onClick={(event) => {
              event.stopPropagation();
              onTripCalloutDismiss?.();
            }}
            aria-label="Dismiss trip pantry reminder"
          >
            ×
          </button>
        </div>
      )}

      {selectionMode && (
        <div
          className="fd-pantry-select-bar"
          role="region"
          aria-label="Selected pantry items"
        >
          <span>{selectedPantryIds.size} selected</span>
          <button
            type="button"
            onClick={() => setSelectedPantryIds(new Set())}
            disabled={bulkRemoving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="fd-pantry-select-delete"
            onClick={() => setShowBulkRemoveConfirm(true)}
            disabled={bulkRemoving}
          >
            Delete
          </button>
        </div>
      )}

      {showForm &&
        (() => {
          const form = (
            <form onSubmit={handleAdd} className="fd-add-form">
              <div className="fd-add-form-head">
                <button
                  type="button"
                  className="fd-inline-close fd-inline-close--panel"
                  onClick={() => setShowForm(false)}
                  aria-label="Close add item panel"
                >
                  ×
                </button>
              </div>
              <div className="fd-single-form">
                <div className="fd-sf-field">
                  <label className="fd-sf-label">Item name *</label>
                  <ItemComboBox
                    value={name}
                    onChange={(val) => handleNameChange(val)}
                    onSelect={(item) => {
                      handleNameChange(item.name);
                      if (item.category) {
                        setCat(item.category);
                        setCatAuto(true);
                      }
                      if (item.location) {
                        setLoc(item.location);
                        setLocAuto(true);
                      }
                      if (item.unit) setUnit(item.unit);
                    }}
                    placeholder="e.g. Whole Milk"
                  />
                </div>
                <div className="fd-sf-field">
                  <label className="fd-sf-label">
                    Barcode <span className="fd-sf-opt">(optional)</span>
                  </label>
                  <div className="fd-sf-row fd-sf-row--lookup">
                    <input
                      className="hm-input"
                      inputMode="numeric"
                      placeholder="Type package barcode"
                      value={barcode}
                      onChange={(e) => {
                        setBarcode(e.target.value);
                        setBarcodeMsg("");
                        setBarcodeState("");
                      }}
                    />
                    <button
                      className="hm-btn hm-btn--ghost"
                      type="button"
                      onClick={() => barcodeScanInputRef.current?.click()}
                      disabled={barcodeBusy || barcodeScanning}
                    >
                      {barcodeScanning ? "Scanning..." : "Scan"}
                    </button>
                    <input
                      ref={barcodeScanInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={handleBarcodeScanFile}
                    />
                    <button
                      className="hm-btn hm-btn--ghost"
                      type="button"
                      onClick={() => lookupBarcode()}
                      disabled={
                        barcodeBusy || barcodeScanning || !barcode.trim()
                      }
                    >
                      {barcodeBusy ? "Looking..." : "Lookup"}
                    </button>
                  </div>
                  {barcodeMsg && (
                    <div
                      className={`fd-add-notice fd-add-notice--inline fd-add-notice--${barcodeState || "info"}`}
                    >
                      {barcodeMsg}
                    </div>
                  )}
                </div>
                <div className="fd-sf-field">
                  <label className="fd-sf-label">Category</label>
                  <select
                    className="hm-select"
                    value={cat}
                    onChange={(e) => {
                      const newCat = e.target.value;
                      setCat(newCat);
                      setCatAuto(false);
                      const catToLoc = {
                        dairy: "fridge",
                        produce: "fridge",
                        meat: "fridge",
                        beverages: "fridge",
                        leftovers: "fridge",
                        deli: "fridge",
                        frozen_meals: "freezer",
                        frozen_meat: "freezer",
                        frozen_veg: "freezer",
                        ice_cream: "freezer",
                        grains: "pantry",
                        canned: "pantry",
                        snacks: "pantry",
                        spices: "pantry",
                        condiments: "pantry",
                        dry_goods: "pantry",
                      };
                      const inferredLoc = catToLoc[newCat];
                      if (inferredLoc) {
                        setLoc(inferredLoc);
                        setLocAuto(true);
                      }
                    }}
                  >
                    {CATEGORY_OPTS.map((c) => (
                      <option key={c} value={c}>
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fd-sf-field">
                  <label className="fd-sf-label">Location</label>
                  <div className="fd-sf-pills">
                    {[
                      { val: "fridge", icon: "📦" },
                      { val: "freezer", icon: "❄️" },
                      { val: "pantry", icon: "🏠" },
                    ].map(({ val, icon }) => (
                      <button
                        key={val}
                        type="button"
                        className={
                          "fd-sf-pill" +
                          (loc === val ? " fd-sf-pill--on" : "") +
                          (loc === val && locAuto ? " fd-sf-pill--auto" : "") +
                          (loc === val && !locAuto && loc
                            ? " fd-sf-pill--custom"
                            : "")
                        }
                        onClick={() => {
                          setLoc(val);
                          setLocAuto(false);
                        }}
                      >
                        {icon} {val.charAt(0).toUpperCase() + val.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="fd-sf-field">
                  <label className="fd-sf-label">Expires</label>
                  <div className="fd-sf-exp-row">
                    {EXPIRY_OPTS.map(({ label, days }) => (
                      <button
                        key={label}
                        type="button"
                        className={
                          "fd-sf-exp" +
                          (expiryQuick === label ? " fd-sf-exp--on" : "") +
                          (days <= 3 ? " fd-sf-exp--warn" : "")
                        }
                        onClick={() => handleExpiryQuick(days, label)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <label className="fd-sf-date-wrap">
                    <span className="fd-sf-date-icon" aria-hidden="true">
                      📅
                    </span>
                    <span className="fd-sf-date-copy">Exact date</span>
                    <input
                      className="hm-input fd-sf-date"
                      type="date"
                      value={exp}
                      onChange={(e) => {
                        setExp(e.target.value);
                        setExpiryQuick(null);
                        setAddError(null);
                      }}
                    />
                  </label>
                </div>
                {addError && (
                  <div className="fd-add-inline-error" role="alert">
                    <strong>⚠ {addError.message}</strong>
                    {addError.hint && <span>{addError.hint}</span>}
                  </div>
                )}
                <div className="fd-sf-field">
                  <label className="fd-sf-label">
                    Brand <span className="fd-sf-opt">(optional)</span>
                  </label>
                  <input
                    className="hm-input"
                    placeholder="e.g. Nido, Dole, Heinz…"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                  />
                </div>
                <div className="fd-sf-field">
                  <label className="fd-sf-label">
                    Quantity <span className="fd-sf-opt">(optional)</span>
                  </label>
                  <div className="fd-sf-row">
                    <div>
                      <span className="fd-sf-sub">Amount</span>
                      <input
                        className="hm-input"
                        type="number"
                        inputMode="decimal"
                        placeholder="1"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="fd-sf-sub">Unit</span>
                      <select
                        className="hm-select"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                      >
                        <option value="">units</option>
                        {[
                          "g",
                          "kg",
                          "ml",
                          "L",
                          "cup",
                          "piece",
                          "pack",
                          "bunch",
                          "lb",
                          "oz",
                        ].map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              {addNotice && <div className="fd-add-notice">{addNotice}</div>}
              <div className="fd-add-footer">
                <button
                  type="button"
                  className="fd-add-cancel"
                  onClick={() => {
                    setShowForm(false);
                    resetDraft();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="hm-btn hm-btn--primary fd-add-save"
                  type="submit"
                  disabled={adding || !name.trim() || barcodeDuplicate}
                >
                  {adding ? "Saving..." : "+ Add item"}
                </button>
              </div>
            </form>
          );
          return presentation === "modal" ? (
            <CenteredModal
              title="Add to pantry"
              onClose={() => setShowForm(false)}
            >
              {form}
            </CenteredModal>
          ) : (
            <FoodMobileSheet
              title="Add to pantry"
              label="Add to pantry"
              onClose={() => setShowForm(false)}
              disableBackdropClose
              dragHandleOnly
            >
              {form}
            </FoodMobileSheet>
          );
        })()}

      {loading ? (
        <div className="fd-empty">Loading pantry…</div>
      ) : filtered.length === 0 ? (
        <div className="fd-pantry-empty">
          <span className="fd-pantry-empty__icon" aria-hidden="true">
            {filter === "expiring" ? "✓" : "+"}
          </span>
          <strong>{activeEmpty.title}</strong>
          <p>{activeEmpty.body}</p>
          <em>{activeEmpty.action}</em>
        </div>
      ) : (
        <>
          <div className="fd-item-list">
            {visibleItems.map((item) => (
              <div
                key={item.id}
                className={`fd-item fd-item--${item.level}${editId === item.id && presentation === "modal" ? " fd-item--editing" : ""}${selectedPantryIds.has(item.id) ? " fd-item--selected" : ""}${selectionMode ? " fd-item--selectable" : ""}`}
                onPointerDown={
                  editId !== item.id
                    ? () => startPantryLongPress(item)
                    : undefined
                }
                onPointerUp={stopPantryLongPress}
                onPointerCancel={stopPantryLongPress}
                onPointerLeave={stopPantryLongPress}
                onClick={
                  editId !== item.id
                    ? () => {
                        if (pantrySuppressClickRef.current) {
                          pantrySuppressClickRef.current = false;
                          return;
                        }
                        if (selectionMode) {
                          togglePantrySelection(item.id);
                          return;
                        }
                        startEdit(item);
                      }
                    : undefined
                }
                role={editId !== item.id ? "button" : undefined}
              >
                {editId === item.id && presentation === "modal" ? (
                  <div className="fd-item-edit">
                    {/* Row 1: name (big) */}
                    <input
                      className="hm-input fd-edit-name"
                      placeholder="Name"
                      value={editDraft.name}
                      onChange={(e) => {
                        setEditError(null);
                        setEditDraft((d) => ({ ...d, name: e.target.value }));
                      }}
                    />
                    {editError && (
                      <div className="fd-add-inline-error" role="alert">
                        <strong>⚠ {editError.message}</strong>
                        {editError.hint && <span>{editError.hint}</span>}
                      </div>
                    )}
                    {/* Row 2: qty · unit · location */}
                    <div className="fd-edit-row">
                      <input
                        className="hm-input"
                        placeholder="Qty"
                        value={editDraft.quantity}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            quantity: e.target.value,
                          }))
                        }
                      />
                      <input
                        className="hm-input"
                        placeholder="Unit"
                        value={editDraft.unit}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, unit: e.target.value }))
                        }
                      />
                      <select
                        className="hm-select"
                        value={editDraft.location}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            location: e.target.value,
                          }))
                        }
                      >
                        {LOCATION_OPTS.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Row 3: expiry date */}
                    <div className="fd-edit-row">
                      <label className="fd-edit-label">Expiry</label>
                      <input
                        className="hm-input"
                        type="date"
                        value={editDraft.expiry_date}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            expiry_date: e.target.value,
                          }))
                        }
                        style={{ flex: 1 }}
                      />
                    </div>
                    {/* Row 4: actions */}
                    <div className="fd-edit-actions">
                      <button
                        className="hm-btn hm-btn--primary fd-edit-save"
                        onClick={(e) => {
                          e.stopPropagation();
                          saveEdit(item.id);
                        }}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                      <div className="fd-edit-secondary">
                        <button
                          className="hm-btn hm-btn--ghost fd-edit-cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditId(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="hm-btn hm-btn--danger fd-edit-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditId(null);
                            removePantryItem(item.id);
                          }}
                          disabled={removing}
                        >
                          🗑 Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {selectionMode && (
                      <button
                        type="button"
                        className="fd-pantry-select-check"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePantrySelection(item.id);
                        }}
                        aria-label={
                          selectedPantryIds.has(item.id)
                            ? `Unselect ${item.name}`
                            : `Select ${item.name}`
                        }
                      >
                        {selectedPantryIds.has(item.id) ? "✓" : ""}
                      </button>
                    )}
                    <span className="fd-prow-emoji" aria-hidden="true">
                      {pantryEmoji(item)}
                    </span>
                    <div className="fd-prow-body">
                      <span className="fd-prow-name">
                        {item.name}
                        {item.brand ? (
                          <span className="fd-prow-brand"> · {item.brand}</span>
                        ) : null}
                      </span>
                      <span className="fd-prow-sub">
                        {[
                          item.quantity
                            ? `${item.quantity}${item.unit ? " " + item.unit : ""}`
                            : null,
                          item.location,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    <div className="fd-prow-right">
                      {expiryPill(item)}
                      <span className="fd-prow-chevron" aria-hidden="true">
                        ›
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          {showLowResultNote && (
            <div className="fd-pantry-low-note">
              Showing all {filtered.length} {filterLabels[filter]} item
              {filtered.length === 1 ? "" : "s"}.
            </div>
          )}
          {isAllLimited && (
            <div className="fd-pantry-limit-note">
              <span>
                Showing {visibleItems.length} of {filtered.length} items · Use
                Fridge, Freezer, or Pantry filters to narrow.
              </span>
              <button type="button" onClick={() => setShowAllItems(true)}>
                Show all
              </button>
            </div>
          )}
        </>
      )}
      {editItem && presentation !== "modal" && (
        <FoodMobileSheet
          title={`Edit ${editItem.name || "pantry item"}`}
          label="Edit pantry item"
          onClose={() => setEditId(null)}
        >
          <PantryEditForm
            editDraft={editDraft}
            setEditDraft={(updater) => {
              setEditError(null);
              setEditDraft(updater);
            }}
            saving={saving || removing}
            error={editError}
            onSave={() => saveEdit(editItem.id)}
            onCancel={() => setEditId(null)}
            onRemove={() => {
              setEditId(null);
              removePantryItem(editItem.id);
            }}
          />
        </FoodMobileSheet>
      )}
      {showBulkRemoveConfirm && (
        <ConfirmModal
          tag="Remove pantry items"
          title={`Remove ${selectedPantryIds.size} selected item${selectedPantryIds.size === 1 ? "" : "s"}?`}
          message="This deletes them from your pantry. Meal ideas and shopping suggestions will stop using these items."
          confirmLabel={bulkRemoving ? "Removing..." : "Remove items"}
          cancelLabel="Keep items"
          loading={bulkRemoving}
          onCancel={() => !bulkRemoving && setShowBulkRemoveConfirm(false)}
          onConfirm={removeSelectedPantryItems}
        />
      )}
    </div>
  );
}

// ── Meal Plan Tab ──────────────────────────────────────────────────────────
const MEAL_TYPES = ["breakfast", "lunch", "dinner"];
const MEAL_TYPE_META = {
  breakfast: {
    label: "Breakfast",
    lower: "breakfast",
    plural: "breakfasts",
    short: "Brkfast",
    icon: "🌅",
  },
  lunch: {
    label: "Lunch",
    lower: "lunch",
    plural: "lunches",
    short: "Lunch",
    icon: "☀️",
  },
  dinner: {
    label: "Dinner",
    lower: "dinner",
    plural: "dinners",
    short: "Dinner",
    icon: "🌙",
  },
};
function mealTypeMeta(type) {
  return MEAL_TYPE_META[type] || MEAL_TYPE_META.dinner;
}
const DAY_FULL_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const MEAL_PLAN_TEST_MEALS = [
  {
    name: "Skillet Tomatoes with Eggs & Feta",
    tags: ["High Protein", "Vegetarian"],
    time: "25 min",
  },
  {
    name: "Lemon Herb Chicken Bowl",
    tags: ["High Protein"],
    time: "30 min",
  },
  {
    name: "Creamy Pesto Pasta",
    tags: ["Vegetarian"],
    time: "25 min",
  },
  {
    name: "Teriyaki Tofu",
    sub: "with Veggies",
    tags: ["High Protein", "Vegetarian"],
    time: "30 min",
  },
  {
    name: "Mediterranean Chickpea Bowl",
    tags: ["Vegetarian"],
    time: "20 min",
  },
  {
    name: "Sheet Pan Salmon",
    sub: "with Roasted Vegetables",
    tags: ["High Protein"],
    time: "35 min",
  },
  {
    name: "Cheddar Broccoli Soup",
    tags: ["Vegetarian"],
    time: "40 min",
  },
];
const MEAL_PLAN_ALLOWED_TAGS = new Set([
  "High Protein",
  "Vegetarian",
  "Vegan",
  "Gluten Free",
  "Low Cal",
  "Quick",
]);
const MEAL_PLAN_TITLE_FALLBACKS = {
  "creamy spinach and chicken pasta": {
    tags: ["High Protein"],
    time: "30 min",
  },
  "tomatoes rice bowl": {
    tags: ["High Protein"],
    time: "30 min",
  },
  "quick eggs tacos": {
    tags: ["Vegetarian"],
    time: "30 min",
  },
  "cheddar cheese pasta": {
    tags: ["High Protein", "Vegetarian"],
    time: "30 min",
  },
  "sheet pan plantains": {
    tags: ["Vegetarian"],
    time: "30 min",
  },
  "purified water soup": {
    tags: ["Vegetarian"],
    time: "40 min",
  },
  "leftover butter stir-fry": {
    tags: ["Vegetarian"],
    time: "40 min",
  },
};
function localISODate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function parseDateOnly(value) {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}
function daysUntilDate(value, from = new Date()) {
  const date = parseDateOnly(value);
  if (!date) return null;
  const start = parseDateOnly(localISODate(from));
  return Math.ceil((date - start) / 86400000);
}
function normalizeFoodMatchName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(
      /\b\d+([./]\d+)?\s*(kg|g|gram|grams|lb|lbs|oz|ml|l|liter|litre|cup|cups|tbsp|tsp|can|cans|jar|jars|bottle|bottles|piece|pieces|clove|cloves|bulb|bulbs|slice|slices|pack|packs|packet|packets)?\b/g,
      " ",
    )
    .replace(
      /\b(fresh|frozen|shredded|diced|chopped|minced|sliced|cooked|boiled|raw|large|small|medium|optional|to taste|of|and|or)\b/g,
      " ",
    )
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}
function foodNamesMatch(pantryName, ingredientName) {
  const item = normalizeFoodMatchName(pantryName);
  const ingredient = normalizeFoodMatchName(ingredientName);
  if (!item || !ingredient) return false;
  if (item === ingredient) return true;
  if (item.length >= 4 && ingredient.includes(item)) return true;
  if (ingredient.length >= 4 && item.includes(ingredient)) return true;
  const itemParts = item.split(" ").filter((part) => part.length > 2);
  const ingredientParts = ingredient
    .split(" ")
    .filter((part) => part.length > 2);
  return (
    itemParts.length > 0 &&
    itemParts.every((part) => ingredientParts.includes(part))
  );
}
function foodInventoryNamesMatch(pantryName, ingredientName) {
  const itemParts = normalizeFoodMatchName(pantryName)
    .split(" ")
    .filter(Boolean)
    .map((part) => (part.length > 3 && part.endsWith("s") ? part.slice(0, -1) : part));
  const ingredientParts = normalizeFoodMatchName(ingredientName)
    .split(" ")
    .filter(Boolean)
    .map((part) => (part.length > 3 && part.endsWith("s") ? part.slice(0, -1) : part));
  if (!itemParts.length || itemParts.length !== ingredientParts.length) return false;
  return itemParts.every((part, index) => part === ingredientParts[index]);
}
function pantryQuantityNumber(value) {
  const match = String(value ?? "").match(/\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}
function formatPantryQuantity(value) {
  return Number(value) % 1 === 0
    ? String(Number(value).toFixed(0))
    : String(Math.round(Number(value) * 100) / 100);
}
const COMMON_MEAL_STAPLES = new Set([
  "salt",
  "pepper",
  "black pepper",
  "water",
  "ice",
  "olive oil",
  "oil",
  "vegetable oil",
  "cooking oil",
  "butter",
  "sugar",
]);
function isCommonMealStaple(label) {
  return COMMON_MEAL_STAPLES.has(normalizeFoodMatchName(label));
}
function uniqueIngredientLabels(values = []) {
  const seen = new Set();
  return recipeIngredientLabels(values).filter((label) => {
    const key = normalizeFoodMatchName(label);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function splitIngredientsByPantry(ingredients = [], pantryItems = []) {
  const labels = uniqueIngredientLabels(ingredients);
  const pantry = Array.isArray(pantryItems) ? pantryItems : [];
  const available = [];
  const missing = [];
  labels.forEach((label) => {
    if (isCommonMealStaple(label)) return;
    const matched = pantry.some((item) =>
      foodInventoryNamesMatch(item?.name, label),
    );
    (matched ? available : missing).push(label);
  });
  return { available, missing };
}
function recipePantryTruth(recipe, pantryItems = []) {
  const fullIngredients = uniqueIngredientLabels(recipe?.ingredients);
  const claimedPantry = uniqueIngredientLabels(recipe?.uses_from_pantry);
  const claimedMissing = uniqueIngredientLabels(recipe?.still_need);
  const candidateIngredients = fullIngredients.length
    ? fullIngredients
    : uniqueIngredientLabels([...claimedPantry, ...claimedMissing]);
  const split = splitIngredientsByPantry(candidateIngredients, pantryItems);
  const missingKeys = new Set(split.missing.map(normalizeFoodMatchName));
  const availableKeys = new Set(split.available.map(normalizeFoodMatchName));
  claimedMissing.forEach((label) => {
    const key = normalizeFoodMatchName(label);
    if (key && !availableKeys.has(key) && !missingKeys.has(key)) {
      split.missing.push(label);
      missingKeys.add(key);
    }
  });
  return split;
}
function getNextMonday(date = new Date()) {
  const day = date.getDay();
  const daysUntilMonday = day === 1 ? 7 : (8 - day) % 7;
  const next = new Date(date);
  next.setDate(date.getDate() + daysUntilMonday);
  return localISODate(next);
}
function formatDateRangeFromIso(startIso, lengthDays = 6) {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(start);
  end.setDate(start.getDate() + lengthDays);
  const fmt = (d) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}
function mealPlanDate(plan, dayIndex) {
  if (!plan?.week_start && !plan?.weekStart) return null;
  const base = new Date(`${plan.week_start || plan.weekStart}T00:00:00`);
  base.setDate(base.getDate() + Number(dayIndex || 0));
  return base;
}
function mealPlanDayLabel(plan, day) {
  const date = mealPlanDate(plan, day.day_index);
  return date
    ? DAY_FULL_LABELS[date.getDay() === 0 ? 6 : date.getDay() - 1]
    : DAY_FULL_LABELS[day.day_index] || day.day_label;
}
function formatMealPlanRange(plan) {
  const start = mealPlanDate(plan, 0);
  const end = mealPlanDate(plan, 6);
  if (!start || !end) return "";
  const sameMonth = start.getMonth() === end.getMonth();
  const startLabel = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const endLabel = end.toLocaleDateString(
    undefined,
    sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" },
  );
  return `${startLabel}–${endLabel}`;
}
function mealPlanWeekStart(plan) {
  return String(plan?.week_start || plan?.weekStart || "").slice(0, 10);
}
function mealPlanCoversIso(plan, isoDate) {
  const startIso = mealPlanWeekStart(plan);
  if (!startIso || !isoDate) return false;
  const endDate = mealPlanDate({ week_start: startIso }, 6);
  const endIso = endDate ? localISODate(endDate) : "";
  return isoDate >= startIso && isoDate <= endIso;
}
function formatMealPlanDate(plan, day) {
  const date = mealPlanDate(plan, day.day_index);
  return date
    ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
}
function mealNameParts(name) {
  const raw = String(name || "").trim();
  const cuisineMatch = raw.match(/\s+\(([^)]+)\)\s*$/);
  let title = cuisineMatch ? raw.replace(/\s+\([^)]+\)\s*$/, "").trim() : raw;
  title = compactMealTitle(title);
  if (/^leftover purified water stir-?fry$/i.test(title))
    title = "Vegetable Stir-Fry";
  return { title, cuisine: cuisineMatch?.[1] || "" };
}
function compactMealTitle(name) {
  const raw = String(name || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!raw) return raw;
  const firstSentence = raw.split(/[.;:!?]\s+/)[0].trim();
  const words = firstSentence.split(" ").filter(Boolean);
  if (words.length <= 5 && firstSentence.length <= 55) return firstSentence;
  const lowered = words.map((word) =>
    word.replace(/[.,:;!?()[\]{}]/g, "").toLowerCase(),
  );
  const endWords = [
    "fried rice",
    "stir-fry",
    "stir fry",
    "omelette",
    "quesadilla",
    "frittata",
    "casserole",
    "traybake",
    "fajitas",
    "shakshuka",
    "skillet",
    "tagine",
    "pasta",
    "tacos",
    "salad",
    "soup",
    "stew",
    "bowl",
    "bake",
    "curry",
  ];
  for (const endWord of endWords) {
    const parts = endWord.split(" ");
    for (let index = 0; index <= lowered.length - parts.length; index += 1) {
      if (lowered.slice(index, index + parts.length).join(" ") === endWord) {
        const cut = index + parts.length;
        if (cut > 1 && cut <= 6) return words.slice(0, cut).join(" ");
      }
    }
  }
  const descriptionStarts = new Set([
    "a",
    "an",
    "the",
    "served",
    "topped",
    "filled",
    "made",
    "cooked",
    "simmered",
    "roasted",
    "sauteed",
    "baked",
    "crispy",
    "hearty",
    "fluffy",
    "colorful",
    "colourful",
    "savory",
    "savoury",
  ]);
  for (let index = 2; index < Math.min(words.length, 7); index += 1) {
    if (descriptionStarts.has(lowered[index]))
      return words.slice(0, index).join(" ");
  }
  return words.slice(0, 5).join(" ");
}
const LEGACY_GENERIC_MEAL_IMAGE_IDS = [
  "photo-1473093295043-cdd812d0e601",
  "photo-1551183053-bf91a1d81141",
  "photo-1621996346565-e3dbc646d9a9",
  "photo-1546069901-ba9599a7e63c",
  "photo-1498837167922-ddd27525d352",
  "photo-1540189549336-e6e99c3679fe",
  "photo-1512058564366-18510be2db19",
  "photo-1512621776951-a57141f2eefd",
  "photo-1540420773420-3366772f4999",
  "photo-1547592166-23ac45744acd",
  "photo-1604152135912-04a022e23696",
  "photo-1525351484163-7529414344d8",
  "photo-1525351326368-efbb5cb6814d",
  "photo-1582169505937-b9992bd01ed9",
  "photo-1509722747041-616f39b57569",
  "photo-1553909489-cd47e0907980",
  "photo-1528735602780-2552fd46c7af",
  "photo-1598515214211-89d3c73ae83b",
  "photo-1532550907401-a500c9a57435",
  "photo-1604908176997-125f25cc6f3d",
  "photo-1513104890138-7c749659a591",
  "photo-1565299624946-b28f40a0ae38",
  "photo-1559847844-5315695dadae",
  "photo-1624726175512-19b9baf9fbd1",
  "photo-1562967914-608f82629710",
  "photo-1565299585323-38d6b0865b47",
  "photo-1551504734-5ee1c4a1479b",
  "photo-1624300629298-e9de39c13be8",
  "photo-1603133872878-684f208fb84b",
  "photo-1569058242253-92a9c755a0ec",
  "photo-1600891964092-4316c288032e",
  "photo-1558030006-450675393462",
  "photo-1517673400267-0251440c45dc",
  "photo-1495147466023-ac5c588e2e94",
  "photo-1483648969698-5e7dcaa3444f",
  "photo-1554520735-0a6b8b6ce8b7",
  "photo-1528207776546-365bb710ee93",
  "photo-1481070414801-51fd732d7184",
  "photo-1502741224143-90386d7f8c82",
  "photo-1490885578174-acda8905c2c6",
  "photo-1541519227354-08fa5d50c820",
];
function isLegacyGenericMealImageUrl(url = "") {
  const value = String(url || "");
  return LEGACY_GENERIC_MEAL_IMAGE_IDS.some((id) => value.includes(id));
}
function validMealImageUrl(url = "") {
  return isLegacyGenericMealImageUrl(url) ? "" : String(url || "");
}
function mealPlanImageUrl(day) {
  return validMealImageUrl(
    day?.photo_url ||
      day?.image_url ||
      day?.recipe?.photo_url ||
      day?.recipe?.image_url ||
      "",
  );
}
function mealIconForTitle(title = "") {
  const text = String(title || "").toLowerCase();
  if (/(taco|quesadilla|fajita|tortilla)/.test(text)) return "🌮";
  if (/(pasta|alfredo|carbonara|spaghetti|lasagna|shell|parmesan)/.test(text))
    return "🍝";
  if (/(soup|stew|chili|pozole)/.test(text)) return "🍲";
  if (/(salad|greens)/.test(text)) return "🥗";
  if (/(rice|bowl|curry|masala)/.test(text)) return "🍛";
  if (/(beef|steak|stroganoff|shepherd)/.test(text)) return "🍽";
  if (/(chicken|turkey)/.test(text)) return "🍗";
  if (/(egg|frittata|omelette|shakshuka)/.test(text)) return "🍳";
  return "🍽";
}
function recipeIngredientLabels(values) {
  return (values || [])
    .map((ing) =>
      typeof ing === "string"
        ? ing
        : [ing.quantity, ing.unit, ing.name].filter(Boolean).join(" "),
    )
    .filter(Boolean);
}
const MEAL_INGREDIENT_FALLBACKS = {
  "turkey avocado wrap": ["turkey", "tortilla", "avocado", "lettuce"],
  "turkey avocado sandwich": ["turkey", "bread", "avocado", "lettuce"],
  "avocado toast": ["bread", "avocado"],
  "greek yogurt parfait": ["yogurt", "granola", "berries"],
  "berry smoothie": ["berries", "banana", "milk"],
  "banana oatmeal": ["oats", "banana", "milk"],
  "vegetable omelette": ["eggs", "pepper", "onion", "cheese"],
  "breakfast burrito": ["tortilla", "eggs", "cheese", "salsa"],
  "chicken caesar salad": ["chicken", "romaine", "parmesan", "croutons"],
  "caprese sandwich": ["mozzarella", "tomato", "basil", "bread"],
  "black bean quesadilla": ["tortilla", "black beans", "cheese", "salsa"],
  "egg salad sandwich": ["eggs", "mayo", "mustard", "bread"],
  "chicken rice bowl": ["chicken", "rice", "cucumber", "soy sauce"],
};
function fallbackIngredientsForMealTitle(title) {
  const key = String(title || "")
    .trim()
    .toLowerCase();
  if (!key) return [];
  if (MEAL_INGREDIENT_FALLBACKS[key]) return MEAL_INGREDIENT_FALLBACKS[key];
  const inferred = [];
  const text = key.replace(/[^a-z0-9\s]/g, " ");
  [
    "chicken",
    "turkey",
    "beef",
    "salmon",
    "shrimp",
    "egg",
    "eggs",
    "bean",
    "beans",
    "avocado",
    "tomato",
    "rice",
    "pasta",
    "corn",
    "cheese",
    "yogurt",
    "banana",
    "berries",
    "oats",
    "milk",
  ].forEach((token) => {
    if (text.includes(token)) inferred.push(token);
  });
  if (/\bwrap\b/.test(text)) inferred.push("tortilla");
  if (/\bsandwich\b|\btoast\b|\bpanini\b/.test(text)) inferred.push("bread");
  if (/\bsalad\b/.test(text)) inferred.push("lettuce");
  if (/\btaco|quesadilla|fajita\b/.test(text)) inferred.push("tortilla");
  if (/\bomelette|omelet|scramble|frittata\b/.test(text)) inferred.push("eggs");
  if (/\bsmoothie\b/.test(text)) inferred.push("milk");
  if (/\bparfait\b/.test(text)) inferred.push("yogurt");
  if (/\bpancake|waffle|crepe\b/.test(text))
    inferred.push("flour", "eggs", "milk");
  return [...new Set(inferred)].slice(0, 5);
}
function mealPlanDayIngredients(day, recipe) {
  // Priority order: recipe object's ingredients → day.recipe_ingredients (saved to DB)
  // → day.ingredients (AI top-level key, now saved by backend but keep for safety)
  // → uses_from_pantry → keyword fallback from meal name
  const recipeIngredients = recipeIngredientLabels(recipe?.ingredients);
  if (recipeIngredients.length) return recipeIngredients;
  const dayIngredients = recipeIngredientLabels(day?.recipe_ingredients);
  if (dayIngredients.length) return dayIngredients;
  const dayDirectIngredients = recipeIngredientLabels(day?.ingredients);
  if (dayDirectIngredients.length) return dayDirectIngredients;
  const pantryIngredients = recipeIngredientLabels(
    recipe?.uses_from_pantry || day?.uses_from_pantry,
  );
  if (pantryIngredients.length) return pantryIngredients;
  return fallbackIngredientsForMealTitle(recipe?.title || day?.meal_name);
}
function mealPlanDisplay(day, parts, usedImageUrls = null) {
  const fallback = MEAL_PLAN_TEST_MEALS[Number(day?.day_index || 0)] || null;
  const titleFallback =
    MEAL_PLAN_TITLE_FALLBACKS[
      String(parts?.title || day?.meal_name || "")
        .trim()
        .toLowerCase()
    ] || null;
  const title = parts?.title || fallback?.name || "";
  const explicitImageUrl = mealPlanImageUrl(day);
  const imageUrl = explicitImageUrl || "";
  if (usedImageUrls) {
    if (imageUrl) usedImageUrls.add(imageUrl);
  }
  return {
    title,
    sub:
      day?.recipe_description ||
      day?.recipe?.description ||
      titleFallback?.sub ||
      "",
    tags: [day?.cuisine_tag, ...(titleFallback?.tags || [])].filter(Boolean),
    time:
      day?.cook_time ||
      (day?.cook_time_minutes ? `${day.cook_time_minutes} min` : "") ||
      titleFallback?.time ||
      "",
    imageUrl,
    icon: mealIconForTitle(title),
  };
}
function mealPlanTags(day, fallbackTags = []) {
  const rawTags = [
    ...(Array.isArray(day?.tags) ? day.tags : []),
    ...(Array.isArray(day?.recipe?.tags) ? day.recipe.tags : []),
    ...fallbackTags,
  ].filter(Boolean);
  const nutrition = day?.recipe?.nutrition || {};
  if (Number(nutrition.protein || nutrition.Protein || 0) >= 25)
    rawTags.push("High Protein");
  if (
    Number(nutrition.calories || nutrition.Calories || 0) > 0 &&
    Number(nutrition.calories || nutrition.Calories || 0) <= 450
  )
    rawTags.push("Low Cal");
  return [
    ...new Set(
      rawTags
        .map((tag) => String(tag).trim())
        .filter((tag) => MEAL_PLAN_ALLOWED_TAGS.has(tag)),
    ),
  ].slice(0, 3);
}
function recipeStepsForDay(day, recipe) {
  const steps = recipe?.instructions?.length
    ? recipe.instructions
    : day?.recipe_steps?.length
      ? day.recipe_steps
      : day?.notes
        ? [day.notes]
        : [];
  const cleaned = steps
    .map((step) => String(step || "").trim())
    .filter(Boolean);
  const ingredients = mealPlanDayIngredients(day, recipe);
  const pantryPrep = ingredients.length
    ? `Gather ${ingredients.slice(0, 4).join(", ")} from your pantry.`
    : "Gather the pantry ingredients for this meal.";
  if (cleaned.length === 0 && (day?.meal_name || recipe?.title)) {
    return [
      pantryPrep,
      `Cook ${day?.meal_name || recipe?.title} until the main ingredients are tender and well seasoned.`,
      "Taste, adjust seasoning, and serve warm.",
    ];
  }
  if (cleaned.length !== 1) return cleaned;
  return [pantryPrep, cleaned[0], "Taste, adjust seasoning, and serve warm."];
}

const RECIPE_TIME_OPTIONS = [15, 20, 30, 45, 60];
function recipeImageUrl(recipe) {
  return validMealImageUrl(
    recipe?.image_url ||
      recipe?.photo_url ||
      recipe?.recipe?.image_url ||
      recipe?.recipe?.photo_url ||
      "",
  );
}

/**
 * useMealImages — lazily fetches a real Unsplash photo for each meal in the
 * current plan that doesn't already have an explicit image URL.
 *
 * Returns a Map<mealImageKey(day), imageUrl> that components can use to
 * override empty/loading image slots.
 *
 * - Fires one request per unique meal name (not per day).
 * - Requests are staggered by 120 ms so we don't hammer the server.
 * - Results survive re-renders via a ref-backed module-level cache so the
 *   same meal name is never fetched twice per browser session.
 */
const _sessionImageCache = new Map();

function mealImageKey(day) {
  const mealType = String(day?.meal_type || "dinner")
    .trim()
    .toLowerCase();
  const dayIndex = Number.isInteger(day?.day_index) ? day.day_index : "";
  const name = String(day?.meal_name || "")
    .trim()
    .toLowerCase();
  return `${mealType}:${dayIndex}:${name}`;
}

function mealDayKey(day) {
  if (!day) return "";
  const mealType = String(day.meal_type || "dinner")
    .trim()
    .toLowerCase();
  return `${mealType}:${day.day_index}`;
}

function useMealImages(plan) {
  const [imageMap, setImageMap] = useState(() => new Map());
  const pendingRef = useRef(new Set());
  const mealImageSignature = (plan?.days || [])
    .map((day) =>
      [
        day?.id || "",
        day?.meal_type || "dinner",
        day?.day_index ?? "",
        day?.meal_name || "",
        day?.photo_url ||
          day?.image_url ||
          day?.recipe?.photo_url ||
          day?.recipe?.image_url ||
          "",
      ].join(":"),
    )
    .join("|");

  useEffect(() => {
    if (!plan?.days?.length) return;

    const currentImageKeys = new Set(plan.days.map((day) => mealImageKey(day)));
    setImageMap((prev) => {
      const next = new Map();
      // Carry forward any previously-resolved images for keys still in this plan
      prev.forEach((url, key) => {
        if (currentImageKeys.has(key)) next.set(key, url);
      });
      // Seed immediately from day.image_url so cards render on first paint
      for (const day of plan.days) {
        const explicit = validMealImageUrl(
          day?.photo_url ||
            day?.image_url ||
            day?.recipe?.photo_url ||
            day?.recipe?.image_url,
        );
        if (explicit) {
          const key = mealImageKey(day);
          next.set(key, explicit);
          // Also prime the session cache so the fetch loop won't re-request it
          const cacheKey = String(day.meal_name || "")
            .trim()
            .toLowerCase();
          if (cacheKey) _sessionImageCache.set(cacheKey, explicit);
        }
      }
      return next;
    });

    // Only fetch for days that have no image_url from the server
    const days = plan.days.filter((day) => {
      const explicit = validMealImageUrl(
        day?.photo_url ||
          day?.image_url ||
          day?.recipe?.photo_url ||
          day?.recipe?.image_url,
      );
      return !explicit && day?.meal_name;
    });

    if (!days.length) return;

    let cancelled = false;
    (async () => {
      for (let i = 0; i < days.length; i += 1) {
        if (cancelled) break;
        const day = days[i];
        const name = String(day.meal_name || "").trim();
        if (!name) continue;
        const cacheKey = name.toLowerCase();
        const dayImageKey = mealImageKey(day);

        // Already resolved this session
        if (_sessionImageCache.has(cacheKey)) {
          setImageMap((prev) => {
            const next = new Map(prev);
            next.set(dayImageKey, _sessionImageCache.get(cacheKey));
            return next;
          });
          continue;
        }

        // Request already in flight
        if (pendingRef.current.has(cacheKey)) continue;
        pendingRef.current.add(cacheKey);

        // Stagger requests so we don't hit rate limits
        if (i > 0) await new Promise((resolve) => setTimeout(resolve, 140 * i));
        if (cancelled) break;

        try {
          const result = await mealsApi.mealImage(
            name,
            day.meal_type || "dinner",
          );
          if (result?.image_url) {
            _sessionImageCache.set(cacheKey, result.image_url);
            setImageMap((prev) => {
              const next = new Map(prev);
              next.set(dayImageKey, result.image_url);
              return next;
            });
          }
        } catch {
          // silently ignore — empty image slot stays in place
        } finally {
          pendingRef.current.delete(cacheKey);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [plan?.id, plan?.days, mealImageSignature]);

  return [imageMap, setImageMap];
}

function recipeImageKey(recipe) {
  return String(recipe?.id || recipe?.title || "")
    .trim()
    .toLowerCase();
}

function useRecipeImages(recipes) {
  const [imageMap, setImageMap] = useState(() => new Map());
  const [pendingKeys, setPendingKeys] = useState(() => new Set());
  const pendingRef = useRef(new Set());
  const recipeSignature = (recipes || [])
    .map((recipe) =>
      [
        recipe?.id || "",
        recipe?.title || "",
        recipe?.meal_type || "dinner",
      ].join(":"),
    )
    .join("|");

  useEffect(() => {
    const items = (recipes || []).filter(
      (recipe) => recipe?.title && !recipeImageUrl(recipe),
    );
    if (!items.length) {
      setImageMap(new Map());
      setPendingKeys(new Set());
      return;
    }

    const currentKeys = new Set(items.map((recipe) => recipeImageKey(recipe)));
    setImageMap((prev) => {
      const next = new Map();
      prev.forEach((url, key) => {
        if (currentKeys.has(key)) next.set(key, url);
      });
      return next;
    });

    let cancelled = false;
    (async () => {
      for (let i = 0; i < items.length; i += 1) {
        if (cancelled) break;
        const recipe = items[i];
        const name = String(recipe.title || "").trim();
        if (!name) continue;
        const cacheKey = name.toLowerCase();
        const key = recipeImageKey(recipe);

        if (_sessionImageCache.has(cacheKey)) {
          setImageMap((prev) => {
            const next = new Map(prev);
            next.set(key, _sessionImageCache.get(cacheKey));
            return next;
          });
          setPendingKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          continue;
        }
        if (pendingRef.current.has(cacheKey)) continue;
        pendingRef.current.add(cacheKey);
        setPendingKeys((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });

        if (i > 0) await new Promise((resolve) => setTimeout(resolve, 120 * i));
        if (cancelled) {
          pendingRef.current.delete(cacheKey);
          setPendingKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          break;
        }

        try {
          const result = await mealsApi.mealImage(
            name,
            recipe.meal_type || "dinner",
          );
          if (result?.image_url) {
            _sessionImageCache.set(cacheKey, result.image_url);
            setImageMap((prev) => {
              const next = new Map(prev);
              next.set(key, result.image_url);
              return next;
            });
          }
        } catch {
          // Keep existing saved image if lookup fails.
        } finally {
          pendingRef.current.delete(cacheKey);
          setPendingKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [recipeSignature, recipes]);

  return [imageMap, pendingKeys];
}

function MealPlanTab({
  pantry,
  prefs,
  setPrefs,
  workspaceId,
  aiProvider,
  onMarkItemsUsed,
  onCookMeal,
  onAiUsage,
  onShare,
  canShare,
  sharedResourceKeys,
  onToast,
  onSetCookingTonight,
  onStatsChanged,
  onPantryRefresh,
  onShoppingPlanContext,
  requestedMealDayId,
}) {
  const [plan, setPlan] = useState(null);
  const [mealPlanList, setMealPlanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [shoppingBusy, setShoppingBusy] = useState(false);
  // shoppingDone is persisted to localStorage so it survives food-tab switches
  // (MealPlanTab fully unmounts/remounts when switching Pantry ↔ Meal Plan ↔ Shopping).
  const shoppingStorageKey = workspaceId
    ? `hm_shopping_done:${workspaceId}`
    : "hm_shopping_done";
  function readPersistedShoppingDone(planId) {
    try {
      const raw = localStorage.getItem(shoppingStorageKey);
      if (!raw) return false;
      const { id, done } = JSON.parse(raw);
      return id === planId && done === true;
    } catch {
      return false;
    }
  }
  function persistShoppingDone(planId, done) {
    try {
      if (done && planId)
        localStorage.setItem(
          shoppingStorageKey,
          JSON.stringify({ id: planId, done: true }),
        );
      else localStorage.removeItem(shoppingStorageKey);
    } catch {}
  }
  const [shoppingDone, setShoppingDoneRaw] = useState(false);
  function setShoppingDone(val, planId) {
    setShoppingDoneRaw(val);
    persistShoppingDone(planId ?? plan?.id, val);
  }
  const [generationJobMsg, setGenerationJobMsg] = useState("");
  const [clearingPlan, setClearingPlan] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [planResetIntent, setPlanResetIntent] = useState("regenerate");
  const [clearChoice, setClearChoice] = useState(null);
  const [showPassedMealsByType, setShowPassedMealsByType] = useState({});
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [replacingDay, setReplacingDay] = useState(false);
  const [recentlyUpdatedDayKey, setRecentlyUpdatedDayKey] = useState(null);
  const [savingSheetRecipe, setSavingSheetRecipe] = useState(false);
  const [compactMealSheet, setCompactMealSheet] = useState(false);
  const [dislikeToast, setDislikeToast] = useState(null);
  const [dislikeSheetDay, setDislikeSheetDay] = useState(null);
  const [dislikeChoice, setDislikeChoice] = useState(null);
  const [swapSheetDay, setSwapSheetDay] = useState(null);
  const [manualMealName, setManualMealName] = useState("");
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [savedMealOptions, setSavedMealOptions] = useState([]);
  const [savedMealsLoading, setSavedMealsLoading] = useState(false);
  const [mealActionNotice, setMealActionNotice] = useState(null);
  const [shoppingIngredientTarget, setShoppingIngredientTarget] = useState(null);
  const [shoppingIngredientBusy, setShoppingIngredientBusy] = useState(false);
  const [shoppingAddedIngredientKeys, setShoppingAddedIngredientKeys] = useState(() => new Set());
  const [shoppingIngredientNames, setShoppingIngredientNames] = useState([]);
  const [liveMealShortage, setLiveMealShortage] = useState(null);
  const [savedRecipeDayKeys, setSavedRecipeDayKeys] = useState(() => new Set());
  const usedPantryStorageKey = workspaceId
    ? `hm_meal_pantry_used:${workspaceId}`
    : "hm_meal_pantry_used";
  const postedCookingStorageKey = workspaceId
    ? `hm_meal_cooking_posted:${workspaceId}`
    : "hm_meal_cooking_posted";
  const skippedMealStorageKey = workspaceId
    ? `hm_meal_skipped:${workspaceId}`
    : "hm_meal_skipped";
  const [usedPantryDayKeys, setUsedPantryDayKeys] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem(usedPantryStorageKey) || "[]"),
      );
    } catch {
      return new Set();
    }
  });
  const [postedCookingDayKeys, setPostedCookingDayKeys] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem(postedCookingStorageKey) || "[]"),
      );
    } catch {
      return new Set();
    }
  });
  const [skippedMealDayKeys, setSkippedMealDayKeys] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem(skippedMealStorageKey) || "[]"),
      );
    } catch {
      return new Set();
    }
  });
  const avoidStorageBaseKey = workspaceId
    ? `${FOOD_RECENT_AVOID_KEY}:${workspaceId}`
    : FOOD_RECENT_AVOID_KEY;
  const lovedStorageKey = workspaceId
    ? `${FOOD_LOVED_MEALS_KEY}:${workspaceId}`
    : FOOD_LOVED_MEALS_KEY;

  const [lovedMeals, setLovedMeals] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(lovedStorageKey) || "[]");
    } catch {
      return [];
    }
  });
  const activeMealTypeKey = workspaceId
    ? `hm_active_meal_type:${workspaceId}`
    : "hm_active_meal_type";
  const [activeMealType, setActiveMealType] = useState(() => {
    try {
      return localStorage.getItem(activeMealTypeKey) || "dinner";
    } catch {
      return "dinner";
    }
  });
  const activeMealMeta = mealTypeMeta(activeMealType);
  const activeMealLabel = activeMealMeta.label;
  const activeMealLower = activeMealMeta.lower;
  const activeMealPlural = activeMealMeta.plural || `${activeMealLower}s`;
  const activeMealShort = activeMealMeta.short || activeMealLabel;
  const activeMealPlanLabel = `${activeMealLower} plan`;
  const avoidStorageKey = `${avoidStorageBaseKey}:${activeMealType}`;
  function changeActiveMealType(type) {
    setActiveMealType(type);
    try {
      localStorage.setItem(activeMealTypeKey, type);
    } catch {}
    setSelectedDayKey(null);
  }
  function togglePassedMealsForType(type) {
    setShowPassedMealsByType((current) => ({
      ...current,
      [type]: !current[type],
    }));
  }
  const [savingPlan, setSavingPlan] = useState(false);

  function toggleLovedMeal(name) {
    if (!name) return;
    setLovedMeals((prev) => {
      const next = prev.includes(name)
        ? prev.filter((n) => n !== name)
        : [...prev, name];
      try {
        localStorage.setItem(lovedStorageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
  }
  const [recentAvoidMeals, setRecentAvoidMeals] = useState([]);
  const dislikeUndoTimerRef = useRef(null);
  const swipeTouchRef = useRef(null); // { startX, startY, day }
  const showMealPlanWorking = useMinLoad(
    generating || savingPlan || shoppingBusy || replacingDay || clearingPlan,
    700,
  );
  const [mealImages] = useMealImages(plan);

  const closeSelectedMeal = useCallback(() => {
    setSelectedDayKey(null);
  }, []);

  useEffect(() => {
    const query = window.matchMedia(
      `(max-width: 768px), ${PORTRAIT_TABLET_QUERY}`,
    );
    const update = () => setCompactMealSheet(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (!plan || !requestedMealDayId) return;
    const requestedDay = (plan.days || []).find(
      day => Number(day.id) === Number(requestedMealDayId),
    );
    if (!requestedDay) return;
    setActiveMealType(requestedDay.meal_type || "dinner");
    setSelectedDayKey(mealDayKey(requestedDay));
  }, [plan, requestedMealDayId]);

  useEffect(() => {
    if (recentlyUpdatedDayKey === null) return undefined;
    const timer = window.setTimeout(() => setRecentlyUpdatedDayKey(null), 9000);
    return () => window.clearTimeout(timer);
  }, [recentlyUpdatedDayKey]);

  useEffect(
    () => () => {
      if (dislikeUndoTimerRef.current)
        window.clearTimeout(dislikeUndoTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const p = await mealsApi.current();
        setPlan(p);
        // Seed from localStorage immediately so button doesn't flicker on remount
        if (p?.id && readPersistedShoppingDone(p.id)) setShoppingDoneRaw(true);
        await loadShoppingStateForPlan(p);
        await loadShoppingIngredientNames(p);
        const plans = await mealsApi.list();
        setMealPlanList(Array.isArray(plans) ? plans : []);
      } catch (e) {
        /* no plan yet */
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onShoppingPlanContext?.(
      plan
        ? {
            id: plan.id,
            week_start: mealPlanWeekStart(plan),
            range: formatMealPlanRange(plan),
            is_current_week: mealPlanCoversIso(plan, localISODate()),
          }
        : null,
    );
  }, [plan, onShoppingPlanContext]);

  async function loadShoppingIngredientNames(targetPlan) {
    try {
      const [flatResult, listSummaries] = await Promise.all([
        shoppingApi.list(),
        mealsApi.shoppingLists(),
      ]);
      const names = (flatResult?.items || [])
        .filter(item => !item.checked)
        .map(item => item.name)
        .filter(Boolean);
      const matchingList = (listSummaries || []).find(
        list => Number(list.meal_plan_id) === Number(targetPlan?.id),
      );
      if (matchingList?.id) {
        const fullList = await mealsApi.getShoppingList(matchingList.id);
        names.push(
          ...(fullList?.items || [])
            .filter(item => !item.is_checked)
            .map(item => item.name)
            .filter(Boolean),
        );
      }
      setShoppingIngredientNames(names);
    } catch {
      // Shopping state is supplementary; meal plan loading must still succeed.
    }
  }

  async function loadShoppingStateForPlan(targetPlan) {
    if (!targetPlan?.id) {
      setShoppingDoneRaw(false);
      return;
    }
    // Fast path: restore from localStorage while the API call runs
    if (readPersistedShoppingDone(targetPlan.id)) {
      setShoppingDoneRaw(true);
    }
    try {
      const status = await mealsApi.shoppingListStatus(targetPlan.id);
      const hasCurrentList = Boolean(status?.has_current_list);
      setShoppingDoneRaw(hasCurrentList);
      persistShoppingDone(targetPlan.id, hasCurrentList);
      return;
    } catch {
      // Fall back to the summary list below; older deployments may not have the
      // exact status endpoint yet.
    }
    try {
      const lists = await mealsApi.shoppingLists();
      const found = (lists || []).some(
        (list) =>
          (list.meal_plan_id === targetPlan.id ||
            list.meal_plan_week_start === targetPlan.week_start) &&
          (list.status === "active" || list.status === "completed"),
      );
      setShoppingDoneRaw(found);
      persistShoppingDone(targetPlan.id, found);
    } catch {
      // API failed — keep whatever localStorage said (already applied above)
    }
  }

  async function showMealPlan(targetPlan) {
    if (!targetPlan) return;
    setPlan(targetPlan);
    setSelectedDayKey(null);
    setRecentlyUpdatedDayKey(null);
    await loadShoppingStateForPlan(targetPlan);
  }

  useEffect(() => {
    try {
      localStorage.removeItem(FOOD_RECENT_AVOID_KEY);
      localStorage.removeItem(avoidStorageBaseKey);
      const cutoff = Date.now() - 21 * 24 * 60 * 60 * 1000;
      const stored = JSON.parse(localStorage.getItem(avoidStorageKey) || "[]");
      const scoped = (Array.isArray(stored) ? stored : [])
        .filter((item) => item?.name && Number(item.savedAt || 0) >= cutoff)
        .map((item) => item.name);
      setRecentAvoidMeals(scoped);
    } catch {
      setRecentAvoidMeals([]);
    }
  }, [avoidStorageBaseKey, avoidStorageKey]);

  const loadSavedMealOptions = useCallback(async () => {
    setSavedMealsLoading(true);
    try {
      const data = await mealsApi.savedRecipes();
      setSavedMealOptions(
        Array.isArray(data.recipes)
          ? data.recipes.filter(
              (recipe) =>
                String(recipe.source || "").toLowerCase() !==
                "meal_image_cache",
            )
          : [],
      );
    } catch {
      setSavedMealOptions([]);
    } finally {
      setSavedMealsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSavedMealOptions();
  }, [loadSavedMealOptions]);

  useEffect(() => {
    try {
      setUsedPantryDayKeys(
        new Set(JSON.parse(localStorage.getItem(usedPantryStorageKey) || "[]")),
      );
    } catch {
      setUsedPantryDayKeys(new Set());
    }
  }, [usedPantryStorageKey]);

  useEffect(() => {
    try {
      setPostedCookingDayKeys(
        new Set(
          JSON.parse(localStorage.getItem(postedCookingStorageKey) || "[]"),
        ),
      );
    } catch {
      setPostedCookingDayKeys(new Set());
    }
  }, [postedCookingStorageKey]);

  useEffect(() => {
    try {
      setSkippedMealDayKeys(
        new Set(
          JSON.parse(localStorage.getItem(skippedMealStorageKey) || "[]"),
        ),
      );
    } catch {
      setSkippedMealDayKeys(new Set());
    }
  }, [skippedMealStorageKey]);

  function openPlanReset(intent) {
    setPlanResetIntent(intent);
    setClearChoice(null);
    setShowClearConfirm(true);
  }

  function openPlanGeneration() {
    openPlanReset(
      hasAnyMealType(activeMealType) ? "regenerate" : "generate",
    );
  }

  function openDislikeSheet(day) {
    if (!day) return;
    setDislikeSheetDay(day);
    setDislikeChoice(null);
  }

  function closeDislikeSheet() {
    if (replacingDay) return;
    setDislikeSheetDay(null);
    setDislikeChoice(null);
  }

  function openSwapSheet(day) {
    if (!day) return;
    const date = mealPlanDate(plan, day.day_index);
    const dateIso = date ? localISODate(date) : "";
    if (dateIso && dateIso < localISODate()) {
      onToast?.(
        `Past ${mealTypeMeta(day.meal_type || activeMealType).lower}s cannot be swapped`,
      );
      return;
    }
    setSwapSheetDay(day);
    setManualMealName("");
    setManualEntryOpen(false);
    loadSavedMealOptions();
  }

  function openSwapSheetAfterMealClose(day) {
    if (!day) return;
    closeSelectedMeal();
    window.setTimeout(() => openSwapSheet(day), 0);
  }

  function closeSwapSheet() {
    if (replacingDay) return;
    setSwapSheetDay(null);
    setManualMealName("");
    setManualEntryOpen(false);
  }

  function rememberAvoidMeals(names = []) {
    const cleanNames = names
      .map((name) => String(name || "").trim())
      .filter(Boolean);
    if (!cleanNames.length) return;
    const now = Date.now();
    const cutoff = now - 21 * 24 * 60 * 60 * 1000;
    // Read the raw stored objects so we preserve their original savedAt timestamps.
    // Previously we used recentAvoidMeals (just names) and stamped everything with
    // `now`, which reset the age of every existing entry and prevented expiry.
    let storedItems = [];
    try {
      const raw = localStorage.getItem(avoidStorageKey);
      storedItems = JSON.parse(raw || "[]");
      if (!Array.isArray(storedItems)) storedItems = [];
    } catch {
      storedItems = [];
    }
    const merged = [
      ...cleanNames.map((name) => ({ name, savedAt: now })),
      ...storedItems,
    ].filter((item) => item?.name && Number(item.savedAt || 0) >= cutoff);
    const byName = new Map();
    merged.forEach((item) => {
      const key = String(item.name || "").toLowerCase();
      if (key && !byName.has(key)) byName.set(key, item);
    });
    const next = [...byName.values()].slice(0, 40);
    setRecentAvoidMeals(next.map((item) => item.name));
    localStorage.setItem(avoidStorageKey, JSON.stringify(next));
  }

  function mealScheduleActionKey(day) {
    if (!day) return "";
    const planKey = plan?.id || plan?.week_start || "current";
    const dayKey = day.id || mealDayKey(day);
    const mealName = String(day.meal_name || "")
      .trim()
      .toLowerCase();
    return `${planKey}:${dayKey}:${mealName}`;
  }

  function rememberMealAction(storageKey, setter, key) {
    if (!key) return;
    setter((prev) => {
      const next = new Set(prev);
      next.add(key);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }

  function forgetMealAction(storageKey, setter, key) {
    if (!key) return;
    setter((prev) => {
      const next = new Set(prev);
      next.delete(key);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }

  async function markMealCookedAndUsePantry(day, { closeSheet = true } = {}) {
    if (!day) return 0;
    const actionKey = mealScheduleActionKey(day);
    if (usedPantryDayKeys.has(actionKey)) return 0;
    const ingredients = mealPlanDayIngredients(day, day.recipe);
    const result = onCookMeal
      ? await onCookMeal(day)
      : { confirmed: true, pantryCount: await onMarkItemsUsed?.(ingredients) };
    if (result?.confirmed) {
      rememberMealAction(usedPantryStorageKey, setUsedPantryDayKeys, actionKey);
      forgetMealAction(skippedMealStorageKey, setSkippedMealDayKeys, actionKey);
      if (closeSheet) closeSelectedMeal();
    }
    return result?.pantryCount || 0;
  }

  function markMealSkipped(day, { silent = false } = {}) {
    const actionKey = mealScheduleActionKey(day);
    if (!actionKey) return;
    rememberMealAction(skippedMealStorageKey, setSkippedMealDayKeys, actionKey);
    if (!silent)
      onToast?.(
        `${mealTypeMeta(day.meal_type || activeMealType).label} marked skipped`,
      );
    if (selectedDayKey === mealDayKey(day)) closeSelectedMeal();
  }

  async function generate({ weekStart, replaceExisting = false } = {}) {
    setGenerating(true);
    setError("");
    const targetWeekStart =
      weekStart || mealPlanWeekStart(plan) || getNextMonday();
    const existingLaneNames = replaceExisting
      ? (plan?.days || [])
          .filter((day) => (day.meal_type || "dinner") === activeMealType)
          .map((day) => day.meal_name)
          .filter(Boolean)
      : [];
    const avoidMeals = [...recentAvoidMeals, ...existingLaneNames];

    try {
      // Create the background job — returns immediately
      const jobResp = await mealsApi.createGenerationJob({
        meal_type: activeMealType,
        week_start: targetWeekStart,
        avoid_meals: avoidMeals,
        loved_meals: lovedMeals,
        provider: aiProvider || "openai",
        replace_existing: replaceExisting || !plan?.id,
      });

      const jobId = jobResp.job_id;

      // Production generation can legitimately take several minutes when the
      // provider retries or HouseMind repairs variety/quality issues. Keep the
      // sheet attached to the server job long enough to receive that result,
      // while polling slowly enough not to create avoidable database egress.
      const POLL_MS = 5000;
      const MAX_POLLS = 96; // 8 minutes max
      let consecutivePollErrors = 0;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        let job;
        try {
          job = await mealsApi.getGenerationJob(jobId);
          consecutivePollErrors = 0;
        } catch (pollError) {
          consecutivePollErrors += 1;
          if (consecutivePollErrors >= 3) {
            const msg =
              pollError?.message ||
              "HouseMind could not check meal-generation progress. Please try again.";
            setError(msg);
            onToast?.(msg);
            return;
          }
          continue; // tolerate two brief network hiccups
        }

        setGenerationJobMsg(
          job.progress_msg ||
            (i >= 24
              ? "Still preparing your week — this can take a few minutes…"
              : ""),
        );

        if (job.status === "complete") {
          if (job.ai_usage) onAiUsage?.(job.ai_usage);
          const saved = job.plan;
          if (!saved) {
            setError("Plan saved but could not load. Please refresh.");
            onToast?.("Meal plan saved but could not load. Please refresh.");
            return;
          }
          const savedLaneDays = (saved.days || []).filter(
            (day) => (day.meal_type || "dinner") === activeMealType,
          );
          if (savedLaneDays.length === 0) {
            const msg = `${activeMealLabel} generation finished but no ${activeMealPlural} were saved. Please try again.`;
            setError(msg);
            onToast?.(msg);
            return;
          }
          const plans = await mealsApi.list().catch(() => []);
          setMealPlanList(Array.isArray(plans) ? plans : []);
          const todayIsoForPlan = localISODate();
          const savedIsFuture = mealPlanWeekStart(saved) > todayIsoForPlan;
          const keepCurrentWeekVisible =
            savedIsFuture && mealPlanCoversIso(plan, todayIsoForPlan);
          if (!keepCurrentWeekVisible) {
            await showMealPlan(saved);
          }
          setRecentlyUpdatedDayKey(null);
          rememberAvoidMeals(
            savedLaneDays.map((d) => d.meal_name).filter(Boolean),
          );
          onStatsChanged?.();
          onToast?.(
            keepCurrentWeekVisible
              ? `✓ ${activeMealLabel} plan saved for ${formatMealPlanRange(saved) || "next week"}`
              : `✓ ${activeMealLabel} plan generated`,
          );
          if (keepCurrentWeekVisible) setSelectedDayKey(null);
          return;
        }

        if (job.status === "failed") {
          if (job.ai_usage) onAiUsage?.(job.ai_usage);
          const msg =
            job.error_message ||
            `${activeMealLabel} generation failed. Please try again.`;
          setError(msg);
          onToast?.(msg);
          return;
        }
        // queued / generating / saving — keep polling
      }
      setError(
        "Meal generation is taking longer than expected. Please try again in a moment.",
      );
      onToast?.(
        "Meal generation is taking longer than expected. Please try again in a moment.",
      );
    } catch (e) {
      const msg = e.message || `${activeMealLabel} generation failed`;
      setError(msg);
      onToast?.(msg);
    } finally {
      setSavingPlan(false);
      setGenerating(false);
      setGenerationJobMsg("");
    }
  }

  async function buildShoppingList() {
    if (!plan) return;
    setShoppingBusy(true);
    try {
      const result = await mealsApi.generateShoppingList(plan.id);
      onAiUsage?.(result.ai_usage);
      setShoppingDone(true, plan.id);
      onStatsChanged?.();
      if (result?.already_completed) {
        onToast?.(
          "✓ Shopping done — list was already marked complete for this week",
        );
      } else if (result?.already_built) {
        onToast?.("✓ Shopping list is up to date — tap View list to see it");
      } else if (
        pantry.filter((item) => String(item?.name || "").trim()).length === 0
      ) {
        onToast?.(
          "✓ Shopping list ready — buy these first, then add them to Pantry",
        );
      } else {
        onToast?.("✓ Shopping list ready — tap View list to see it");
      }
    } catch (e) {
      setError(e.message);
    }
    setShoppingBusy(false);
  }

  function plannedMealLaneSummary() {
    const plannedTypes = MEAL_TYPES.filter((type) => hasAnyMealType(type));
    if (plannedTypes.length === 0) return "planned meals";
    if (plannedTypes.length === 3) return "breakfast, lunch, and dinner";
    return plannedTypes.map((type) => mealTypeMeta(type).lower).join(" + ");
  }

  async function shiftWeek(direction) {
    try {
      const plans = await mealsApi.list();
      if (!plans || plans.length === 0) return;
      setMealPlanList(plans);
      const currentIdx = plans.findIndex((p) => p.id === plan?.id);
      const targetIdx = currentIdx + direction; // direction: -1=prev, 1=next
      if (targetIdx < 0 || targetIdx >= plans.length) return;
      await showMealPlan(plans[targetIdx]);
    } catch (e) {
      /* silent */
    }
  }

  async function clearAndRegenerate(startFrom) {
    const weekStart =
      startFrom === "next_monday" ? getNextMonday() : localISODate();
    const replaceExisting = planResetIntent === "regenerate";
    setShowClearConfirm(false);
    setClearChoice(null);
    setClearingPlan(true);
    try {
      await generate({ weekStart, replaceExisting });
    } finally {
      setClearingPlan(false);
    }
  }

  async function clearActiveMealPlanOnly() {
    if (!plan?.id) return;
    const clearedMeals = (plan.days || [])
      .filter((day) => (day.meal_type || "dinner") === activeMealType)
      .map((day) => day.meal_name)
      .filter(Boolean);
    setShowClearConfirm(false);
    setClearChoice(null);
    setClearingPlan(true);
    try {
      await mealsApi.remove(plan.id, activeMealType);
      rememberAvoidMeals(clearedMeals);
      const remainingDays = (plan.days || []).filter(
        (day) => (day.meal_type || "dinner") !== activeMealType,
      );
      setPlan(remainingDays.length ? { ...plan, days: remainingDays } : null);
      setSelectedDayKey(null);
      setShoppingDone(false);
      setRecentlyUpdatedDayKey(null);
      onStatsChanged?.();
      onToast?.(`✓ ${activeMealLabel} plan cleared`);
    } catch (e) {
      setError(e.message || `Could not clear ${activeMealPlanLabel}`);
    } finally {
      setClearingPlan(false);
    }
  }

  function showDislikeToast(mealName) {
    if (!mealName) return;
    if (dislikeUndoTimerRef.current)
      window.clearTimeout(dislikeUndoTimerRef.current);
    setDislikeToast({ mealName });
    dislikeUndoTimerRef.current = window.setTimeout(() => {
      setDislikeToast(null);
      dislikeUndoTimerRef.current = null;
    }, 5000);
  }

  async function undoDislike(mealName) {
    if (dislikeUndoTimerRef.current) {
      window.clearTimeout(dislikeUndoTimerRef.current);
      dislikeUndoTimerRef.current = null;
    }
    setDislikeToast(null);
    try {
      const current = prefs?.dislikes || [];
      const updatedDislikes = current.filter(
        (item) =>
          String(item).trim().toLowerCase() !==
          String(mealName).trim().toLowerCase(),
      );
      const updatedPrefs = await mealsApi.savePreferences({
        ...(prefs || {}),
        dislikes: updatedDislikes,
      });
      setPrefs?.(updatedPrefs);
      onToast?.("↩ Removed from dislikes");
    } catch {
      onToast?.("Could not undo dislike");
    }
  }

  async function replaceDay(day, saveDislike = false, reopenSheet = true) {
    if (!plan || !day) return;
    const mealBeingReplaced = day.meal_name;
    const dayMealType = day.meal_type || activeMealType || "dinner";
    const dayMealLower = mealTypeMeta(dayMealType).lower;
    setReplacingDay(true);
    setError("");
    try {
      const minutes =
        day.prep_time_minutes ||
        Number(String(day.cook_time || "").match(/\d+/)?.[0]) ||
        45;
      const allScheduledMeals = (plan.days || [])
        .map((d) => d.meal_name)
        .filter(Boolean);
      const result = await mealsApi.regenerateDay(plan.id, day.day_index, {
        meal_type: dayMealType,
        avoid: allScheduledMeals,
        save_dislike: saveDislike,
        time_available: minutes,
      });
      onAiUsage?.(result.ai_usage);
      setPlan(result.plan);
      setShoppingDone(false);
      setRecentlyUpdatedDayKey(mealDayKey(day));
      if (reopenSheet) setSelectedDayKey(mealDayKey(day));
      if (saveDislike) {
        const dislikedMeal = result.disliked_meal || mealBeingReplaced;
        const nextDislikes = [...(prefs?.dislikes || [])];
        if (
          dislikedMeal &&
          !nextDislikes.some(
            (item) =>
              String(item).trim().toLowerCase() ===
              String(dislikedMeal).trim().toLowerCase(),
          )
        ) {
          nextDislikes.push(dislikedMeal);
          setPrefs?.({
            ...(prefs || {}),
            dislikes: nextDislikes,
            has_preferences: true,
          });
        }
        showDislikeToast(dislikedMeal);
        onToast?.(`✓ Saved dislike and swapped ${dayMealLower}`);
      } else {
        onToast?.(
          `✓ ${mealTypeMeta(dayMealType).label} replaced for this week`,
        );
      }
    } catch (e) {
      setError(e.message || `Could not replace that ${dayMealLower}`);
    }
    setReplacingDay(false);
  }

  async function saveManualMeal() {
    const mealName = manualMealName.trim();
    if (!plan || !swapSheetDay || !mealName) return;
    const dayMealType = swapSheetDay.meal_type || activeMealType || "dinner";
    const dayMealLower = mealTypeMeta(dayMealType).lower;
    setReplacingDay(true);
    setError("");
    try {
      const updated = await mealsApi.update(plan.id, {
        days: [
          {
            day_index: swapSheetDay.day_index,
            meal_type: dayMealType,
            meal_name: mealName,
            recipe_id: null,
            cook_time: null,
            prep_time_minutes: null,
            uses_expiring_items: false,
            notes: "",
            source: "manual",
          },
        ],
      });
      setPlan(updated);
      setShoppingDone(false);
      setRecentlyUpdatedDayKey(mealDayKey(swapSheetDay));
      setSelectedDayKey(mealDayKey(swapSheetDay));
      setSwapSheetDay(null);
      setManualMealName("");
      setManualEntryOpen(false);
      onToast?.(`✓ Manual ${dayMealLower} added`);
    } catch (e) {
      setError(e.message || `Could not save that ${dayMealLower}`);
    }
    setReplacingDay(false);
  }

  async function applySavedMealToDay(recipe) {
    if (!plan || !swapSheetDay || !recipe?.id) return;
    const dayMealType = swapSheetDay.meal_type || activeMealType || "dinner";
    const dayMealLower = mealTypeMeta(dayMealType).lower;
    setReplacingDay(true);
    setError("");
    try {
      const updated = await mealsApi.update(plan.id, {
        days: [
          {
            day_index: swapSheetDay.day_index,
            meal_type: dayMealType,
            meal_name: recipe.title,
            recipe_id: null,
            cook_time: recipe.prep_time
              ? `${recipe.prep_time} min`
              : recipe.cook_time || null,
            prep_time_minutes: recipe.prep_time || null,
            recipe_ingredients:
              recipe.ingredients || recipe.uses_from_pantry || [],
            recipe_steps: recipe.instructions || [],
            recipe_description: recipe.description || "",
            cuisine_tag: recipe.cuisine || "",
            cook_time_minutes: recipe.cook_time || recipe.prep_time || null,
            difficulty: recipe.difficulty || "easy",
            uses_expiring_items: false,
            notes: recipe.description || "",
            source: "saved",
          },
        ],
      });
      await mealsApi.removeRecipe(recipe.id);
      setPlan(updated);
      setSavedMealOptions((prev) =>
        prev.filter((item) => item.id !== recipe.id),
      );
      setShoppingDone(false);
      setRecentlyUpdatedDayKey(mealDayKey(swapSheetDay));
      setSelectedDayKey(mealDayKey(swapSheetDay));
      setSwapSheetDay(null);
      setManualMealName("");
      setManualEntryOpen(false);
      onToast?.(`✓ Saved recipe used for ${dayMealLower}`);
    } catch (e) {
      setError(
        e.message || `Could not use that saved recipe for ${dayMealLower}`,
      );
    }
    setReplacingDay(false);
  }

  async function confirmDislikeChoice() {
    if (!dislikeSheetDay || !dislikeChoice) return;
    const day = dislikeSheetDay;
    const permanent = dislikeChoice === "forever";
    closeDislikeSheet();
    closeSelectedMeal();
    await replaceDay(day, permanent, false);
  }

  async function saveSelectedRecipe() {
    if (!selectedDay) return null;
    if (selectedRecipeSaved) return selectedRecipe;
    setSavingSheetRecipe(true);
    setMealActionNotice({
      title: "Saving recipe",
      message: `Saving this ${selectedMealLower} to Recipes...`,
    });
    setError("");
    try {
      const parts = mealNameParts(selectedDay.meal_name);
      const recipePayload = selectedRecipe || {};
      const scheduleImageUrl = validMealImageUrl(
        selectedMealImage || mealPlanImageUrl(selectedDay),
      );
      const saved = await mealsApi.saveRecipe({
        title: parts.title || recipePayload.title || selectedDay.meal_name,
        prep_time:
          recipePayload.prep_time ||
          selectedDay.prep_time_minutes ||
          Number(String(selectedDay.cook_time || "").match(/\d+/)?.[0]) ||
          null,
        difficulty: recipePayload.difficulty || "easy",
        ingredients: selectedIngredients,
        instructions: recipeStepsForDay(selectedDay, selectedRecipe),
        image_url: scheduleImageUrl,
        source: "meal_plan",
        meal_type: selectedDay.meal_type || activeMealType || "dinner",
        explicit_save: true,
      });
      setPlan((current) =>
        current
          ? {
              ...current,
              days: (current.days || []).map((day) =>
                day.day_index === selectedDay.day_index &&
                (day.meal_type || "dinner") ===
                  (selectedDay.meal_type || activeMealType || "dinner")
                  ? {
                      ...day,
                      recipe_id: saved.id,
                      recipe: {
                        ...(day.recipe || recipePayload),
                        id: saved.id,
                        source: saved.source || "meal_plan",
                        title:
                          saved.title || parts.title || selectedDay.meal_name,
                        ingredients: saved.ingredients || selectedIngredients,
                        instructions:
                          saved.instructions ||
                          recipeStepsForDay(selectedDay, selectedRecipe),
                        image_url: saved.image_url || scheduleImageUrl,
                      },
                    }
                  : day,
              ),
            }
          : current,
      );
      setSavedRecipeDayKeys((prev) =>
        new Set(prev).add(
          `${selectedDay.id || `${selectedDay.meal_type || activeMealType}:${selectedDay.day_index}`}:${selectedDay.meal_name}`,
        ),
      );
      const message =
        selectedIngredients.length || selectedSteps.length
          ? "Recipe saved to Recipes"
          : "Meal idea saved to Recipes";
      setMealActionNotice({ title: "Saved", message });
      onToast?.(`✓ ${message}`);
      await pause(950);
      return saved;
    } catch (e) {
      setError(e.message || "Could not save this recipe.");
      return null;
    } finally {
      setSavingSheetRecipe(false);
      setMealActionNotice(null);
    }
  }

  async function postSelectedMealToFamily({ closeSheet = false } = {}) {
    const mealName = selectedMealParts.title || selectedDay?.meal_name;
    if (!mealName) return;
    const actionKey = mealScheduleActionKey(selectedDay);
    setMealActionNotice({
      title: "Posting",
      message: `Posting this ${selectedMealLower} to Family activity...`,
    });
    setError("");
    try {
      await onSetCookingTonight?.(
        mealName,
        selectedDay?.meal_type || activeMealType || "dinner",
      );
      rememberMealAction(
        postedCookingStorageKey,
        setPostedCookingDayKeys,
        actionKey,
      );
      if (closeSheet) closeSelectedMeal();
      onToast?.(`✓ ${selectedMealMeta.label} posted to Family activity`);
    } catch (e) {
      setError(e.message || "Could not post to Family activity");
      onToast?.(e.message || "Could not post to Family activity", "error");
    } finally {
      setMealActionNotice(null);
    }
  }

  async function shareSelectedMealRecipe({ closeSheet = false } = {}) {
    if (!canShare || !selectedDay) return;
    setError("");
    let recipeToShare = selectedRecipeSaved ? selectedRecipe : null;
    if (!recipeToShare?.id) {
      recipeToShare = await saveSelectedRecipe();
    }
    if (!recipeToShare?.id) return;
    if (closeSheet) closeSelectedMeal();
    onShare?.(
      "recipe",
      recipeToShare.id,
      recipeToShare.title || selectedMealParts.title || selectedDay.meal_name,
    );
    onToast?.("Share sheet opened");
  }

  const needsPreferences = !prefs?.has_preferences;
  const selectedDay =
    selectedDayKey === null
      ? null
      : (plan?.days || []).find(
          (day) =>
            mealDayKey(day) === selectedDayKey &&
            (day.meal_type || "dinner") === activeMealType,
        );
  const namedPantryCount = pantry.filter((item) =>
    String(item?.name || "").trim(),
  ).length;
  const pantryIsEmpty = namedPantryCount === 0;
  const selectedRecipe = selectedDay?.recipe || null;
  const selectedMealMeta = mealTypeMeta(
    selectedDay?.meal_type || activeMealType,
  );
  const selectedMealLower = selectedMealMeta.lower;
  const swapSheetMealMeta = mealTypeMeta(
    swapSheetDay?.meal_type || activeMealType,
  );
  const swapSheetMealLower = swapSheetMealMeta.lower;
  const dislikeSheetMealMeta = mealTypeMeta(
    dislikeSheetDay?.meal_type || activeMealType,
  );
  const dislikeSheetMealLower = dislikeSheetMealMeta.lower;
  const todayIso = localISODate();
  const selectedIngredients = mealPlanDayIngredients(
    selectedDay,
    selectedRecipe,
  );
  const selectedPantryTruth = splitIngredientsByPantry(
    selectedIngredients,
    pantry,
  );
  const selectedPantryIngredients = selectedPantryTruth.available;
  const selectedMissingIngredients =
    liveMealShortage &&
    selectedDay?.id &&
    liveMealShortage.meal_day_id === selectedDay.id
      ? liveMealShortage.missing_ingredients || []
      : [];
  const ingredientIsInShopping = (ingredient) => (
    (liveMealShortage?.shopping_ingredients || []).some(name =>
      foodNamesMatch(name, ingredient),
    ) ||
    shoppingAddedIngredientKeys.has(
      `${selectedDay?.id}:${String(ingredient).trim().toLowerCase()}`,
    ) || shoppingIngredientNames.some(name => foodNamesMatch(name, ingredient))
  );

  useEffect(() => {
    let cancelled = false;
    if (!selectedDay?.id) {
      setLiveMealShortage(null);
      return undefined;
    }
    setLiveMealShortage({ meal_day_id: selectedDay.id, loading: true });
    dashboardApi
      .mealShortageState(selectedDay.id)
      .then((state) => {
        if (!cancelled) setLiveMealShortage(state);
      })
      .catch(() => {
        if (!cancelled) {
          setLiveMealShortage({
            meal_day_id: selectedDay.id,
            missing_ingredients: [],
            shopping_ingredients: [],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDay?.id, pantry]);
  const selectedMealParts = mealNameParts(selectedDay?.meal_name);
  const selectedSteps = recipeStepsForDay(selectedDay, selectedRecipe);
  const selectedDate = selectedDay
    ? mealPlanDate(plan, selectedDay.day_index)
    : null;
  const selectedDateIso = selectedDate ? localISODate(selectedDate) : "";
  const selectedIsToday = selectedDateIso === todayIso;
  const selectedIsPast = Boolean(selectedDateIso && selectedDateIso < todayIso);
  const selectedMealDisplay = selectedDay
    ? mealPlanDisplay(selectedDay, selectedMealParts)
    : null;
  const selectedMealTags = selectedDay
    ? mealPlanTags(selectedDay, selectedMealDisplay?.tags || [])
    : [];
  const selectedMealImage =
    (selectedDay ? mealImages.get(mealImageKey(selectedDay)) : null) ||
    selectedMealDisplay?.imageUrl ||
    "";
  const selectedMealSummary =
    selectedRecipe?.description ||
    selectedMealDisplay?.sub ||
    selectedDay?.notes ||
    "";
  const selectedPrepMinutes =
    selectedDay?.prep_time_minutes ||
    Number(String(selectedDay?.cook_time || "").match(/\d+/)?.[0]) ||
    null;
  const planRange = formatMealPlanRange(plan);
  const planStartIso = mealPlanWeekStart(plan);
  const planIsCurrentWeek = mealPlanCoversIso(plan, todayIso);

  async function addSelectedIngredientToShopping() {
    const ingredient = String(shoppingIngredientTarget?.ingredient || "").trim();
    const mealDayId = shoppingIngredientTarget?.mealDayId;
    if (!mealDayId || !ingredient) return;
    setShoppingIngredientBusy(true);
    try {
      const result = await dashboardApi.addMealShortageToShopping(
        mealDayId,
        [ingredient],
      );
      setLiveMealShortage(result);
      const key = `${mealDayId}:${ingredient.toLowerCase()}`;
      setShoppingAddedIngredientKeys(current => new Set([...current, key]));
      setShoppingIngredientNames(current => [...current, ingredient]);
      setShoppingIngredientTarget(null);
      onToast?.(
        result?.created_count
          ? `${ingredient} added to Shopping`
          : `${ingredient} is already in Shopping`,
      );
      onStatsChanged?.();
      window.dispatchEvent(new Event("hm_notifications_refresh"));
    } catch (requestError) {
      if (requestError?.status === 409) {
        const refreshedState = requestError?.data || null;
        if (refreshedState) setLiveMealShortage(refreshedState);
        setShoppingIngredientTarget(null);
        const remainsMissing = (refreshedState?.missing_ingredients || []).some(name =>
          foodNamesMatch(name, ingredient),
        );
        onToast?.(
          remainsMissing
            ? `${ingredient} is still needed. Please try adding it again.`
            : `${ingredient} is now available in Pantry.`,
          remainsMissing ? "error" : "info",
        );
      } else {
        onToast?.(
          requestError?.message || "HouseMind could not add that ingredient.",
          "error",
        );
      }
    } finally {
      setShoppingIngredientBusy(false);
    }
  }
  const planHeadingPrefix =
    planStartIso > todayIso
      ? "Next week"
      : planIsCurrentWeek
        ? "This week"
        : "Meal plan";
  const planHeading = planRange
    ? `${planHeadingPrefix} · ${planRange}`
    : planHeadingPrefix;
  const activeTodayPlan = mealPlanList.find((item) =>
    mealPlanCoversIso(item, todayIso),
  );
  const futurePreparedPlan =
    mealPlanList
      .filter((item) => mealPlanWeekStart(item) > todayIso)
      .sort((a, b) =>
        mealPlanWeekStart(a).localeCompare(mealPlanWeekStart(b)),
      )[0] || null;
  const contextPlanTarget =
    activeTodayPlan && plan?.id !== activeTodayPlan.id
      ? activeTodayPlan
      : futurePreparedPlan && plan?.id !== futurePreparedPlan.id
        ? futurePreparedPlan
        : null;
  const contextPlanLabel = contextPlanTarget
    ? contextPlanTarget.id === activeTodayPlan?.id
      ? "Today"
      : "Next week ready"
    : "";
  const planStatusLabel = {
    draft: "",
    active: "",
    complete: "Complete",
    archived: "Archived",
  };
  const statusDisplay = planStatusLabel[plan?.status] ?? plan?.status;
  const freshTodayRange = formatDateRangeFromIso(todayIso);
  const freshNextMondayRange = formatDateRangeFromIso(getNextMonday());
  // Filter saved plan days by the active meal-type lane
  const planDays = (plan?.days || []).filter(
    (d) => (d.meal_type || "dinner") === activeMealType,
  );
  const hasAnyMealType = (type) =>
    (plan?.days || []).some((d) => (d.meal_type || "dinner") === type);
  const emptyLaneCopy =
    planStartIso > todayIso && activeTodayPlan
      ? `No ${activeMealLower} planned for ${planRange || "this week"} yet. Your current week is unchanged.`
      : `Generate one without changing other meals.`;
  const pastPlanDays = planDays.filter((day) => {
    const date = mealPlanDate(plan, day.day_index);
    const dateIso = date ? localISODate(date) : "";
    return dateIso && dateIso < todayIso;
  });
  const visiblePlanDays = planDays.filter((day) => {
    const date = mealPlanDate(plan, day.day_index);
    const dateIso = date ? localISODate(date) : "";
    return !dateIso || dateIso >= todayIso;
  });
  const passedMealCount = pastPlanDays.filter((day) =>
    Boolean(
      day.meal_name || mealPlanDisplay(day, mealNameParts(day.meal_name)).title,
    ),
  ).length;
  const passedRangeLabel = pastPlanDays.length
    ? `${mealPlanDayLabel(plan, pastPlanDays[0]).slice(0, 3)}${pastPlanDays.length > 1 ? `–${mealPlanDayLabel(plan, pastPlanDays[pastPlanDays.length - 1]).slice(0, 3)}` : ""}`
    : "";
  const unconfirmedPastMeals = pastPlanDays.filter((day) => {
    if (!day?.meal_name) return false;
    const actionKey = mealScheduleActionKey(day);
    return (
      actionKey &&
      !usedPantryDayKeys.has(actionKey) &&
      !skippedMealDayKeys.has(actionKey)
    );
  });
  const allPlanDaysPassed = planDays.length > 0 && visiblePlanDays.length === 0;
  const showPassedMeals = Boolean(showPassedMealsByType[activeMealType]);
  const renderedPlanDays = [
    ...(showPassedMeals ? pastPlanDays : []),
    ...visiblePlanDays,
  ];
  const renderedMealImageUrls = new Set();
  const closePlanResetSheet = () => {
    if (clearingPlan || generating) return;
    setShowClearConfirm(false);
    setClearChoice(null);
  };
  const selectedRecipeDayKey = selectedDay
    ? `${selectedDay.id || `${selectedDay.meal_type || activeMealType}:${selectedDay.day_index}`}:${selectedDay.meal_name}`
    : "";
  const selectedRecipeSaved = Boolean(
    savedRecipeDayKeys.has(selectedRecipeDayKey) ||
    (selectedRecipe?.id &&
      !["ai", "meal_plan_generated"].includes(
        String(selectedRecipe?.source || "").toLowerCase(),
      )),
  );
  const swapMealType = String(
    swapSheetDay?.meal_type || activeMealType || "dinner",
  ).toLowerCase();
  const swapSavedMealOptions = savedMealOptions.filter((recipe) => {
    const recipeMealType = String(recipe?.meal_type || "dinner").toLowerCase();
    return recipeMealType === swapMealType;
  });
  const selectedRecipeHasDetails =
    selectedIngredients.length > 0 || selectedSteps.length > 0;
  const selectedMealActionKey = selectedDay
    ? mealScheduleActionKey(selectedDay)
    : "";
  const selectedPantryUsed = Boolean(
    selectedMealActionKey && usedPantryDayKeys.has(selectedMealActionKey),
  );
  const selectedMealSkipped = Boolean(
    selectedMealActionKey && skippedMealDayKeys.has(selectedMealActionKey),
  );
  const selectedCookingPosted = Boolean(
    selectedMealActionKey && postedCookingDayKeys.has(selectedMealActionKey),
  );
  const selectedHasNoIngredients = selectedIngredients.length === 0;
  const selectedIsManual = selectedDay?.source === "manual";
  const selectedIsManualNoIngredients =
    selectedHasNoIngredients && selectedIsManual;
  const selectedBlockPantryButton = selectedIsManualNoIngredients;
  const selectedNeedsPastCookConfirm =
    selectedIsPast && !selectedPantryUsed && !selectedMealSkipped;
  const selectedCanConfirmCooked =
    !selectedIsPast || selectedNeedsPastCookConfirm;
  const selectedPantryBlockCopy = selectedIsManualNoIngredients
    ? "Manual entry — no pantry ingredients tracked for this meal."
    : selectedHasNoIngredients
      ? "No recipe ingredients are tracked for this meal yet."
      : "No pantry items matched this recipe. Add groceries before marking pantry used.";
  const selectedRecipeSaveLabel = savingSheetRecipe
    ? "Saving recipe..."
    : selectedRecipeSaved
      ? selectedRecipeHasDetails
        ? "Recipe saved"
        : `${selectedMealMeta.label} idea saved`
      : selectedRecipeHasDetails
        ? "Save recipe to Recipes"
        : `Save ${selectedMealLower} idea`;
  const planResetSheet = showClearConfirm ? (
    <FoodMobileSheet
      title={
        planResetIntent === "regenerate"
          ? `Replace ${activeMealPlural}`
          : planResetIntent === "generate"
            ? `Generate ${activeMealPlural}`
            : `Clear ${activeMealPlural}`
      }
      label={
        planResetIntent === "regenerate"
          ? `Replace ${activeMealPlanLabel}`
          : planResetIntent === "generate"
            ? `Generate ${activeMealPlanLabel}`
            : `Clear ${activeMealPlanLabel}`
      }
      onClose={closePlanResetSheet}
      disableBackdropClose
      dragHandleOnly
    >
      <div className="fd-clear-sheet-content">
        <div className="fd-clear-sheet__head">
          <div className="fd-clear-sheet__warn">
            ⚠ This will{" "}
            {planResetIntent === "regenerate"
              ? `replace this week's ${activeMealPlural} only`
              : planResetIntent === "generate"
                ? `create a new 7-day ${activeMealLower} schedule`
                : `clear this week's ${activeMealPlural} only`}
          </div>
          <div className="fd-clear-sheet__sub">
            {planResetIntent === "regenerate"
              ? `Other meal lanes stay untouched. When should the new 7-day ${activeMealLower} schedule start?`
              : planResetIntent === "generate"
                ? `Choose when the new 7-day ${activeMealLower} schedule should start.`
                : `Breakfast, lunch, and dinner are separate. Other meal lanes stay untouched for ${planRange || "this week"}.`}
          </div>
        </div>
        {planResetIntent !== "clear" && (
          <div className="fd-clear-sheet__body">
            <button
              type="button"
              className={`fd-clear-opt${clearChoice === "today" ? " fd-clear-opt--on" : ""}`}
              onClick={() => setClearChoice("today")}
              disabled={clearingPlan || generating}
            >
              <div className="fd-clear-opt__ico">📅</div>
              <div className="fd-clear-opt__info">
                <div className="fd-clear-opt__label">Start from today</div>
                <div className="fd-clear-opt__range">{freshTodayRange}</div>
                <div className="fd-clear-opt__hint">
                  Mid-week reset for {activeMealPlural}
                </div>
              </div>
              <div className="fd-clear-opt__radio" />
            </button>
            <button
              type="button"
              className={`fd-clear-opt${clearChoice === "next_monday" ? " fd-clear-opt--on" : ""}`}
              onClick={() => setClearChoice("next_monday")}
              disabled={clearingPlan || generating}
            >
              <div className="fd-clear-opt__ico">🗓</div>
              <div className="fd-clear-opt__info">
                <div className="fd-clear-opt__label">Start next Monday</div>
                <div className="fd-clear-opt__range">{freshNextMondayRange}</div>
                <div className="fd-clear-opt__hint">
                  Plan a full fresh {activeMealLower} week
                </div>
              </div>
              <div className="fd-clear-opt__radio" />
            </button>
          </div>
        )}
        <div className="fd-clear-sheet__foot">
          <button
            className="fd-clear-cancel"
            type="button"
            onClick={closePlanResetSheet}
            disabled={clearingPlan || generating}
          >
            Cancel
          </button>
          <button
            className="hm-btn hm-btn--primary fd-clear-confirm"
            type="button"
            disabled={
              (planResetIntent !== "clear" && !clearChoice) ||
              clearingPlan ||
              generating
            }
            onClick={() =>
              planResetIntent !== "clear"
                ? clearChoice && clearAndRegenerate(clearChoice)
                : clearActiveMealPlanOnly()
            }
          >
            {planResetIntent !== "clear"
              ? clearingPlan || generating
                ? `Generating ${activeMealPlural}…`
                : planResetIntent === "regenerate"
                  ? `Generate new ${activeMealPlural} ↻`
                  : `Generate ${activeMealPlural} →`
              : clearingPlan
                ? "Clearing…"
                : `Clear ${activeMealPlural}`}
          </button>
        </div>
      </div>
    </FoodMobileSheet>
  ) : null;
  if (loading) return <div className="fd-empty">Loading plan…</div>;

  return (
    <div className="fd-tab-content">
      {showMealPlanWorking && (
        <SavingNotice
          title={
            generating
              ? `Generating ${activeMealPlanLabel}`
              : savingPlan
                ? `Saving ${activeMealPlanLabel}`
                : shoppingBusy
                  ? "Building shopping list"
                  : replacingDay
                    ? `Replacing ${activeMealLower}`
                    : clearingPlan
                      ? `Clearing ${activeMealPlural}`
                      : "Finding meal ideas"
          }
          message={
            generating
              ? generationJobMsg ||
                (pantryIsEmpty
                  ? `HouseMind is planning 7 ${activeMealPlural}. Your shopping list will cover the groceries first.`
                  : `HouseMind is planning 7 ${activeMealPlural} from your pantry...`)
              : savingPlan
                ? `Saving your ${activeMealPlanLabel}...`
                : shoppingBusy
                  ? pantryIsEmpty
                    ? "HouseMind is building the grocery list this plan depends on..."
                    : "HouseMind is building your shopping list..."
                  : replacingDay
                    ? `HouseMind is finding another ${activeMealLower} option for that day...`
                    : clearingPlan
                      ? `Removing this week's ${activeMealPlural}...`
                      : "HouseMind is looking at expiring food..."
          }
        />
      )}
      {mealActionNotice && (
        <SavingNotice
          title={mealActionNotice.title}
          message={mealActionNotice.message}
        />
      )}
      {dislikeToast && (
        <div className="fd-dislike-toast" role="status" aria-live="polite">
          <span className="fd-dislike-toast__ico" aria-hidden="true">
            👎
          </span>
          <span className="fd-dislike-toast__msg">
            <strong>{dislikeToast.mealName}</strong>
            added to dislikes
          </span>
          <button
            className="fd-dislike-toast__undo"
            type="button"
            onClick={() => undoDislike(dislikeToast.mealName)}
          >
            Undo
          </button>
        </div>
      )}
      {unconfirmedPastMeals.length > 0 && (
        <div className="fd-cooked-reminder">
          <span className="fd-cooked-reminder__icon" aria-hidden="true">
            ✓
          </span>
          <div className="fd-cooked-reminder__copy">
            <strong>
              {unconfirmedPastMeals.length} past{" "}
              {unconfirmedPastMeals.length === 1
                ? activeMealLower
                : activeMealPlural}{" "}
              need pantry update
            </strong>
            <p>
              Mark cooked to update Pantry, or skip if you ate something else.
            </p>
          </div>
          <div className="fd-cooked-reminder__actions">
            <button
              type="button"
              className="hm-btn hm-btn--primary"
              onClick={() =>
                setSelectedDayKey(mealDayKey(unconfirmedPastMeals[0]))
              }
            >
              Review
            </button>
            <button
              type="button"
              className="hm-btn hm-btn--ghost"
              onClick={() => {
                unconfirmedPastMeals.forEach((day) =>
                  markMealSkipped(day, { silent: true }),
                );
                onToast?.(
                  `${unconfirmedPastMeals.length} ${activeMealPlural} marked skipped`,
                );
              }}
            >
              Skip all
            </button>
          </div>
        </div>
      )}

      {error && <div className="fd-error">{error}</div>}

      {/* Preferences nudge — shown when prefs are missing or incomplete */}
      {needsPreferences && (
        <div className="fd-prefs-nudge">
          <span className="fd-prefs-nudge__icon">⚙️</span>
          <div>
            <strong>Set your preferences first for better results</strong>
            <p>
              Add allergies, dietary type, and favourite cuisines before
              generating your first meal plan.
            </p>
          </div>
          <button
            className="hm-btn hm-btn--ghost fd-prefs-nudge__btn"
            onClick={() => {
              // bubble up to FoodPage tab switcher via custom event
              window.dispatchEvent(
                new CustomEvent("hm_food_tab", { detail: 4 }),
              );
            }}
          >
            Set up →
          </button>
        </div>
      )}

      {/* ── Meal-type lane selector ── */}
      <div className="fd-meal-lane-tabs" role="tablist" aria-label="Meal type">
        {MEAL_TYPES.map((type) => {
          const meta = mealTypeMeta(type);
          return (
            <button
              type="button"
              key={type}
              role="tab"
              aria-selected={activeMealType === type}
              data-active={activeMealType === type ? "true" : "false"}
              className={`fd-meal-lane-tab${activeMealType === type ? " fd-meal-lane-tab--active" : ""}${hasAnyMealType(type) ? " fd-meal-lane-tab--has-plan" : ""}`}
              onClick={() => changeActiveMealType(type)}
              title={
                hasAnyMealType(type)
                  ? `${meta.label} plan exists for this week`
                  : `No ${meta.lower} plan yet`
              }
            >
              <span className="fd-meal-lane-tab__icon">{meta.icon}</span>
              <span className="fd-meal-lane-tab__label">{meta.label}</span>
              {hasAnyMealType(type) && (
                <span className="fd-meal-lane-tab__dot" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {!plan ? (
        /* ── No plan at all yet ── */
        <div className="fd-empty-plan">
          <div className="fd-empty-plan__icon">📅</div>
          <h3>No meal plan for this week yet</h3>
          <p>
            {pantryIsEmpty
              ? `Generate a 7-day ${activeMealPlanLabel} now. HouseMind will build the shopping list first, then you can add bought items to Pantry.`
              : `Generate a 7-day ${activeMealPlanLabel} from your pantry to get started.`}
          </p>
          <button
            className="hm-btn hm-btn--primary fd-gen-btn"
            onClick={openPlanGeneration}
            disabled={generating || clearingPlan}
          >
            {generating
              ? `✨ Generating ${activeMealLower}…`
              : `✨ Generate ${activeMealPlanLabel}`}
          </button>
          {planResetSheet}
        </div>
      ) : (
        <>
          {/* ── Plan header + action buttons — always visible when plan exists ── */}
          {plan && (
            <div className="fd-plan-header">
              <div className="fd-plan-summary">
                <div className="fd-plan-summary-main">
                  <h3 className="fd-plan-title">{planHeading}</h3>
                  <p className="fd-plan-sub">
                    {activeMealShort} {planDays.length}/7
                    {hasAnyMealType(activeMealType) ? "" : " not planned"}
                    {statusDisplay ? ` · ${statusDisplay}` : ""}
                  </p>
                </div>
                {contextPlanTarget && (
                  <button
                    className="fd-plan-context-chip"
                    type="button"
                    onClick={() => showMealPlan(contextPlanTarget)}
                  >
                    {contextPlanLabel}
                  </button>
                )}
              </div>
              <div className="fd-week-nav">
                <button
                  className="fd-week-nav-btn"
                  onClick={() => shiftWeek(1)}
                  aria-label="Older week"
                >
                  ‹
                </button>
                <button
                  className="fd-week-nav-btn"
                  onClick={() => shiftWeek(-1)}
                  aria-label="Newer week"
                >
                  ›
                </button>
              </div>
              <div
                className={`fd-plan-actions${shoppingDone ? " fd-plan-actions--ready" : ""}`}
              >
                {pantryIsEmpty && (
                  <div className="fd-plan-shopping-note">
                    <strong>Shopping-first plan</strong>
                    <span>
                      Pantry is empty, so these meals depend on the grocery
                      list. After shopping, add bought items to Pantry.
                    </span>
                  </div>
                )}
                <div className="fd-plan-primary-row">
                  {shoppingDone && (
                    <button
                      className="fd-plan-btn-sec fd-plan-btn-sec--rebuild"
                      onClick={buildShoppingList}
                      disabled={shoppingBusy || generating || clearingPlan}
                    >
                      {shoppingBusy ? "…" : "↻ Rebuild"}
                    </button>
                  )}
                  <button
                    className="hm-btn hm-btn--primary fd-plan-btn-primary"
                    onClick={() =>
                      shoppingDone
                        ? window.dispatchEvent(
                            new CustomEvent("hm_food_tab", { detail: 3 }),
                          )
                        : buildShoppingList()
                    }
                    disabled={shoppingBusy || generating || clearingPlan}
                    title={`Build one grocery list for ${plannedMealLaneSummary()}`}
                  >
                    {shoppingDone
                      ? "✓ View list →"
                      : shoppingBusy
                        ? "Building…"
                        : "🛒 Build list"}
                  </button>
                </div>
                <div className="fd-plan-btn-row">
                  <button
                    className="fd-plan-btn-sec"
                    onClick={openPlanGeneration}
                    disabled={generating || clearingPlan}
                  >
                    {generating
                      ? "…"
                      : hasAnyMealType(activeMealType)
                        ? "↻ Regenerate"
                        : "✨ Generate"}
                  </button>
                  {canShare && (
                    <button
                      className="fd-plan-btn-sec"
                      title="Share meal plan"
                      aria-label="Share meal plan"
                      onClick={() =>
                        onShare?.(
                          "meal_plan",
                          plan.id,
                          `Meal plan from ${plan.week_start}`,
                        )
                      }
                      disabled={sharedResourceKeys?.has(`meal_plan:${plan.id}`)}
                    >
                      {sharedResourceKeys?.has(`meal_plan:${plan.id}`)
                        ? "✓ Shared"
                        : "↗ Share"}
                    </button>
                  )}
                  <button
                    className="fd-plan-btn-sec fd-plan-btn-sec--danger"
                    title={`Clear ${activeMealPlanLabel}`}
                    aria-label={`Clear ${activeMealPlanLabel}`}
                    onClick={() => openPlanReset("clear")}
                    disabled={clearingPlan}
                  >
                    🗑 Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {planResetSheet}

          {/* Inline empty state when plan exists but this tab has no meals yet */}
          {plan && planDays.length === 0 && (
            <div className="fd-empty-lane">
              <span className="fd-empty-lane__icon">{activeMealMeta.icon}</span>
              <div className="fd-empty-lane__copy">
                <strong>No {activeMealPlanLabel} yet</strong>
                <p>{emptyLaneCopy}</p>
                <button
                  type="button"
                  className="hm-btn hm-btn--primary fd-empty-lane__btn"
                  onClick={openPlanGeneration}
                  disabled={generating || clearingPlan}
                >
                  {generating
                    ? `✨ Generating ${activeMealLower}…`
                    : `✨ Generate ${activeMealPlanLabel}`}
                </button>
              </div>
            </div>
          )}

          <div className="fd-week-grid">
            {pastPlanDays.length > 0 && (
              <button
                type="button"
                className={`fd-passed-days-row${allPlanDaysPassed ? " fd-passed-days-row--complete" : ""}${showPassedMeals ? " is-open" : ""}`}
                onClick={() => togglePassedMealsForType(activeMealType)}
                aria-expanded={showPassedMeals}
              >
                <span aria-hidden="true">✓</span>
                <strong>
                  {allPlanDaysPassed
                    ? `${activeMealLabel} week complete`
                    : `${passedRangeLabel} · ${passedMealCount} ${passedMealCount === 1 ? activeMealLower : activeMealPlural} done`}
                </strong>
                {allPlanDaysPassed ? (
                  <em
                    onClick={(event) => {
                      event.stopPropagation();
                      openPlanReset("regenerate");
                    }}
                  >
                    Regenerate {activeMealPlural}
                  </em>
                ) : (
                  <small>{showPassedMeals ? "Hide" : "View"}</small>
                )}
              </button>
            )}
            {renderedPlanDays.map((day, i) =>
              (() => {
                const date = mealPlanDate(plan, day.day_index);
                const dateIso = date ? localISODate(date) : "";
                const isPast = dateIso && dateIso < todayIso;
                const isToday = dateIso === todayIso;
                const parts = mealNameParts(day.meal_name);
                const dayKey = mealDayKey(day);
                const isUpdated = recentlyUpdatedDayKey === dayKey;
                const display = mealPlanDisplay(
                  day,
                  parts,
                  renderedMealImageUrls,
                );
                const hasMeal = Boolean(display.title || day.meal_name);
                const dayMealMeta = mealTypeMeta(
                  day.meal_type || activeMealType,
                );
                // Prefer the meal-name lookup image; do not fall back to
                // generic keyword photos because they mismatch too easily.
                const imageUrl =
                  mealImages.get(mealImageKey(day)) || display.imageUrl;
                const tags = mealPlanTags(day, display.tags);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`fd-day-card fd-day-card--button${day.uses_expiring_items ? " fd-day-card--expiring" : ""}${selectedDayKey === dayKey ? " fd-day-card--selected" : ""}${isPast ? " fd-day-card--past" : ""}${isToday ? " fd-day-card--today" : ""}${isUpdated ? " fd-day-card--updated" : ""}${!hasMeal ? " fd-day-card--empty" : ""}`}
                    onClick={() => setSelectedDayKey(dayKey)}
                    onTouchStart={(e) => {
                      const t = e.touches[0];
                      swipeTouchRef.current = {
                        startX: t.clientX,
                        startY: t.clientY,
                        day,
                      };
                    }}
                    onTouchEnd={(e) => {
                      if (!swipeTouchRef.current) return;
                      const {
                        startX,
                        startY,
                        day: swipeDay,
                      } = swipeTouchRef.current;
                      swipeTouchRef.current = null;
                      const t = e.changedTouches[0];
                      const dx = t.clientX - startX;
                      const dy = t.clientY - startY;
                      if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx))
                        return; // not a horizontal swipe
                      e.preventDefault();
                      if (dx < 0) {
                        // swipe left → regenerate this day
                        if (!replacingDay && swipeDay.meal_name)
                          replaceDay(swipeDay, false, false);
                      } else {
                        // swipe right → mark as cooked tonight
                        if (swipeDay.meal_name) {
                          onSetCookingTonight?.(
                            swipeDay.meal_name,
                            swipeDay.meal_type || activeMealType || "dinner",
                          );
                          onToast?.(
                            `✓ ${dayMealMeta.label} posted to Family activity`,
                          );
                        }
                      }
                    }}
                  >
                    <div className="fd-day-date">
                      <span className="fd-day-label">
                        {mealPlanDayLabel(plan, day).slice(0, 3)}
                      </span>
                      <span className="fd-day-date__sub">
                        {formatMealPlanDate(plan, day)}
                      </span>
                      {isUpdated && (
                        <span className="fd-badge fd-badge--updated">
                          Updated
                        </span>
                      )}
                      {isToday && (
                        <span className="fd-badge fd-badge--today">Today</span>
                      )}
                      {isPast && (
                        <span className="fd-badge fd-badge--passed">
                          Passed
                        </span>
                      )}
                      {day.source === "seed" && (
                        <span className="fd-badge fd-badge--demo">Demo</span>
                      )}
                      {day.source === "manual" && (
                        <span className="fd-badge fd-badge--manual">
                          ✏ Manual
                        </span>
                      )}
                      {day.allergen_warning && (
                        <span className="fd-badge fd-badge--allergen">
                          ⚠ Allergen
                        </span>
                      )}
                    </div>
                    <div
                      className={`fd-day-photo${hasMeal && !imageUrl ? " fd-day-photo--shimmer" : ""}`}
                    >
                      {imageUrl && (
                        <img
                          key={`${day.day_index}-${display.title || day.meal_name || imageUrl}`}
                          src={imageUrl}
                          alt=""
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                            event.currentTarget.parentElement?.classList.add(
                              "fd-day-photo--fallback",
                            );
                          }}
                        />
                      )}
                      <span aria-hidden="true">{display.icon}</span>
                    </div>
                    <div className="fd-day-main">
                      <div className="fd-day-meal">
                        {display.title ? (
                          <span className="fd-day-meal-title">
                            {display.title}
                          </span>
                        ) : (
                          <em className="fd-muted">Add meal</em>
                        )}
                        {display.sub && (
                          <span className="fd-day-meal-sub">{display.sub}</span>
                        )}
                      </div>
                      <div className="fd-day-meta">
                        {tags.map((tag) => (
                          <span key={tag} className="fd-cuisine-chip">
                            {tag}
                          </span>
                        ))}
                        {display.time && (
                          <div className="fd-day-time">⏱ {display.time}</div>
                        )}
                      </div>
                    </div>
                    {day.notes && (
                      <div className="fd-day-notes">{day.notes}</div>
                    )}
                  </button>
                );
              })(),
            )}
          </div>

          {selectedDay && !compactMealSheet && (
            <div className="fd-day-detail fd-day-detail--desktop">
              {selectedMealImage && (
                <div className="fd-meal-detail-photo">
                  <img
                    src={selectedMealImage}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="fd-day-detail__head">
                <div>
                  <p className="fd-label">
                    {mealPlanDayLabel(plan, selectedDay)}
                    {selectedIsToday ? " · Today" : ""}
                  </p>
                  <h3>{selectedMealParts.title || selectedDay.meal_name}</h3>
                </div>
                <button
                  className="fd-inline-close fd-inline-close--panel"
                  type="button"
                  onClick={closeSelectedMeal}
                  aria-label="Close meal details"
                >
                  ×
                </button>
              </div>
              <div className="fd-day-detail__meta">
                {selectedMealParts.cuisine && (
                  <span className="fd-cuisine-chip">
                    {selectedMealParts.cuisine}
                  </span>
                )}
                {selectedDay.cook_time && (
                  <span>⏱ {selectedDay.cook_time}</span>
                )}
                <span>{selectedRecipe?.difficulty || "easy"}</span>
                {selectedPrepMinutes && (
                  <span>
                    {selectedPrepMinutes <= 30 ? "Quick" : "Planned prep"}
                  </span>
                )}
                {selectedDay.uses_expiring_items && (
                  <span>Uses expiring items</span>
                )}
                {selectedDay.source === "manual" && <span>✏ Manual</span>}
                {selectedDay.allergen_warning && (
                  <span style={{ color: "#c0392b", fontWeight: 600 }}>
                    ⚠ May contain allergen — review ingredients
                  </span>
                )}
                {selectedDay.meal_name && (
                  <button
                    type="button"
                    className={`fd-loved-btn${lovedMeals.includes(selectedDay.meal_name) ? " fd-loved-btn--active" : ""}`}
                    onClick={() => toggleLovedMeal(selectedDay.meal_name)}
                    title={
                      lovedMeals.includes(selectedDay.meal_name)
                        ? "Remove from favourites"
                        : "Love this meal"
                    }
                  >
                    {lovedMeals.includes(selectedDay.meal_name)
                      ? "❤️ Loved"
                      : "🤍 Love it"}
                  </button>
                )}
              </div>
              {selectedMealSummary && (
                <p className="fd-meal-detail-summary">{selectedMealSummary}</p>
              )}
              {selectedMealTags.length > 0 && (
                <div className="fd-tag-row fd-meal-detail-tags">
                  {selectedMealTags.map((tag) => (
                    <span key={tag} className="fd-tag fd-tag--green">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {selectedPantryIngredients.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">From your pantry</p>
                  <div className="fd-tag-row">
                    {selectedPantryIngredients.map((ing, i) => (
                      <span key={i} className="fd-tag fd-tag--green">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedMissingIngredients.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">Shopping needed</p>
                  <div className="fd-tag-row">
                    {selectedMissingIngredients.map((ing) => (
                      ingredientIsInShopping(ing) ? (
                        <span key={ing} className="fd-tag fd-tag--amber">{ing}</span>
                      ) : (
                        <button
                          key={ing}
                          type="button"
                          className="fd-tag fd-tag--amber fd-tag--action"
                          onClick={() => setShoppingIngredientTarget({
                            ingredient: ing,
                            mealDayId: selectedDay.id,
                            mealName: selectedDay.meal_name,
                          })}
                          aria-label={`Add ${ing} to Shopping`}
                        >
                          {ing}<span aria-hidden="true">+</span>
                        </button>
                      )
                    ))}
                  </div>
                </div>
              )}
              {selectedSteps.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">Recipe</p>
                  <ol className="fd-steps-list">
                    {selectedSteps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="fd-day-detail__actions">
                {!selectedIsPast && (
                  <>
                    <button
                      className="hm-btn hm-btn--ghost"
                      type="button"
                      onClick={() => openSwapSheet(selectedDay)}
                      disabled={replacingDay}
                    >
                      ↻ Swap {selectedMealLower}
                    </button>
                    <button
                      className="hm-btn hm-btn--ghost"
                      type="button"
                      onClick={() => openDislikeSheet(selectedDay)}
                      disabled={replacingDay}
                    >
                      👎 Avoid {selectedMealLower}
                    </button>
                    {canShare &&
                      (selectedMealParts.title || selectedDay.meal_name) && (
                        <button
                          className="hm-btn hm-btn--ghost"
                          type="button"
                          onClick={() => postSelectedMealToFamily()}
                          disabled={
                            Boolean(mealActionNotice) || selectedCookingPosted
                          }
                        >
                          {selectedCookingPosted
                            ? "✓ Posted to Family"
                            : `${selectedMealMeta.icon} Cooking ${selectedMealLower === "dinner" ? "tonight" : selectedMealLower === "breakfast" ? "this morning" : "for lunch"}`}
                        </button>
                      )}
                  </>
                )}
                {selectedCanConfirmCooked &&
                  (selectedBlockPantryButton && !selectedPantryUsed ? (
                    <p className="fd-no-ingredients-note">
                      {selectedPantryBlockCopy}
                    </p>
                  ) : (
                    <button
                      className="hm-btn hm-btn--primary"
                      type="button"
                      disabled={selectedBlockPantryButton || selectedPantryUsed}
                      onClick={() => markMealCookedAndUsePantry(selectedDay)}
                    >
                      {selectedPantryUsed
                        ? "✓ Pantry items used"
                        : `Cooked this ${selectedMealLower} — mark pantry used`}
                    </button>
                  ))}
                {selectedNeedsPastCookConfirm && (
                  <button
                    className="hm-btn hm-btn--ghost"
                    type="button"
                    onClick={() => markMealSkipped(selectedDay)}
                  >
                    Mark skipped
                  </button>
                )}
                <button
                  className="hm-btn hm-btn--ghost"
                  type="button"
                  onClick={saveSelectedRecipe}
                  disabled={savingSheetRecipe || selectedRecipeSaved}
                >
                  {selectedRecipeSaveLabel}
                </button>
                {canShare && (
                  <button
                    className="hm-btn hm-btn--ghost"
                    type="button"
                    onClick={() => shareSelectedMealRecipe()}
                    disabled={savingSheetRecipe}
                  >
                    Share recipe
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedDay && compactMealSheet && (
            <FoodMobileSheet
              title={
                selectedMealParts.title ||
                selectedDay.meal_name ||
                "Meal details"
              }
              label={`${selectedMealParts.title || "Meal"} details`}
              onClose={closeSelectedMeal}
              disableBackdropClose
              dragHandleOnly
            >
              <div className="fd-meal-sheet fd-meal-sheet--shared">
                {selectedMealImage && (
                  <div className="fd-meal-sheet__photo">
                    <img
                      src={selectedMealImage}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="fd-meal-sheet__head">
                  <p className="fd-meal-sheet__day">
                    {mealPlanDayLabel(plan, selectedDay)}
                    {selectedIsToday && (
                      <span className="fd-meal-sheet__today">Today</span>
                    )}
                  </p>
                  <div className="fd-meal-sheet__tags">
                    {selectedMealParts.cuisine && (
                      <span className="fd-meal-sheet__tag fd-meal-sheet__tag--cuisine">
                        {selectedMealParts.cuisine}
                      </span>
                    )}
                    {selectedDay.cook_time && (
                      <span className="fd-meal-sheet__tag">
                        ⏱ {selectedDay.cook_time}
                      </span>
                    )}
                    <span className="fd-meal-sheet__tag">
                      {selectedRecipe?.difficulty || "easy"}
                    </span>
                    {selectedMealTags.map((tag) => (
                      <span key={tag} className="fd-meal-sheet__tag">
                        {tag}
                      </span>
                    ))}
                    {selectedDay.source === "manual" && (
                      <span className="fd-meal-sheet__tag fd-meal-sheet__tag--manual">
                        ✏ Manual
                      </span>
                    )}
                  </div>
                </div>
                <div className="fd-meal-sheet__body">
                  {selectedMealSummary && (
                    <section className="fd-meal-sheet__summary">
                      {selectedMealSummary}
                    </section>
                  )}
                  {selectedPantryIngredients.length > 0 && (
                    <div className="fd-recipe-section">
                      <p className="fd-label">Using from your pantry</p>
                      <div className="fd-tag-row">
                        {selectedPantryIngredients.map((ing, i) => (
                          <span key={i} className="fd-tag fd-tag--green">
                            {ing}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMissingIngredients.length > 0 && (
                    <div className="fd-recipe-section">
                      <p className="fd-label">Shopping needed</p>
                      <div className="fd-tag-row">
                        {selectedMissingIngredients.map((ing) => (
                          ingredientIsInShopping(ing) ? (
                            <span key={ing} className="fd-tag fd-tag--amber">{ing}</span>
                          ) : (
                            <button
                              key={ing}
                              type="button"
                              className="fd-tag fd-tag--amber fd-tag--action"
                              onClick={() => setShoppingIngredientTarget({
                                ingredient: ing,
                                mealDayId: selectedDay.id,
                                mealName: selectedDay.meal_name,
                              })}
                              aria-label={`Add ${ing} to Shopping`}
                            >
                              {ing}<span aria-hidden="true">+</span>
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedSteps.length > 0 && (
                    <div className="fd-recipe-section">
                      <p className="fd-label">Recipe steps</p>
                      <ol className="fd-steps-list fd-steps-list--sheet">
                        {selectedSteps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
                <div className="fd-meal-sheet__actions">
                  {selectedCanConfirmCooked &&
                    (selectedBlockPantryButton && !selectedPantryUsed ? (
                      <p className="fd-no-ingredients-note fd-no-ingredients-note--sheet">
                        {selectedPantryBlockCopy}
                      </p>
                    ) : (
                      <button
                        className="hm-btn hm-btn--primary fd-meal-sheet__primary"
                        type="button"
                        disabled={
                          selectedBlockPantryButton || selectedPantryUsed
                        }
                        onClick={() => markMealCookedAndUsePantry(selectedDay)}
                      >
                        {selectedPantryUsed
                          ? "✓ Pantry items used"
                          : `✓ Cooked this ${selectedMealLower} — mark pantry used`}
                      </button>
                    ))}
                  {selectedNeedsPastCookConfirm && (
                    <button
                      className="hm-btn hm-btn--ghost fd-meal-sheet__secondary"
                      type="button"
                      onClick={() => markMealSkipped(selectedDay)}
                    >
                      Mark skipped
                    </button>
                  )}
                  {!selectedIsPast &&
                    canShare &&
                    (selectedMealParts.title || selectedDay.meal_name) && (
                      <button
                        className="hm-btn hm-btn--ghost fd-meal-sheet__secondary"
                        type="button"
                        onClick={() =>
                          postSelectedMealToFamily({ closeSheet: true })
                        }
                        disabled={
                          Boolean(mealActionNotice) || selectedCookingPosted
                        }
                      >
                        {selectedCookingPosted
                          ? "✓ Posted to Family"
                          : `${selectedMealMeta.icon} Cooking ${selectedMealLower === "dinner" ? "tonight" : selectedMealLower === "breakfast" ? "this morning" : "for lunch"}`}
                      </button>
                    )}
                  <button
                    className="hm-btn hm-btn--ghost fd-meal-sheet__secondary"
                    type="button"
                    onClick={saveSelectedRecipe}
                    disabled={savingSheetRecipe || selectedRecipeSaved}
                  >
                    💾 {selectedRecipeSaveLabel}
                  </button>
                  {!selectedIsPast && (
                    <>
                      <button
                        className="hm-btn hm-btn--ghost"
                        type="button"
                        onClick={() => openSwapSheetAfterMealClose(selectedDay)}
                        disabled={replacingDay}
                      >
                        ↻ Swap {selectedMealLower}
                      </button>
                      <button
                        className="hm-btn hm-btn--ghost"
                        type="button"
                        onClick={() => openDislikeSheet(selectedDay)}
                        disabled={replacingDay}
                      >
                        👎 Avoid {selectedMealLower}
                      </button>
                    </>
                  )}
                  {canShare && (
                    <button
                      className="hm-btn hm-btn--ghost"
                      type="button"
                      onClick={() =>
                        shareSelectedMealRecipe({ closeSheet: true })
                      }
                      disabled={savingSheetRecipe}
                    >
                      ↗ Share
                    </button>
                  )}
                </div>
              </div>
            </FoodMobileSheet>
          )}
          {swapSheetDay && (
            <FoodMobileSheet
              title={`Swap ${swapSheetMealLower}`}
              label={`${swapSheetMealMeta.label} swap options`}
              onClose={closeSwapSheet}
              disableBackdropClose
              dragHandleOnly
            >
              <div className="fd-clear-sheet-content fd-swap-sheet">
                <div className="fd-clear-sheet__head">
                  <div className="fd-clear-sheet__warn">
                    ↻ Choose this day's {swapSheetMealLower}
                  </div>
                  <div className="fd-clear-sheet__sub">
                    {mealPlanDayLabel(plan, swapSheetDay)} can use your own{" "}
                    {swapSheetMealLower} or a fresh HouseMind suggestion.
                  </div>
                </div>
                <div className="fd-clear-sheet__body">
                  <button
                    type="button"
                    className={`fd-clear-opt${manualEntryOpen ? " fd-clear-opt--on" : ""}`}
                    onClick={() => setManualEntryOpen(true)}
                    disabled={replacingDay}
                  >
                    <div className="fd-clear-opt__ico">✏️</div>
                    <div className="fd-clear-opt__info">
                      <div className="fd-clear-opt__label">
                        Enter my own {swapSheetMealLower}
                      </div>
                      <div className="fd-clear-opt__range">
                        Manual {swapSheetMealLower}
                      </div>
                      <div className="fd-clear-opt__hint">
                        Regenerate will skip this day
                      </div>
                    </div>
                    <div className="fd-clear-opt__radio" />
                  </button>
                  {manualEntryOpen && (
                    <div className="fd-swap-manual">
                      <label className="fd-label" htmlFor="manual-meal-name">
                        {swapSheetMealMeta.label} name
                      </label>
                      <input
                        id="manual-meal-name"
                        className="fd-swap-manual__input"
                        type="text"
                        value={manualMealName}
                        onChange={(event) =>
                          setManualMealName(event.target.value)
                        }
                        placeholder="e.g. Jollof rice and chicken"
                        autoFocus
                      />
                      <button
                        className="hm-btn hm-btn--primary fd-swap-manual__save"
                        type="button"
                        onClick={saveManualMeal}
                        disabled={replacingDay || !manualMealName.trim()}
                      >
                        {replacingDay
                          ? "Saving…"
                          : `Save manual ${swapSheetMealLower}`}
                      </button>
                    </div>
                  )}
                  <div className="fd-swap-saved">
                    <div className="fd-swap-saved__head">
                      <span>Saved {swapSheetMealLower}s</span>
                      <small>
                        {savedMealsLoading
                          ? "Loading..."
                          : swapSavedMealOptions.length
                            ? `${swapSavedMealOptions.length} available`
                            : "None saved"}
                      </small>
                    </div>
                    {swapSavedMealOptions.length > 0 && (
                      <div className="fd-swap-saved__list">
                        {swapSavedMealOptions.slice(0, 6).map((recipe) => (
                          <button
                            key={recipe.id}
                            type="button"
                            className="fd-swap-saved__row"
                            onClick={() => applySavedMealToDay(recipe)}
                            disabled={replacingDay}
                          >
                            <span>{recipe.title}</span>
                            <small>
                              {[
                                recipe.prep_time
                                  ? `${recipe.prep_time} min`
                                  : null,
                                recipe.ingredients?.length
                                  ? `${recipe.ingredients.length} ingredients`
                                  : "Saved recipe",
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </small>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="fd-clear-opt"
                    onClick={async () => {
                      const day = swapSheetDay;
                      setSwapSheetDay(null);
                      setManualMealName("");
                      setManualEntryOpen(false);
                      await replaceDay(day, false, true);
                    }}
                    disabled={replacingDay}
                  >
                    <div className="fd-clear-opt__ico">✨</div>
                    <div className="fd-clear-opt__info">
                      <div className="fd-clear-opt__label">
                        Suggest another {swapSheetMealLower}
                      </div>
                      <div className="fd-clear-opt__range">HouseMind AI</div>
                      <div className="fd-clear-opt__hint">
                        Replaces only this {swapSheetMealLower}
                      </div>
                    </div>
                    <div className="fd-clear-opt__radio" />
                  </button>
                </div>
                <div className="fd-clear-sheet__foot">
                  <button
                    className="fd-clear-cancel"
                    type="button"
                    onClick={closeSwapSheet}
                    disabled={replacingDay}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </FoodMobileSheet>
          )}
          {dislikeSheetDay && (
            <FoodMobileSheet
              title={`Avoid this ${dislikeSheetMealLower}`}
              label={`${dislikeSheetMealMeta.label} avoid options`}
              onClose={closeDislikeSheet}
              disableBackdropClose
              dragHandleOnly
            >
              <div className="fd-clear-sheet-content fd-dislike-sheet">
                <div className="fd-clear-sheet__head">
                  <div className="fd-clear-sheet__warn">
                    👎 Choose how HouseMind should avoid it
                  </div>
                  <div className="fd-clear-sheet__sub">
                    {dislikeSheetDay.meal_name ||
                      `This ${dislikeSheetMealLower}`}{" "}
                    will be replaced. Choose a one-time swap or save it to
                    dislikes for future {dislikeSheetMealLower} plans.
                  </div>
                </div>
                <div className="fd-clear-sheet__body">
                  <button
                    type="button"
                    className={`fd-clear-opt${dislikeChoice === "week" ? " fd-clear-opt--on" : ""}`}
                    onClick={() => setDislikeChoice("week")}
                    disabled={replacingDay}
                  >
                    <div className="fd-clear-opt__ico">📆</div>
                    <div className="fd-clear-opt__info">
                      <div className="fd-clear-opt__label">
                        Swap only this week
                      </div>
                      <div className="fd-clear-opt__range">
                        No dislike saved
                      </div>
                      <div className="fd-clear-opt__hint">
                        Same as swapping this {dislikeSheetMealLower}, without
                        saving a dislike
                      </div>
                    </div>
                    <div className="fd-clear-opt__radio" />
                  </button>
                  <button
                    type="button"
                    className={`fd-clear-opt${dislikeChoice === "forever" ? " fd-clear-opt--on" : ""}`}
                    onClick={() => setDislikeChoice("forever")}
                    disabled={replacingDay}
                  >
                    <div className="fd-clear-opt__ico">🚫</div>
                    <div className="fd-clear-opt__info">
                      <div className="fd-clear-opt__label">
                        Save to dislikes
                      </div>
                      <div className="fd-clear-opt__range">
                        Saved to dislikes
                      </div>
                      <div className="fd-clear-opt__hint">
                        Applies to future {dislikeSheetMealLower} plans and
                        replacements
                      </div>
                    </div>
                    <div className="fd-clear-opt__radio" />
                  </button>
                </div>
                <div className="fd-clear-sheet__foot">
                  <button
                    className="fd-clear-cancel"
                    type="button"
                    onClick={closeDislikeSheet}
                    disabled={replacingDay}
                  >
                    Cancel
                  </button>
                  <button
                    className="hm-btn hm-btn--primary fd-clear-confirm"
                    type="button"
                    disabled={!dislikeChoice || replacingDay}
                    onClick={confirmDislikeChoice}
                  >
                    {replacingDay
                      ? "Replacing…"
                      : dislikeChoice === "forever"
                        ? `Save dislike & swap ${dislikeSheetMealLower} ↻`
                        : `Swap ${dislikeSheetMealLower} ↻`}
                  </button>
                </div>
              </div>
            </FoodMobileSheet>
          )}
          {shoppingIngredientTarget && (
            <ConfirmModal
              tag="Shopping needed"
              title={`Add ${shoppingIngredientTarget.ingredient}?`}
              message={`Add this missing ingredient for ${shoppingIngredientTarget.mealName || "this meal"} to your Food Shopping list.`}
              confirmLabel="Add to shopping"
              cancelLabel="Not now"
              danger={false}
              loading={shoppingIngredientBusy}
              onConfirm={addSelectedIngredientToShopping}
              onCancel={() => !shoppingIngredientBusy && setShoppingIngredientTarget(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Recipes Tab ────────────────────────────────────────────────────────────
function RecipesTab({ pantry, workspaceId, onCookRecipe, onAiUsage, onToast }) {
  const [time, setTime] = useState(() => {
    const saved = Number(window.localStorage.getItem("hm_food_recipe_time"));
    return RECIPE_TIME_OPTIONS.includes(saved) ? saved : 60;
  });
  const recipeResultsStorageKey = workspaceId
    ? `hm_food_recipe_results:${workspaceId}`
    : "";
  const [recipes, setRecipes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingRecipeIndex, setSavingRecipeIndex] = useState(null);
  const [cookingRecipeIndex, setCookingRecipeIndex] = useState(null);
  const [selectedRecipeIndex, setSelectedRecipeIndex] = useState(null);
  const [selectedSavedRecipe, setSelectedSavedRecipe] = useState(null);
  const [cookedHistory, setCookedHistory] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedRecipesOpen, setSavedRecipesOpen] = useState(true);
  const [removingSavedRecipeId, setRemovingSavedRecipeId] = useState(null);
  const showRecipeSaving = useMinLoad(savingRecipeIndex !== null, 700);
  const showRecipeCooking = useMinLoad(cookingRecipeIndex !== null, 700);
  const recipeSheetRef = useRef(null);
  const recipeSheetDrag = useRef({
    startY: 0,
    deltaY: 0,
    active: false,
    dragging: false,
  });
  const selectedRecipe =
    selectedSavedRecipe ||
    (selectedRecipeIndex === null ? null : recipes?.[selectedRecipeIndex]);
  const [suggestedRecipeImages, suggestedRecipeImagePending] = useRecipeImages(
    recipes || [],
  );
  const [savedRecipeImages, savedRecipeImagePending] =
    useRecipeImages(savedRecipes);
  const recipeDisplayImageUrl = useCallback(
    (recipe) =>
      suggestedRecipeImages.get(recipeImageKey(recipe)) ||
      savedRecipeImages.get(recipeImageKey(recipe)) ||
      recipeImageUrl(recipe),
    [savedRecipeImages, suggestedRecipeImages],
  );
  const recipeImagePending = useCallback(
    (recipe) =>
      suggestedRecipeImagePending.has(recipeImageKey(recipe)) ||
      savedRecipeImagePending.has(recipeImageKey(recipe)),
    [savedRecipeImagePending, suggestedRecipeImagePending],
  );

  function updateRecipes(nextRecipes) {
    setRecipes(nextRecipes);
    if (recipeResultsStorageKey) {
      window.localStorage.setItem(
        recipeResultsStorageKey,
        JSON.stringify(nextRecipes),
      );
    }
  }

  useEffect(() => {
    if (!recipeResultsStorageKey) {
      setRecipes(null);
      return;
    }
    try {
      const saved = JSON.parse(
        window.localStorage.getItem(recipeResultsStorageKey) || "null",
      );
      setRecipes(Array.isArray(saved) ? saved : null);
    } catch {
      setRecipes(null);
    }
    window.localStorage.removeItem("hm_food_recipe_results");
  }, [recipeResultsStorageKey]);

  const loadCookedHistory = useCallback(async () => {
    try {
      const data = await mealsApi.cookedRecipes();
      setCookedHistory(Array.isArray(data.history) ? data.history : []);
    } catch {
      setCookedHistory([]);
    }
  }, []);

  const loadSavedRecipes = useCallback(async () => {
    try {
      const data = await mealsApi.savedRecipes();
      setSavedRecipes(Array.isArray(data.recipes) ? data.recipes : []);
    } catch {
      setSavedRecipes([]);
    }
  }, []);

  useEffect(() => {
    loadCookedHistory();
    loadSavedRecipes();
  }, [loadCookedHistory, loadSavedRecipes]);

  const closeRecipeSheet = useCallback(() => {
    if (recipeSheetRef.current) {
      recipeSheetRef.current.style.transform = "";
      recipeSheetRef.current.style.transition = "";
    }
    recipeSheetDrag.current = {
      startY: 0,
      deltaY: 0,
      active: false,
      dragging: false,
    };
    setSelectedRecipeIndex(null);
    setSelectedSavedRecipe(null);
  }, []);

  const startRecipeSheetDrag = useCallback((clientY) => {
    if (!clientY && clientY !== 0) return;
    recipeSheetDrag.current = {
      startY: clientY,
      deltaY: 0,
      active: true,
      dragging: false,
    };
    if (recipeSheetRef.current)
      recipeSheetRef.current.style.transition = "none";
  }, []);

  const moveRecipeSheetDrag = useCallback((clientY, eventTarget, event) => {
    const drag = recipeSheetDrag.current;
    if (!drag.active || (!clientY && clientY !== 0)) return;

    const deltaY = Math.max(0, clientY - drag.startY);
    if (deltaY <= 0) return;
    if (deltaY > 6) {
      event?.preventDefault?.();
      drag.dragging = true;
    }
    drag.deltaY = deltaY;
    if (recipeSheetRef.current) {
      recipeSheetRef.current.style.transform = `translate3d(0, ${deltaY}px, 0)`;
    }
  }, []);

  const endRecipeSheetDrag = useCallback(() => {
    const drag = recipeSheetDrag.current;
    if (drag.active && drag.deltaY > 72) {
      closeRecipeSheet();
      return;
    }
    if (recipeSheetRef.current) {
      recipeSheetRef.current.style.transition = "transform .18s ease";
      recipeSheetRef.current.style.transform = "";
    }
    recipeSheetDrag.current = {
      startY: 0,
      deltaY: 0,
      active: false,
      dragging: false,
    };
  }, [closeRecipeSheet]);

  function dismissRecipe(index) {
    updateRecipes((recipes || []).filter((_, i) => i !== index));
  }

  const pantryItemCount = pantry.filter((item) =>
    String(item?.name || "").trim(),
  ).length;
  const canSuggestFromPantry = pantryItemCount >= 3;
  const canSuggestWithStaples = pantryItemCount > 0;
  const smallPantryMessage =
    pantryItemCount === 0
      ? "Add a few pantry items first. Recipes work best with at least 3 saved ingredients."
      : `You only have ${pantryItemCount} pantry item${pantryItemCount === 1 ? "" : "s"} saved. Add a few more ingredients or use common staples.`;

  async function suggest({ includeCommonStaples = false } = {}) {
    if (!canSuggestFromPantry && !includeCommonStaples) {
      setRecipes([]);
      setError(smallPantryMessage);
      return;
    }
    if (includeCommonStaples && !canSuggestWithStaples) {
      setRecipes([]);
      setError(
        "Add at least one pantry item first so HouseMind has something to build around.",
      );
      return;
    }
    setLoading(true);
    setError("");
    setRecipes(null);
    try {
      const r = await mealsApi.suggestRecipes({
        time_available: time,
        include_common_staples: includeCommonStaples,
      });
      onAiUsage?.(r.ai_usage);
      const nextRecipes = r.recipes || [];
      setRecipes(nextRecipes);
      window.localStorage.setItem("hm_food_recipe_time", String(time));
      window.localStorage.setItem(
        "hm_food_recipe_results",
        JSON.stringify(nextRecipes),
      );
      if (!r.ai_configured)
        setError("AI not configured — showing example recipes.");
      if (nextRecipes.length === 0) {
        setError(
          "No recipes matched your pantry yet. Add a few more ingredients or try common staples.",
        );
      }
    } catch (e) {
      if (e.status === 422) {
        setRecipes([]);
        setError(e.hint ? `${e.message} ${e.hint}` : e.message);
      } else if (/timeout|timed out/i.test(e.message || "")) {
        setError(
          "Recipe search took too long. Add a few more pantry items or try common staples for a faster result.",
        );
      } else {
        setError(e.message || "Could not find recipes right now.");
      }
    }
    setLoading(false);
  }

  async function saveRecipe(recipe, index) {
    if (!recipe?.title) return;
    setSavingRecipeIndex(index);
    setError("");
    try {
      const saved = await mealsApi.saveRecipe({
        ...recipe,
        image_url: validMealImageUrl(
          recipe.image_url ||
            recipe.photo_url ||
            recipe.recipe?.image_url ||
            recipe.recipe?.photo_url ||
            "",
        ),
        source: "ai",
        explicit_save: true,
      });
      loadSavedRecipes();
      const nextRecipes = (recipes || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, id: saved.id } : item,
      );
      updateRecipes(nextRecipes);
      return saved;
    } catch (e) {
      setError(e.message || "Could not save this recipe.");
      return null;
    } finally {
      setSavingRecipeIndex(null);
    }
  }

  async function handleRecipeSave(recipe, index) {
    if (recipe?.id) return;
    const saved = await saveRecipe(recipe, index);
    if (saved) onToast?.("Recipe saved to Recipes");
  }

  async function removeSavedRecipe(recipe) {
    if (!recipe?.id) return;
    setRemovingSavedRecipeId(recipe.id);
    setError("");
    try {
      await mealsApi.removeRecipe(recipe.id);
      setSavedRecipes((prev) => prev.filter((item) => item.id !== recipe.id));
      setSelectedSavedRecipe(null);
      onToast?.("Recipe removed from saved");
    } catch (e) {
      setError(e.message || "Could not remove saved recipe.");
    } finally {
      setRemovingSavedRecipeId(null);
    }
  }

  async function cookRecipe(recipe, index) {
    if (!recipe?.title) return;
    setError("");
    try {
      const cooked = await onCookRecipe?.(recipe);
      if (cooked === null) return;
      if (Number.isInteger(index)) dismissRecipe(index);
      setSelectedRecipeIndex(null);
      setSelectedSavedRecipe(null);
      loadCookedHistory();
    } catch (e) {
      setError(e.message || "Could not cook this recipe.");
    } finally {
      setCookingRecipeIndex(null);
    }
  }

  const urgent = pantry.filter((i) => i.level !== "stable");
  const selectedRecipeTruth = selectedRecipe
    ? recipePantryTruth(selectedRecipe, pantry)
    : { available: [], missing: [] };
  const selectedRecipeIngredients = selectedRecipe
    ? uniqueIngredientLabels(
        selectedRecipe.ingredients?.length
          ? selectedRecipe.ingredients
          : [
              ...(selectedRecipe.uses_from_pantry || []),
              ...(selectedRecipe.still_need || []),
            ],
      )
    : [];
  const visibleSavedRecipes = savedRecipes.filter((recipe) => {
    const source = String(recipe.source || "").toLowerCase();
    return !["meal_plan_generated", "meal_image_cache"].includes(source);
  });

  return (
    <div className="fd-tab-content">
      {loading && (
        <SavingNotice
          title="Finding recipes"
          message="HouseMind is looking at your pantry..."
        />
      )}
      {showRecipeSaving && (
        <SavingNotice title="Saving" message="Saving recipe..." />
      )}
      {showRecipeCooking && (
        <SavingNotice
          title="Cooking"
          message="HouseMind is updating your pantry and history..."
        />
      )}
      {removingSavedRecipeId && (
        <SavingNotice title="Removing" message="Removing saved recipe..." />
      )}
      <div className="fd-recipe-controls">
        <div>
          <p className="fd-label">How much time do you have?</p>
          <div className="fd-time-pills">
            {RECIPE_TIME_OPTIONS.map((t) => (
              <button
                key={t}
                className={`fd-pill${time === t ? " fd-pill--active" : ""}`}
                onClick={() => setTime(t)}
              >
                {t} min
              </button>
            ))}
          </div>
        </div>
        {urgent.length > 0 && (
          <div className="fd-recipe-expiry-hint">
            🟠 {urgent.length} item{urgent.length > 1 ? "s" : ""} expiring — AI
            will prioritise these
          </div>
        )}
        {pantryItemCount < 3 && (
          <div className="fd-recipe-expiry-hint fd-recipe-expiry-hint--neutral">
            {smallPantryMessage}
          </div>
        )}
        <button
          className="hm-btn hm-btn--primary"
          onClick={() => suggest()}
          disabled={loading || pantryItemCount === 0}
        >
          {loading ? "Finding recipes…" : "🔍 Find recipes from my pantry"}
        </button>
        {pantryItemCount > 0 && pantryItemCount < 3 && (
          <button
            className="hm-btn hm-btn--ghost"
            type="button"
            onClick={() => suggest({ includeCommonStaples: true })}
            disabled={loading}
          >
            Use common staples
          </button>
        )}
      </div>

      {error && <div className="fd-error">{error}</div>}

      {visibleSavedRecipes.length > 0 && (
        <section
          className={`fd-saved-recipes${savedRecipesOpen ? " fd-saved-recipes--open" : ""}`}
          aria-label="Saved recipes"
        >
          <button
            type="button"
            className="fd-saved-recipes__toggle"
            onClick={() => setSavedRecipesOpen((open) => !open)}
            aria-expanded={savedRecipesOpen}
          >
            <span>
              <strong>Saved recipes</strong>
              <small>{visibleSavedRecipes.length} saved</small>
            </span>
            <span aria-hidden="true">{savedRecipesOpen ? "⌄" : "›"}</span>
          </button>
          {savedRecipesOpen && (
            <div className="fd-saved-recipes__list">
              {visibleSavedRecipes.slice(0, 10).map((recipe) => (
                <button
                  key={recipe.id}
                  type="button"
                  className="fd-saved-recipe-row"
                  onClick={() => setSelectedSavedRecipe(recipe)}
                >
                  <span>{recipe.title}</span>
                  <small>
                    {[
                      recipe.prep_time ? `${recipe.prep_time} min` : null,
                      recipe.ingredients?.length
                        ? `${recipe.ingredients.length} ingredients`
                        : "Meal idea",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {cookedHistory.length > 0 && (
        <section
          className="fd-recipe-history"
          aria-label="Recently cooked recipes"
        >
          <div className="fd-recipe-history__head">
            <h3>Recently cooked</h3>
            <span>{cookedHistory.length} saved</span>
          </div>
          <div className="fd-recipe-history__list">
            {cookedHistory.slice(0, 3).map((item) => (
              <article key={item.id} className="fd-recipe-history__item">
                <strong>{item.title}</strong>
                <span>
                  {item.cooked_at
                    ? new Date(item.cooked_at).toLocaleDateString([], {
                        month: "short",
                        day: "numeric",
                      })
                    : "Saved"}
                </span>
              </article>
            ))}
          </div>
        </section>
      )}

      {recipes && recipes.length === 0 && (
        <div className="fd-empty">
          No recipes found. Try adding more items to your pantry first.
        </div>
      )}

      {recipes &&
        recipes.map((r, i) => {
          const imageUrl = recipeDisplayImageUrl(r);
          const imagePending = recipeImagePending(r);
          const pantryTruth = recipePantryTruth(r, pantry);
          return (
            <div
              key={i}
              className={`fd-recipe-card${!imageUrl && !imagePending ? " fd-recipe-card--no-photo" : ""}`}
            >
              {(imageUrl || imagePending) && (
                <div
                  className={`fd-recipe-photo${imagePending && !imageUrl ? " fd-recipe-photo--loading" : ""}`}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                        event.currentTarget.parentElement?.classList.add(
                          "fd-recipe-photo--empty",
                        );
                      }}
                    />
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </div>
              )}
              <div className="fd-recipe-header">
                <div>
                  <h3 className="fd-recipe-title">{r.title}</h3>
                  <div className="fd-recipe-meta">
                    {r.prep_time && <span>⏱ {r.prep_time} min</span>}
                    {r.difficulty && <span>· {r.difficulty}</span>}
                    {r.nutrition?.calories && (
                      <span>· {r.nutrition.calories} cal</span>
                    )}
                  </div>
                </div>
                <button
                  className="fd-inline-close"
                  type="button"
                  onClick={() => dismissRecipe(i)}
                  aria-label="Dismiss recipe suggestion"
                >
                  ×
                </button>
              </div>
              <div className="fd-recipe-body">
                {pantryTruth.available.length > 0 && (
                  <div className="fd-recipe-section">
                    <p className="fd-label">✓ From your pantry</p>
                    <div className="fd-tag-row">
                      {pantryTruth.available.map((ing, j) => (
                        <span key={j} className="fd-tag fd-tag--green">
                          {ing}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {pantryTruth.missing.length > 0 && (
                  <div className="fd-recipe-section">
                    <p className="fd-label">🛒 Still need</p>
                    <div className="fd-tag-row">
                      {pantryTruth.missing.map((ing, j) => (
                        <span key={j} className="fd-tag fd-tag--amber">
                          {ing}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {r.instructions?.length > 0 && (
                  <button
                    className="fd-steps-toggle fd-steps-open"
                    type="button"
                    onClick={() => setSelectedRecipeIndex(i)}
                  >
                    View full recipe ({r.instructions.length} steps)
                  </button>
                )}
                <div className="fd-recipe-actions">
                  <button
                    className="hm-btn hm-btn--ghost fd-recipe-used-btn fd-recipe-cook-btn"
                    type="button"
                    onClick={() => cookRecipe(r, i)}
                    disabled={cookingRecipeIndex === i}
                  >
                    {cookingRecipeIndex === i ? "Cooking..." : "✓ Cooked it"}
                  </button>
                  <button
                    className="hm-btn hm-btn--ghost fd-recipe-used-btn fd-recipe-save-btn"
                    type="button"
                    onClick={() => handleRecipeSave(r, i)}
                    disabled={savingRecipeIndex === i || Boolean(r.id)}
                  >
                    {savingRecipeIndex === i
                      ? "Saving..."
                      : r.id
                        ? "Saved"
                        : "💾 Save"}
                  </button>
                  <button
                    className="hm-btn hm-btn--ghost fd-recipe-next-btn"
                    type="button"
                    onClick={() => dismissRecipe(i)}
                    aria-label="Skip this recipe suggestion"
                  >
                    <span aria-hidden="true">↻</span>
                    <span>Skip</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

      {selectedRecipe && (
        <div className="fd-recipe-sheet-backdrop" onClick={closeRecipeSheet}>
          <div
            ref={recipeSheetRef}
            className="fd-recipe-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedRecipe.title} full recipe`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hm-sheet-top-row fd-recipe-sheet__top">
              <button
                type="button"
                className="hm-sheet-handle"
                aria-label="Close recipe details"
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture?.(e.pointerId);
                  startRecipeSheetDrag(e.clientY);
                }}
                onPointerMove={(e) =>
                  moveRecipeSheetDrag(e.clientY, e.target, e)
                }
                onPointerUp={endRecipeSheetDrag}
                onPointerCancel={endRecipeSheetDrag}
              />
              <button
                type="button"
                className="hm-sheet-top-close"
                onClick={closeRecipeSheet}
                aria-label="Close recipe details"
              >
                ×
              </button>
            </div>
            <SheetLogoRow
              title="Recipe"
              onClose={closeRecipeSheet}
              closeLabel="Close recipe details"
              showClose={false}
            />
            {(() => {
              const imageUrl = recipeDisplayImageUrl(selectedRecipe);
              const imagePending = recipeImagePending(selectedRecipe);
              if (!imageUrl && !imagePending) return null;
              return (
                <div
                  className={`fd-recipe-sheet__photo${imagePending && !imageUrl ? " fd-recipe-photo--loading" : ""}`}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                        event.currentTarget.parentElement?.classList.add(
                          "fd-recipe-photo--empty",
                        );
                      }}
                    />
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </div>
              );
            })()}
            <div className="fd-recipe-sheet__head">
              <h3>{selectedRecipe.title}</h3>
              <p>
                {selectedRecipe.prep_time
                  ? `${selectedRecipe.prep_time} min`
                  : null}
                {selectedRecipe.difficulty
                  ? `${selectedRecipe.prep_time ? " · " : ""}${selectedRecipe.difficulty}`
                  : null}
                {selectedRecipe.nutrition?.calories
                  ? ` · ${selectedRecipe.nutrition.calories} cal`
                  : null}
              </p>
            </div>
            <div className="fd-recipe-sheet__body">
              {selectedRecipeTruth.available.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">From your pantry</p>
                  <div className="fd-tag-row">
                    {selectedRecipeTruth.available.map((ing, j) => (
                      <span key={j} className="fd-tag fd-tag--green">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedRecipeTruth.missing.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">Still need</p>
                  <div className="fd-tag-row">
                    {selectedRecipeTruth.missing.map((ing, j) => (
                      <span key={j} className="fd-tag fd-tag--amber">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedRecipe.instructions?.length > 0 && (
                <div className="fd-recipe-section">
                  <p className="fd-label">Steps</p>
                  <ol className="fd-steps-list fd-steps-list--sheet">
                    {selectedRecipe.instructions.map((step, j) => (
                      <li key={j}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              {!selectedRecipeIngredients.length &&
                !selectedRecipe.instructions?.length && (
                  <div className="fd-recipe-empty-detail">
                    This saved item only has the meal name. Save from a full
                    recipe card to keep ingredients and steps.
                  </div>
                )}
            </div>
            <div className="fd-recipe-sheet__actions">
              <button
                className="hm-btn hm-btn--primary"
                type="button"
                onClick={() =>
                  cookRecipe(
                    selectedRecipe,
                    selectedSavedRecipe ? "saved" : selectedRecipeIndex,
                  )
                }
                disabled={
                  cookingRecipeIndex ===
                  (selectedSavedRecipe ? "saved" : selectedRecipeIndex)
                }
              >
                {cookingRecipeIndex ===
                (selectedSavedRecipe ? "saved" : selectedRecipeIndex)
                  ? "Cooking..."
                  : selectedRecipeTruth.available.length
                    ? "✓ Cooked it - mark pantry used"
                    : "✓ Cooked it"}
              </button>
              {!selectedRecipe.id && (
                <button
                  className="hm-btn hm-btn--ghost"
                  type="button"
                  onClick={() =>
                    handleRecipeSave(selectedRecipe, selectedRecipeIndex)
                  }
                  disabled={savingRecipeIndex === selectedRecipeIndex}
                >
                  {savingRecipeIndex === selectedRecipeIndex
                    ? "Saving..."
                    : "💾 Save"}
                </button>
              )}
              {selectedSavedRecipe && (
                <button
                  className="hm-btn hm-btn--ghost fd-recipe-remove-sheet"
                  type="button"
                  onClick={() => removeSavedRecipe(selectedSavedRecipe)}
                  disabled={removingSavedRecipeId === selectedSavedRecipe.id}
                >
                  {removingSavedRecipeId === selectedSavedRecipe.id
                    ? "Removing..."
                    : "Remove from saved"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shopping Tab ──────────────────────────────────────────────────────────
function ShoppingTab({
  pantry = [],
  mealPlanContext,
  onShare,
  canShare,
  sharedResourceKeys,
  onPantryAdded,
}) {
  const [active, setActive] = useState(null); // full list with items
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pantryPromptItems, setPantryPromptItems] = useState([]);
  const [showPantryPrompt, setShowPantryPrompt] = useState(false);
  const [shoppingActualTotal, setShoppingActualTotal] = useState("");
  const [shoppingProvider, setShoppingProvider] = useState("");
  const [shoppingPurchaseDate, setShoppingPurchaseDate] = useState(() => localISODate());
  const [pantryPromptBusy, setPantryPromptBusy] = useState(false);

  // Legacy flat shopping items (backward compat)
  const [flatItems, setFlatItems] = useState([]);
  const [addName, setAddName] = useState("");
  const [addSection, setAddSection] = useState("other");
  const [addQty, setAddQty] = useState("");
  const [addUnit, setAddUnit] = useState("");
  const [addingFlat, setAddingFlat] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addNotice, setAddNotice] = useState("");
  const [removingItemKey, setRemovingItemKey] = useState("");
  const [actionItem, setActionItem] = useState(null);
  const [actionDraft, setActionDraft] = useState({
    quantity: "",
    section: "other",
  });
  const [shoppingHintDismissed, setShoppingHintDismissed] = useState(
    () => localStorage.getItem("hm_food_shopping_longpress_hint") === "1",
  );
  const [dismissedLowPromptKeys, setDismissedLowPromptKeys] = useState(() => {
    try {
      return new Set(
        JSON.parse(
          localStorage.getItem("hm_food_shopping_low_dismissed") || "[]",
        ),
      );
    } catch {
      return new Set();
    }
  });
  const [exportingList, setExportingList] = useState(false);
  const [selectingSection, setSelectingSection] = useState("");
  const shoppingLongPressRef = useRef(null);
  const shoppingSuppressClickRef = useRef(false);
  const presentation = useFormPresentation();
  const showShoppingWorking = useMinLoad(
    addingFlat || pantryPromptBusy || exportingList || Boolean(removingItemKey),
    700,
  );

  const pickShoppingListSummary = useCallback(
    (lists = []) => {
      const activeLists = lists.filter((list) => list.status === "active");
      if (!activeLists.length) return null;
      if (mealPlanContext?.id) {
        const byPlan = activeLists.find(
          (list) => Number(list.meal_plan_id) === Number(mealPlanContext.id),
        );
        if (byPlan) return byPlan;
        return null;
      }
      if (mealPlanContext?.week_start) {
        const byWeek = activeLists.find(
          (list) => list.meal_plan_week_start === mealPlanContext.week_start,
        );
        if (byWeek) return byWeek;
        return null;
      }
      // Without a plan context, never guess that an old generated list is the
      // user's current cart. Standalone/manual lists are the only safe fallback.
      return activeLists.find((list) => !list.meal_plan_id) || null;
    },
    [mealPlanContext?.id, mealPlanContext?.week_start],
  );

  function resetShoppingDraft() {
    setAddName("");
    setAddQty("");
    setAddUnit("");
  }

  function closeShoppingAdd() {
    setShowAddForm(false);
    resetShoppingDraft();
  }

  function showShoppingAddNotice(message) {
    setAddNotice(message);
    setTimeout(() => setAddNotice(""), 2200);
  }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setActive(null);
        setError("");
        const [listsRes, flatRes] = await Promise.all([
          mealsApi.shoppingLists(),
          shoppingApi.list(),
        ]);
        setFlatItems(flatRes?.items || []);
        const selectedSummary = pickShoppingListSummary(listsRes || []);
        setActive(
          selectedSummary
            ? {
                ...(await mealsApi.getShoppingList(selectedSummary.id)),
                ...selectedSummary,
              }
            : null,
        );
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    })();
  }, [
    mealPlanContext?.id,
    mealPlanContext?.week_start,
    pickShoppingListSummary,
  ]);

  async function toggleShoppingGroup(item, checked) {
    const members = item._members || [item];
    await Promise.all(
      members.map(member => (
        member._type === "structured"
          ? mealsApi.toggleShoppingItem(member._listId, member.id, { is_checked: checked })
          : shoppingApi.update(member.id, { checked })
      )),
    );
    const memberKeys = new Set(members.map(member => `${member._type}-${member.id}`));
    setFlatItems(items => items.map(row => (
      memberKeys.has(`flat-${row.id}`) ? { ...row, checked } : row
    )));
    setActive(current => current ? {
      ...current,
      items: (current.items || []).map(row => (
        memberKeys.has(`structured-${row.id}`) ? { ...row, is_checked: checked } : row
      )),
    } : current);
  }

  async function toggleShoppingSection(section, sectionItems) {
    if (!sectionItems.length || selectingSection) return;
    const shouldCheck = sectionItems.some((item) => !item.checked);
    setSelectingSection(section);
    setError("");
    try {
      const results = await Promise.allSettled(
        sectionItems.flatMap(item => item._members || [item]).map((item) =>
          item._type === "structured"
            ? mealsApi.toggleShoppingItem(item._listId, item.id, {
                is_checked: shouldCheck,
              })
            : shoppingApi.update(item.id, { checked: shouldCheck }),
        ),
      );
      const failed = results.find((result) => result.status === "rejected");
      if (failed) throw failed.reason || new Error(`Could not update ${section}.`);
      const sectionKeys = new Set(sectionItems.flatMap(item => item._members || [item]).map(
        item => `${item._type}-${item.id}`,
      ));
      setFlatItems((items) =>
        items.map((item) =>
          sectionKeys.has(`flat-${item.id}`)
            ? { ...item, checked: shouldCheck }
            : item,
        ),
      );
      setActive((current) =>
        current
          ? {
              ...current,
              items: (current.items || []).map((item) =>
                sectionKeys.has(`structured-${item.id}`)
                  ? { ...item, is_checked: shouldCheck }
                  : item,
              ),
            }
          : current,
      );
    } catch (e) {
      setError(e.message || `Could not update ${section}.`);
      try {
        const [listsRes, flatRes] = await Promise.all([
          mealsApi.shoppingLists(),
          shoppingApi.list(),
        ]);
        setFlatItems(flatRes?.items || []);
        const selectedSummary = pickShoppingListSummary(listsRes || []);
        setActive(
          selectedSummary
            ? {
                ...(await mealsApi.getShoppingList(selectedSummary.id)),
                ...selectedSummary,
              }
            : null,
        );
      } catch {}
    } finally {
      setSelectingSection("");
    }
  }

  async function removeShoppingItem(item) {
    const key = `${item._type}-${item.id}`;
    setRemovingItemKey(key);
    setActionItem(null);
    setError("");
    try {
      if (item._type === "structured") {
        await mealsApi.removeShoppingItem(item._listId, item.id);
        setActive((prev) =>
          prev
            ? {
                ...prev,
                items: (prev.items || []).filter((i) => i.id !== item.id),
              }
            : prev,
        );
      } else {
        await shoppingApi.remove(item.id);
        setFlatItems((prev) => prev.filter((i) => i.id !== item.id));
      }
      showShoppingAddNotice(`Removed ${item.name}`);
    } catch (e) {
      setError(e.message || "Could not remove shopping item.");
    } finally {
      setRemovingItemKey("");
    }
  }

  async function saveShoppingItemEdit() {
    if (!actionItem) return;
    const nextQuantity = String(actionDraft.quantity || "").trim();
    const nextSection = normalizeSection(
      actionDraft.section || actionItem.section || "other",
    );
    try {
      if (actionItem._type === "structured") {
        const updated = await mealsApi.toggleShoppingItem(
          actionItem._listId,
          actionItem.id,
          {
            quantity: nextQuantity,
            aisle: nextSection,
            category: nextSection,
          },
        );
        setActive((prev) =>
          prev
            ? {
                ...prev,
                items: (prev.items || []).map((item) =>
                  item.id === actionItem.id ? updated : item,
                ),
              }
            : prev,
        );
      } else {
        const updated = await shoppingApi.update(actionItem.id, {
          quantity: nextQuantity,
          section: nextSection,
        });
        setFlatItems((prev) =>
          prev.map((item) => (item.id === actionItem.id ? updated : item)),
        );
      }
      setActionItem(null);
      showShoppingAddNotice(`Updated ${actionItem.name}`);
    } catch (e) {
      setError(e.message || "Could not update shopping item.");
    }
  }

  function openShoppingItemActions(item) {
    setActionItem(item);
    setActionDraft({
      quantity: item.quantity || "",
      section: item.section || "other",
    });
    if (!shoppingHintDismissed) {
      localStorage.setItem("hm_food_shopping_longpress_hint", "1");
      setShoppingHintDismissed(true);
    }
  }

  function dismissShoppingHint() {
    localStorage.setItem("hm_food_shopping_longpress_hint", "1");
    setShoppingHintDismissed(true);
  }

  function dismissLowPrompt(item) {
    const key = `${item.id || item.name}:${item.expiry_date || ""}`;
    setDismissedLowPromptKeys((current) => {
      const next = new Set(current);
      next.add(key);
      localStorage.setItem(
        "hm_food_shopping_low_dismissed",
        JSON.stringify(Array.from(next).slice(-100)),
      );
      return next;
    });
  }

  function startShoppingLongPress(item) {
    window.clearTimeout(shoppingLongPressRef.current);
    shoppingSuppressClickRef.current = false;
    shoppingLongPressRef.current = window.setTimeout(() => {
      shoppingSuppressClickRef.current = true;
      openShoppingItemActions(item);
    }, 560);
  }

  function clearShoppingLongPress() {
    window.clearTimeout(shoppingLongPressRef.current);
  }

  async function addShoppingItem({
    name,
    section,
    quantity,
    unit,
    sourcePantryItemId,
  }) {
    const cleanName = String(name || "").trim();
    if (!cleanName) return null;
    const nameLower = cleanName.toLowerCase();
    const onFlatList = flatItems.some(
      (i) =>
        String(i.name || "")
          .trim()
          .toLowerCase() === nameLower,
    );
    const onStructuredList = (active?.items || []).some(
      (i) =>
        String(i.name || "")
          .trim()
          .toLowerCase() === nameLower,
    );
    if (onFlatList || onStructuredList) {
      setError(`"${cleanName}" is already on your list.`);
      setTimeout(() => setError(""), 2500);
      return null;
    }
    const cleanQuantity = [
      String(quantity || "").trim(),
      String(unit || "").trim(),
    ]
      .filter(Boolean)
      .join(" ");
    const cleanSection = section || "other";
    if (active?.id && active.status !== "completed") {
      const item = await mealsApi.addShoppingItem(active.id, {
        name: cleanName,
        aisle: cleanSection,
        category: cleanSection,
        quantity: cleanQuantity,
        for_recipes: ["Manual add"],
        pantry_item_id: sourcePantryItemId || null,
      });
      return { _type: "structured", ...item };
    }
    const item = await shoppingApi.add({
      name: cleanName,
      section: cleanSection,
      quantity: cleanQuantity,
    });
    return {
      _type: "flat",
      ...item,
      source_pantry_item_id: sourcePantryItemId || null,
    };
  }

  async function addFlat(e) {
    e?.preventDefault?.();
    if (!addName.trim()) return;
    setAddingFlat(true);
    try {
      const item = await addShoppingItem({
        name: addName,
        section: addSection,
        quantity: addQty,
        unit: addUnit,
      });
      if (!item) return;
      if (item._type === "structured") {
        setActive((prev) =>
          prev ? { ...prev, items: [item, ...(prev.items || [])] } : prev,
        );
      } else {
        setFlatItems((prev) => [item, ...prev]);
      }
      resetShoppingDraft();
      showShoppingAddNotice("✓ Added — type another or tap Cancel when done");
    } finally {
      setAddingFlat(false);
    }
  }

  async function clearChecked() {
    const tasks = [shoppingApi.clearChecked()];
    if (active?.items?.some((i) => i.is_checked)) {
      tasks.push(mealsApi.clearCheckedShoppingItems(active.id));
    }
    await Promise.all(tasks);
    setFlatItems((prev) => prev.filter((i) => !i.checked));
    setActive((prev) =>
      prev
        ? {
            ...prev,
            items: (prev.items || []).filter((i) => !i.is_checked),
          }
        : prev,
    );
  }

  async function clearCompletedShoppingItems(completedItems, actualTotal = "") {
    const completedNames = new Set(
      (completedItems || [])
        .map((item) =>
          String(item.name || "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    );
    if (!completedNames.size) {
      await clearChecked();
      return;
    }

    const flatMatches = flatItems.filter(
      (item) =>
        item.checked ||
        completedNames.has(
          String(item.name || "")
            .trim()
            .toLowerCase(),
        ),
    );
    const structuredMatches = (active?.items || []).filter(
      (item) =>
        item.is_checked ||
        completedNames.has(
          String(item.name || "")
            .trim()
            .toLowerCase(),
        ),
    );
    const remainingFlatItems = flatItems.filter(
      (item) =>
        !item.checked &&
        !completedNames.has(
          String(item.name || "")
            .trim()
            .toLowerCase(),
        ),
    );
    const remainingStructuredItems = (active?.items || []).filter(
      (item) =>
        !item.is_checked &&
        !completedNames.has(
          String(item.name || "")
            .trim()
            .toLowerCase(),
        ),
    );

    const completesEntireList =
      remainingFlatItems.length === 0 && remainingStructuredItems.length === 0;

    // Keep completion bounded to at most one request per backing list. The old
    // per-item DELETE fan-out could leave a normal weekly shop spinning while
    // dozens of concurrent requests competed to update the same list.
    if (active?.id && completesEntireList) {
      await mealsApi.completeShoppingList(active.id, {
        add_checked_to_pantry: false,
        actual_total: actualTotal === "" ? null : actualTotal,
        store_name: shoppingProvider.trim() || null,
        purchased_on: shoppingPurchaseDate || localISODate(),
      });
      if (flatMatches.length) await shoppingApi.clearChecked();
    } else {
      const clearTasks = [];
      if (flatMatches.length) clearTasks.push(shoppingApi.clearChecked());
      if (active?.id && structuredMatches.length)
        clearTasks.push(mealsApi.clearCheckedShoppingItems(active.id));
      await Promise.all(clearTasks);
    }

    setFlatItems(remainingFlatItems);
    setActive((prev) =>
      prev
        ? {
            ...prev,
            status: completesEntireList ? "completed" : prev.status,
            items: remainingStructuredItems,
          }
        : prev,
    );
  }

  function sectionToLocation(section) {
    const map = {
      produce: "fridge",
      dairy: "fridge",
      meat: "fridge",
      frozen: "freezer",
      pantry: "pantry",
      bakery: "pantry",
      canned: "pantry",
      "dry goods": "pantry",
      beverages: "pantry",
      snacks: "pantry",
      other: "pantry",
    };
    return map[section] || "pantry";
  }

  async function promptPantryAdd() {
    const checkedItems = mergedItems
      .filter((i) => i.checked)
      .filter((i) => !["occasion", "pet_food"].includes(i.source))
      .map((i) => {
        const section = i.section || "other";
        const location = sectionToLocation(section);
        const restockId = i.source_pantry_item_id || i.pantry_item_id || null;
        const suggestedExpiry = suggestedShoppingExpiryDate(
          i.name,
          section,
          location,
        );
        const plannedUseDate = shoppingItemFirstUseDate(
          i.for_recipes,
          active?.meal_plan_week_start || mealPlanContext?.week_start,
        );
        return {
          id: i.id,
          _type: i._type,
          _listId: i._listId,
          source_pantry_item_id:
            i.source_pantry_item_id || i.pantry_item_id || null,
          name: i.name,
          quantity: i.quantity || "",
          section,
          selected: true,
          location,
          category: section,
          source: i.source || "shopping",
          expiry_date: restockId
            ? stableRestockExpiryDate(suggestedExpiry)
            : suggestedExpiry,
          planned_use_date: plannedUseDate,
          for_recipes: i.for_recipes || [],
          restock_replaces_item_id: restockId,
        };
      });

    setError("");
    if (!checkedItems.length) {
      await skipPantryAdd();
      return;
    }
    setPantryPromptBusy(false);
    setShoppingActualTotal(active?.actual_total ?? "");
    setShoppingProvider(active?.store_name || "");
    setShoppingPurchaseDate(active?.purchased_on || localISODate());
    setPantryPromptItems(checkedItems);
    setShowPantryPrompt(true);
  }

  async function confirmPantryAdd() {
    const selectedItems = pantryPromptItems.filter((i) => i.selected);
    if (!selectedItems.length) {
      await skipPantryAdd();
      return;
    }
    setPantryPromptBusy(true);
    setError("");
    try {
      const result = await pantryApi.bulkAdd(
        selectedItems.map((item) => ({
          name: item.name,
          category: item.category || "other",
          quantity: item.quantity || "",
          location: item.location || "pantry",
          expiry_date: item.expiry_date || null,
          source: item.source || "shopping",
          restock_replaces_item_id: item.restock_replaces_item_id || null,
        })),
      );
      onPantryAdded?.(result?.items || []);
      await clearCompletedShoppingItems(pantryPromptItems, shoppingActualTotal);
      setShowPantryPrompt(false);
      setPantryPromptItems([]);
      showShoppingAddNotice("Bought items moved to pantry");
    } catch (e) {
      setShowPantryPrompt(true);
      setError(
        e.message ||
          "Could not add items to pantry. Your shopping list was not cleared.",
      );
    } finally {
      setPantryPromptBusy(false);
    }
  }

  async function skipPantryAdd() {
    setPantryPromptBusy(true);
    setShowPantryPrompt(false);
    setPantryPromptItems([]);
    try {
      await clearChecked();
    } catch (e) {
      setError(e.message || "Could not clear checked items.");
    } finally {
      setPantryPromptBusy(false);
    }
  }

  async function exportActiveList() {
    if (!active?.id) return;
    setExportingList(true);
    setError("");
    try {
      const payload = await mealsApi.exportShoppingList(active.id);
      if (payload.instacart_search_url) {
        window.open(
          payload.instacart_search_url,
          "_blank",
          "noopener,noreferrer",
        );
      } else if (payload.plain_text && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload.plain_text);
        showShoppingAddNotice("Shopping list copied");
      } else {
        showShoppingAddNotice("Shopping list ready to export");
      }
    } catch (e) {
      setError(e.message || "Could not export shopping list.");
    } finally {
      setExportingList(false);
    }
  }

  if (loading) return <div className="fd-empty">Loading shopping lists…</div>;

  // Build unified list: merge structured (meal plan) items into flat display
  // Structured items get a "meal plan" tag so user knows where they came from
  const allSections = [
    "produce",
    "dairy",
    "meat",
    "frozen",
    "pantry",
    "bakery",
    "beverages",
    "snacks",
    "pet supplies",
    "other",
  ];
  const sectionAliases = {
    fruit: "produce",
    fruits: "produce",
    vegetable: "produce",
    vegetables: "produce",
    grains: "pantry",
    grain: "pantry",
    dry: "pantry",
    canned: "pantry",
    proteins: "meat",
    protein: "meat",
    seafood: "meat",
    deli: "meat",
  };
  const normalizeSection = (section) => {
    const raw = String(section || "other")
      .trim()
      .toLowerCase();
    return allSections.includes(raw) ? raw : sectionAliases[raw] || "other";
  };

  // Normalise structured items to the same shape as flat items for rendering
  const structuredFlat =
    active?.items?.map((i) => ({
      _type: "structured",
      _listId: active.id,
      id: i.id,
      name: i.name,
      quantity: i.quantity || "",
      section: normalizeSection(i.aisle),
      checked: !!i.is_checked,
      for_recipes: i.for_recipes || [],
      pantry_item_id: i.pantry_item_id || null,
      source_pantry_item_id: i.pantry_item_id || null,
    })) || [];

  // Present one cart row per normalized food identity even when legacy flat
  // and generated meal-plan storage both contain the same item. Keep the
  // backing rows attached so checking a grouped row updates every source.
  const mergedItems = Array.from(
    [
      ...flatItems.map((i) => ({
        _type: "flat",
        ...i,
        section: normalizeSection(i.section),
      })),
      ...structuredFlat,
    ]
      .reduce((groups, item) => {
        const identity = normalizeFoodMatchName(item.name) || String(item.name || "").trim().toLowerCase();
        if (!identity) return groups;
        const existing = groups.get(identity);
        if (!existing) {
          groups.set(identity, { ...item, _members: [item] });
          return groups;
        }
        existing._members.push(item);
        existing.checked = existing._members.every(member => Boolean(member.checked));
        if (!existing.quantity && item.quantity) existing.quantity = item.quantity;
        return groups;
      }, new Map())
      .values(),
  );

  const visibleMergedItems = mergedItems.filter((i) =>
    allSections.includes(i.section || "other"),
  );
  const uncheckedCount = visibleMergedItems.filter((i) => !i.checked).length;
  const checkedCount = visibleMergedItems.length - uncheckedCount;
  const checkedPercent = visibleMergedItems.length
    ? Math.round((checkedCount / visibleMergedItems.length) * 100)
    : 0;
  const activeShoppingWeekRange = active?.meal_plan_week_start
    ? formatDateRangeFromIso(active.meal_plan_week_start)
    : "";
  const activeShoppingContextLabel = activeShoppingWeekRange
    ? `For ${activeShoppingWeekRange} meal plan`
    : "Manual and meal-plan items";
  function isRecentlyMovedFromShopping(item) {
    if (
      String(item?.source || "")
        .trim()
        .toLowerCase() !== "shopping"
    )
      return false;
    if (!item?.created_at) return true;
    const created = new Date(item.created_at);
    if (Number.isNaN(created.getTime())) return true;
    return Date.now() - created.getTime() < 1000 * 60 * 60 * 24;
  }
  const lowPromptItems = Array.from(
    pantry
      .filter((i) => i.level !== "stable")
      .filter((i) => !isRecentlyMovedFromShopping(i))
      .filter((i) => !mergedItems.some(item => (
        normalizeFoodMatchName(item.name) === normalizeFoodMatchName(i.name)
      )))
      .filter(
        (i) =>
          !dismissedLowPromptKeys.has(
            `${i.id || i.name}:${i.expiry_date || ""}`,
          ),
      )
      .reduce((map, item) => {
        const key = String(item.name || "")
          .trim()
          .toLowerCase();
        if (!key) return map;
        const existing = map.get(key);
        if (!existing) {
          map.set(key, item);
          return map;
        }
        const existingDate = existing.expiry_date || "9999-12-31";
        const itemDate = item.expiry_date || "9999-12-31";
        if (itemDate < existingDate) map.set(key, item);
        return map;
      }, new Map())
      .values(),
  );
  const lowCount = lowPromptItems.length;
  const pantryIsEmpty =
    pantry.filter((item) => String(item?.name || "").trim()).length === 0;
  return (
    <div className="fd-tab-content">
      {showShoppingWorking && (
        <SavingNotice
          title={
            removingItemKey
              ? "Removing item"
              : pantryPromptBusy
                ? "Updating shopping list"
                : exportingList
                  ? "Preparing export"
                  : "Adding item"
          }
          message={
            removingItemKey
              ? "Updating shopping list..."
              : pantryPromptBusy
                ? "Moving bought items..."
                : exportingList
                  ? "Grouping list by aisle..."
                  : "Adding item..."
          }
        />
      )}
      {error && (
        <div className="fd-inline-warning" style={{ marginBottom: "8px" }}>
          {error}
        </div>
      )}
      {actionItem && (
        <CenteredModal title="" onClose={() => setActionItem(null)} width={320}>
          <div className="fd-shop-action-modal">
            <SheetLogoRow
              title={actionItem.name}
              onClose={() => setActionItem(null)}
              closeLabel="Close shopping item actions"
            />
            <div className="fd-shop-action-sub">
              {[
                actionItem.quantity,
                actionItem._type === "structured" ? "AI suggested" : null,
              ]
                .filter(Boolean)
                .join(" · ") || "Shopping item"}
            </div>
            <label>
              <span>Quantity</span>
              <input
                className="hm-input"
                value={actionDraft.quantity}
                placeholder="e.g. 250 g"
                onChange={(event) =>
                  setActionDraft((draft) => ({
                    ...draft,
                    quantity: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span>Category</span>
              <select
                className="hm-select"
                value={actionDraft.section}
                onChange={(event) =>
                  setActionDraft((draft) => ({
                    ...draft,
                    section: event.target.value,
                  }))
                }
              >
                {allSections.map((section) => (
                  <option key={section} value={section}>
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="fd-shop-action-edit"
              onClick={saveShoppingItemEdit}
            >
              Edit quantity / category
            </button>
            <button
              type="button"
              className="fd-shop-action-remove"
              onClick={() => removeShoppingItem(actionItem)}
              disabled={Boolean(removingItemKey)}
            >
              Remove from list
            </button>
          </div>
        </CenteredModal>
      )}
      {showPantryPrompt && (
        <FoodMobileSheet
          title="Add to pantry?"
          label="Add bought items to pantry"
          onClose={() => setShowPantryPrompt(false)}
          disableBackdropClose
          dragHandleOnly
        >
          <div
            className={`fd-clear-sheet-content fd-pantry-prompt${pantryPromptItems.length > 5 ? " fd-pantry-prompt--many" : ""}`}
          >
            <div className="fd-clear-sheet__head">
              <div className="fd-clear-sheet__warn fd-pantry-prompt__warn">
                🛒 Just bought
              </div>
              <div className="fd-clear-sheet__sub">
                Select what should move into Pantry. Unselect anything already
                at home.
              </div>
            </div>
            <div className="fd-shopping-checkout-fields">
              <label><span>Purchased from</span><input list="hm-shopping-provider-options" value={shoppingProvider} onChange={(event) => setShoppingProvider(event.target.value)} placeholder="Store or delivery service" /></label>
              <datalist id="hm-shopping-provider-options"><option value="DoorDash" /><option value="Instacart" /><option value="Walmart Grocery" /><option value="Amazon Fresh" /><option value="Uber Eats" /><option value="Store pickup" /><option value="In store" /></datalist>
              <label><span>Purchase date</span><input type="date" value={shoppingPurchaseDate} onChange={(event) => setShoppingPurchaseDate(event.target.value)} /></label>
              <label><span>Total paid <small>Optional · adds confirmed grocery spending to Money</small></span><div><b>$</b><input type="number" min="0" step="0.01" inputMode="decimal" value={shoppingActualTotal} onChange={(event) => setShoppingActualTotal(event.target.value)} placeholder="0.00" /></div></label>
            </div>
            <div className="fd-pantry-prompt__list">
              {pantryPromptItems.map((item, idx) => {
                const expiryWarning = shoppingExpiryPlanWarning(item);
                return (
                  <div
                    key={`${item._type}-${item.id}`}
                    className={`fd-pantry-prompt__item${item.selected ? " fd-pantry-prompt__item--on" : ""}${expiryWarning ? " fd-pantry-prompt__item--warn" : ""}`}
                  >
                    <button
                      type="button"
                      className="fd-pantry-prompt__check"
                      onClick={() =>
                        setPantryPromptItems((prev) =>
                          prev.map((p, i) =>
                            i === idx ? { ...p, selected: !p.selected } : p,
                          ),
                        )
                      }
                      aria-label={
                        item.selected
                          ? `Skip ${item.name}`
                          : `Add ${item.name} to pantry`
                      }
                    >
                      {item.selected ? "✓" : ""}
                    </button>
                    <div className="fd-pantry-prompt__fields">
                      <span className="fd-pantry-prompt__name">
                        {item.name}
                      </span>
                      <div className="fd-pantry-prompt__grid">
                        <input
                          className="hm-input"
                          value={item.quantity || ""}
                          placeholder="Qty"
                          onChange={(e) =>
                            setPantryPromptItems((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, quantity: e.target.value }
                                  : p,
                              ),
                            )
                          }
                        />
                        <select
                          className="hm-select fd-pantry-prompt__loc-select"
                          value={item.location || "pantry"}
                          onChange={(e) =>
                            setPantryPromptItems((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, location: e.target.value }
                                  : p,
                              ),
                            )
                          }
                        >
                          {LOCATION_OPTS.map((loc) => (
                            <option key={loc} value={loc}>
                              {loc.charAt(0).toUpperCase() + loc.slice(1)}
                            </option>
                          ))}
                        </select>
                        <input
                          className={`hm-input fd-pantry-prompt__date${item.expiry_date ? " has-value" : ""}`}
                          type="date"
                          value={item.expiry_date || ""}
                          data-placeholder="Expires"
                          onChange={(e) =>
                            setPantryPromptItems((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, expiry_date: e.target.value }
                                  : p,
                              ),
                            )
                          }
                        />
                      </div>
                      {expiryWarning && (
                        <div className="fd-pantry-prompt__expiry-warning">
                          {expiryWarning}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="fd-clear-sheet__foot">
              <button
                type="button"
                className="fd-clear-cancel"
                onClick={skipPantryAdd}
                disabled={pantryPromptBusy}
              >
                Skip
              </button>
              <button
                type="button"
                className="hm-btn hm-btn--primary fd-clear-confirm"
                disabled={
                  pantryPromptBusy || !pantryPromptItems.some((i) => i.selected)
                }
                onClick={confirmPantryAdd}
              >
                Add {pantryPromptItems.filter((i) => i.selected).length} to
                pantry ✓
              </button>
            </div>
          </div>
        </FoodMobileSheet>
      )}

      {/* ── Running low / expiring ── */}
      {lowCount > 0 && (
        <div className="fd-shopping-section">
          <div className="fd-shopping-header">
            <h3>⚠️ Running low / expiring</h3>
            <span className="fd-muted" style={{ fontSize: 12 }}>
              Pantry reminders · not yet in your cart
            </span>
          </div>
          <div className="fd-low-chips">
            {lowPromptItems.map((item) => {
              return (
                <button
                  key={item.id}
                  className="fd-low-chip"
                  onClick={async () => {
                    setAddingFlat(true);
                    try {
                      const added = await addShoppingItem({
                        name: item.name,
                        section: item.category || "other",
                        quantity: "",
                        sourcePantryItemId: item.id,
                      });
                      if (added?._type === "structured") {
                        setActive((prev) =>
                          prev
                            ? { ...prev, items: [added, ...(prev.items || [])] }
                            : prev,
                        );
                      } else if (added) {
                        setFlatItems((prev) => [added, ...prev]);
                      }
                    } finally {
                      setAddingFlat(false);
                    }
                  }}
                >
                  <span className="fd-low-chip-plus">+</span>
                  {item.name}
                  <span
                    className={`fd-badge fd-badge--${item.level}`}
                    style={{ marginLeft: 4, fontSize: 10 }}
                  >
                    {item.level === "urgent" ? "urgent" : "soon"}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="fd-low-chip-dismiss"
                    aria-label={`Dismiss ${item.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissLowPrompt(item);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        dismissLowPrompt(item);
                      }
                    }}
                  >
                    ×
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Unified shopping list ── */}
      <div className="fd-shopping-section">
        <div className="fd-shopping-header">
          <div>
            <h3>
              🛒 Shopping list
              {visibleMergedItems.length > 0 && (
                <span className="fd-shop-count-badge">
                  {visibleMergedItems.length}
                </span>
              )}
            </h3>
            <span className="fd-shop-list-sub">
              {activeShoppingContextLabel} · {visibleMergedItems.length} item
              {visibleMergedItems.length !== 1 ? "s" : ""} · {checkedCount}{" "}
              checked
            </span>
          </div>
          <div className="fd-shopping-header__actions">
            <span className="fd-muted" style={{ fontSize: 12 }}>
              {uncheckedCount} item{uncheckedCount !== 1 ? "s" : ""} left
            </span>
            {canShare && active?.id && (
              <button
                className="hm-btn hm-btn--ghost"
                style={{ fontSize: "12px" }}
                onClick={() =>
                  onShare?.(
                    "shopping_list",
                    active.id,
                    active.store_name || "Shopping list",
                  )
                }
                disabled={sharedResourceKeys?.has(`shopping_list:${active.id}`)}
              >
                {sharedResourceKeys?.has(`shopping_list:${active.id}`)
                  ? "Shared"
                  : "Share"}
              </button>
            )}
            {active?.id && visibleMergedItems.length > 0 && (
              <button
                className="hm-btn hm-btn--ghost"
                style={{ fontSize: "12px" }}
                onClick={exportActiveList}
                disabled={exportingList}
              >
                Export
              </button>
            )}
            <button
              className="hm-btn hm-btn--primary fd-shopping-add-btn"
              type="button"
              onClick={() => setShowAddForm((open) => !open)}
            >
              {showAddForm ? "Cancel" : "+ Add"}
            </button>
          </div>
        </div>

        {pantryIsEmpty && visibleMergedItems.length > 0 && (
          <div className="fd-shopping-plan-note">
            <strong>This plan starts with shopping.</strong>
            <span>
              Check off what you buy, then use Finish shopping to add those
              groceries into Pantry.
            </span>
          </div>
        )}

        <div className="fd-shop-progress">
          <div className="fd-shop-progress-bar">
            <span style={{ width: `${checkedPercent}%` }} />
          </div>
          <div className="fd-shop-progress-label">
            <span>
              {checkedCount} of {visibleMergedItems.length} checked
            </span>
            <span>{checkedPercent}%</span>
          </div>
        </div>
        {!shoppingHintDismissed && visibleMergedItems.length > 0 && (
          <div className="fd-shop-hint">
            <span>Long-press any item to edit or remove.</span>
            <button
              type="button"
              onClick={dismissShoppingHint}
              aria-label="Dismiss shopping hint"
            >
              ×
            </button>
          </div>
        )}
        {checkedCount > 0 && (
          <button
            className="fd-shopping-finish-btn"
            type="button"
            onClick={promptPantryAdd}
          >
            {uncheckedCount === 0
              ? "Finish shopping"
              : `Review ${checkedCount} checked item${checkedCount === 1 ? "" : "s"}`}
          </button>
        )}

        {showAddForm &&
          (() => {
            const form = (
              <form
                onSubmit={addFlat}
                className="fd-add-form fd-shopping-add-form"
              >
                <div className="fd-add-form-head">
                  <button
                    type="button"
                    className="fd-inline-close fd-inline-close--panel"
                    onClick={closeShoppingAdd}
                    aria-label="Close add item panel"
                  >
                    ×
                  </button>
                </div>

                <div className="fd-single-form">
                  <div className="fd-sf-field">
                    <label className="fd-sf-label">Item name *</label>
                    <ItemComboBox
                      value={addName}
                      onChange={(value) => {
                        setAddName(value);
                        const sugCat = autoFillCat(value);
                        if (sugCat) setAddSection(normalizeSection(sugCat));
                      }}
                      onSelect={(item) => {
                        setAddName(item.name);
                        setAddSection(
                          normalizeSection(
                            item.category || item.location || "other",
                          ),
                        );
                        if (item.unit) setAddUnit(item.unit);
                      }}
                      placeholder="e.g. Whole Milk"
                    />
                  </div>
                  <div className="fd-sf-field">
                    <label className="fd-sf-label">Category</label>
                    <select
                      className="hm-select"
                      value={addSection}
                      onChange={(e) => setAddSection(e.target.value)}
                    >
                      {allSections.map((section) => (
                        <option key={section} value={section}>
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fd-sf-field">
                    <label className="fd-sf-label">
                      Quantity <span className="fd-sf-opt">(optional)</span>
                    </label>
                    <div className="fd-sf-row">
                      <div>
                        <span className="fd-sf-sub">Amount</span>
                        <input
                          className="hm-input"
                          type="number"
                          inputMode="decimal"
                          placeholder="1"
                          value={addQty}
                          onChange={(e) => setAddQty(e.target.value)}
                        />
                      </div>
                      <div>
                        <span className="fd-sf-sub">Unit</span>
                        <select
                          className="hm-select"
                          value={addUnit}
                          onChange={(e) => setAddUnit(e.target.value)}
                        >
                          <option value="">units</option>
                          {[
                            "g",
                            "kg",
                            "ml",
                            "L",
                            "cup",
                            "piece",
                            "pack",
                            "bunch",
                            "lb",
                            "oz",
                            "bag",
                            "jar",
                          ].map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {addNotice && <div className="fd-add-notice">{addNotice}</div>}
                <div className="fd-add-footer">
                  <button
                    type="button"
                    className="fd-add-cancel"
                    onClick={closeShoppingAdd}
                  >
                    Cancel
                  </button>
                  <button
                    className="hm-btn hm-btn--primary fd-add-save"
                    type="submit"
                    disabled={addingFlat || !addName.trim()}
                  >
                    {addingFlat ? "Adding..." : "+ Add item"}
                  </button>
                </div>
              </form>
            );
            return presentation === "modal" ? (
              form
            ) : (
              <FoodMobileSheet
                title="Add shopping item"
                label="Add shopping item"
                onClose={closeShoppingAdd}
                disableBackdropClose
                dragHandleOnly
              >
                {form}
              </FoodMobileSheet>
            );
          })()}

        {visibleMergedItems.length === 0 ? (
          <div className="fd-empty">
            Your list is empty — add items above or generate a meal plan to get
            AI suggestions.
          </div>
        ) : (
          <div className="fd-flat-list">
            {allSections.map((section) => {
              const sectionItems = visibleMergedItems
                .filter((i) => (i.section || "other") === section)
                .sort((a, b) => Number(a.checked) - Number(b.checked));
              if (!sectionItems.length) return null;
              return (
                <div key={section} className="fd-aisle">
                  <div className="fd-aisle-label">
                    <span>
                      {section.charAt(0).toUpperCase() + section.slice(1)}
                    </span>
                    <em>{sectionItems.length}</em>
                    <i aria-hidden="true" />
                    <button
                      type="button"
                      className="fd-aisle-select-all"
                      onClick={() => toggleShoppingSection(section, sectionItems)}
                      disabled={Boolean(selectingSection)}
                      aria-label={`${sectionItems.every((item) => item.checked) ? "Unselect" : "Select"} all ${section} items`}
                    >
                      {selectingSection === section
                        ? "…"
                        : sectionItems.every((item) => item.checked)
                          ? "Clear"
                          : "All"}
                    </button>
                  </div>
                  <div className="fd-shop-group-card">
                    {sectionItems.map((item) => (
                      <label
                        key={`${item._type}-${item.id}`}
                        className={`fd-shop-item${item.checked ? " fd-shop-item--checked" : ""}${item._type === "structured" ? " fd-shop-item--ai" : ""}${actionItem && actionItem._type === item._type && actionItem.id === item.id ? " fd-shop-item--action" : ""}`}
                        title="Long-press to remove"
                        onContextMenu={(event) => {
                          event.preventDefault();
                          openShoppingItemActions(item);
                        }}
                        onPointerDown={() => startShoppingLongPress(item)}
                        onPointerUp={clearShoppingLongPress}
                        onPointerLeave={clearShoppingLongPress}
                        onPointerCancel={clearShoppingLongPress}
                        onClickCapture={(event) => {
                          if (!shoppingSuppressClickRef.current) return;
                          event.preventDefault();
                          shoppingSuppressClickRef.current = false;
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!item.checked}
                          onChange={(e) => {
                            toggleShoppingGroup(item, e.target.checked);
                          }}
                        />
                        <span className="fd-shop-body">
                          <span className="fd-shop-name">{item.name}</span>
                        </span>
                        <span className="fd-shop-right">
                          {item.quantity && (
                            <span className="fd-shop-qty">{item.quantity}</span>
                          )}
                          {item._type === "structured" && (
                            <span
                              className="fd-shop-ai-dot"
                              title="AI suggested"
                              aria-label="AI suggested"
                            />
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Grocery delivery & meal kit affiliate links ── */}
      <FoodDeliveryLogCard />
      <FoodAffiliateCard />
    </div>
  );
}

function FoodDeliveryLogCard() {
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState({
    provider: "DoorDash",
    order_date: new Date().toISOString().slice(0, 10),
    amount: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  async function refresh() {
    try {
      setPayload(await mealsApi.deliveryLogs(30));
    } catch (err) {
      setError(err.message || "Could not load delivery logs.");
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function save(e) {
    e.preventDefault();
    if (!form.provider.trim()) return;
    setBusy(true);
    setError("");
    try {
      await mealsApi.addDeliveryLog({
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
      });
      setForm((current) => ({
        ...current,
        amount: "",
        notes: "",
        order_date: new Date().toISOString().slice(0, 10),
      }));
      await refresh();
    } catch (err) {
      setError(err.message || "Could not save delivery order.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    setError("");
    try {
      await mealsApi.removeDeliveryLog(id);
      await refresh();
    } catch (err) {
      setError(err.message || "Could not remove delivery order.");
    }
  }

  const logs = payload?.logs || [];
  const summary = payload?.summary || { count: 0, total: 0 };

  return (
    <div
      className={`fd-shopping-section fd-delivery-log-section${open ? " is-open" : ""}`}
      style={{ marginTop: 20 }}
    >
      <button
        type="button"
        className="fd-delivery-drawer-toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span>
          <strong>Takeout & other deliveries</strong>
          <small>
            {summary.count || 0} in 30 days · $
            {Number(summary.total || 0).toFixed(2)}
          </small>
        </span>
        <span className="fd-delivery-drawer-chevron" aria-hidden="true">
          ⌄
        </span>
      </button>
      {open && (
        <div className="fd-delivery-drawer-body">
          <form onSubmit={save} className="fd-add-form fd-shopping-add-form">
            <input
              className="hm-auth-input"
              value={form.provider}
              onChange={(e) =>
                setForm((f) => ({ ...f, provider: e.target.value }))
              }
              placeholder="DoorDash, Uber Eats..."
            />
            <input
              className="hm-auth-input"
              type="date"
              value={form.order_date}
              onChange={(e) =>
                setForm((f) => ({ ...f, order_date: e.target.value }))
              }
            />
            <input
              className="hm-auth-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              placeholder="Amount"
            />
            <button
              className="hm-btn hm-btn--primary"
              type="submit"
              disabled={busy}
            >
              {busy ? "Saving..." : "Log order"}
            </button>
          </form>
          {error && <div className="hm-inline-error">{error}</div>}
          {logs.length > 0 && (
            <div className="fd-delivery-log-list">
              {logs.slice(0, 5).map((log) => (
                <div key={log.id} className="fd-delivery-log-row">
                  <span>
                    <strong>{log.provider}</strong>
                    <small>{log.order_date}</small>
                  </span>
                  <em>
                    {log.amount != null
                      ? `$${Number(log.amount).toFixed(2)}`
                      : "Logged"}
                  </em>
                  <button
                    type="button"
                    onClick={() => remove(log.id)}
                    aria-label={`Remove ${log.provider} order`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FoodAffiliateCard() {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    affiliateApi
      .foodLinks()
      .then(setLinks)
      .catch(() => {});
  }, []);

  if (!links) return null;

  const options = [
    {
      key: "instacart",
      label: "Instacart",
      url: links.instacart,
      icon: "🛒",
      desc: "Same-day grocery delivery",
    },
    {
      key: "hellofresh",
      label: "HelloFresh",
      url: links.hellofresh,
      icon: "🥗",
      desc: "Meal kits, delivered weekly",
    },
    {
      key: "everyplate",
      label: "EveryPlate",
      url: links.everyplate,
      icon: "🍽️",
      desc: "Budget-friendly meal kits",
    },
    {
      key: "walmart",
      label: "Walmart Grocery",
      url: links.walmart,
      icon: "🏬",
      desc: "Pickup & delivery",
    },
    {
      key: "thrive",
      label: "Thrive Market",
      url: links.thrive,
      icon: "🌿",
      desc: "Organic & natural foods",
    },
  ].filter((o) => o.url);

  if (options.length === 0) return null;

  return (
    <div
      className="fd-shopping-section fd-delivery-section"
      style={{ marginTop: 20 }}
    >
      <div className="fd-shopping-header">
        <h3>🚚 Order grocery delivery</h3>
      </div>
      <p className="fd-delivery-sub">
        Get your list delivered. Tap any service to order.
      </p>
      <div className="fd-delivery-grid">
        {options.map((opt) => (
          <a
            key={opt.key}
            href={opt.url}
            target="_blank"
            rel="noopener noreferrer"
            className="fd-delivery-card"
            title={opt.desc}
            onClick={() =>
              affiliateApi.track(opt.key, "food", opt.url).catch(() => {})
            }
          >
            <span className="fd-delivery-name">
              <span>{opt.icon}</span>
              {opt.label}
            </span>
            <span className="fd-delivery-arrow">↗</span>
          </a>
        ))}
      </div>
      <p className="affiliate-disclosure" style={{ marginTop: 10 }}>
        Some links may be affiliate links. HouseMind may earn a small commission
        at no extra cost to you.
      </p>
    </div>
  );
}

// ── Preferences Tab ────────────────────────────────────────────────────────
function PreferencesTab({ onSaved }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openCard, setOpenCard] = useState("allergies");
  const [newAllergy, setNewAllergy] = useState("");
  const [newDislike, setNewDislike] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const p = await mealsApi.preferences();
        setPrefs(p);
        setSaved(Boolean(p?.has_preferences));
      } catch {
        setPrefs({
          has_preferences: false,
          dietary_type: "omnivore",
          allergies: [],
          dislikes: [],
          favorite_cuisines: [],
          cooking_skill: "intermediate",
          weekly_cook_time_minutes: 300,
          servings_default: 4,
        });
      }
      setLoading(false);
    })();
  }, []);

  function editPrefs(updater) {
    setSaved(false);
    setPrefs(updater);
  }

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const [updated] = await Promise.all([
        mealsApi.savePreferences(prefs),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
      setPrefs(updated);
      setSaved(true);
      if (onSaved) onSaved(updated);
    } finally {
      setSaving(false);
    }
  }

  function addToList(field, val, clear) {
    const clean = String(val || "").trim();
    if (!clean) return;
    editPrefs((p) =>
      (p[field] || []).includes(clean)
        ? p
        : { ...p, [field]: [...(p[field] || []), clean] },
    );
    clear?.("");
  }
  function removeFromList(field, val) {
    editPrefs((p) => ({
      ...p,
      [field]: (p[field] || []).filter((x) => x !== val),
    }));
  }

  function toggleListValue(field, val) {
    const active = (prefs[field] || []).includes(val);
    active ? removeFromList(field, val) : addToList(field, val, () => {});
  }

  if (loading || !prefs)
    return <div className="fd-empty">Loading preferences…</div>;

  const allergies = prefs.allergies || [];
  const dislikes = prefs.dislikes || [];
  const cuisines = prefs.favorite_cuisines || [];
  const cuisineRows = [];
  for (let i = 0; i < CUISINE_OPTS.length; i += 3)
    cuisineRows.push(CUISINE_OPTS.slice(i, i + 3));
  const prefCards = [
    {
      id: "diet",
      icon: "🥗",
      title: "Diet & cooking",
      summary: `${prefs.dietary_type || "Omnivore"} · ${prefs.cooking_skill || "Intermediate"} · ${prefs.weekly_cook_time_minutes || 0} min/week`,
    },
    {
      id: "allergies",
      icon: "⚠️",
      title: "Allergies",
      badge: "Hard filter",
      summary: allergies.length
        ? `${allergies.length} added`
        : "None added · tap to add",
    },
    {
      id: "dislikes",
      icon: "👎",
      title: "Dislikes",
      summary: dislikes.length
        ? `${dislikes.slice(0, 2).join(", ")}${dislikes.length > 2 ? ` · ${dislikes.length} items` : ""}`
        : "None added",
    },
    {
      id: "cuisines",
      icon: "🌎",
      title: "Favourite cuisines",
      summary: cuisines.length
        ? `${cuisines.slice(0, 2).join(", ")}${cuisines.length > 2 ? ` · ${cuisines.length} selected` : ""}`
        : "No favourites selected",
    },
  ];

  function PrefCard({ card, children }) {
    const open = openCard === card.id;
    return (
      <section className={`fd-pref-accordion${open ? " is-open" : ""}`}>
        <button
          className="fd-pref-accordion__head"
          type="button"
          onClick={() => setOpenCard(open ? "" : card.id)}
          aria-expanded={open}
        >
          <span className="fd-pref-accordion__icon">{card.icon}</span>
          <span className="fd-pref-accordion__copy">
            <span className="fd-pref-accordion__title">
              {card.title}
              {card.badge && (
                <span className="fd-pref-hard-badge">{card.badge}</span>
              )}
            </span>
            <span className="fd-pref-accordion__summary">{card.summary}</span>
          </span>
          <span className="fd-pref-accordion__chev">{open ? "⌄" : "›"}</span>
        </button>
        {open && <div className="fd-pref-accordion__body">{children}</div>}
      </section>
    );
  }

  return (
    <div className="fd-tab-content fd-preferences-tab">
      {saving && (
        <SavingNotice
          title="Saving preferences"
          message="Saving household food preferences..."
        />
      )}
      <div className="fd-pref-stack">
        <PrefCard card={prefCards[0]}>
          <div className="fd-pref-editor">
            <div className="fd-sf-field">
              <label className="fd-sf-label">Dietary type</label>
              <select
                className="hm-select"
                value={prefs.dietary_type}
                onChange={(e) =>
                  editPrefs((p) => ({ ...p, dietary_type: e.target.value }))
                }
              >
                {DIETARY_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-sf-field">
              <label className="fd-sf-label">Cooking skill</label>
              <div className="fd-skill-pills">
                {SKILL_OPTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`fd-pill${prefs.cooking_skill === s ? " fd-pill--active" : ""}`}
                    onClick={() =>
                      editPrefs((p) => ({ ...p, cooking_skill: s }))
                    }
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="fd-pref-number-grid">
              <label className="fd-pref-number">
                <span>Cook time</span>
                <div className="fd-input-suffix">
                  <input
                    className="hm-input"
                    type="number"
                    min={30}
                    max={1200}
                    step={30}
                    value={prefs.weekly_cook_time_minutes}
                    onChange={(e) =>
                      editPrefs((p) => ({
                        ...p,
                        weekly_cook_time_minutes: Number(e.target.value),
                      }))
                    }
                  />
                  <em>min/week</em>
                </div>
              </label>
              <label className="fd-pref-number">
                <span>Servings</span>
                <input
                  className="hm-input"
                  type="number"
                  min={1}
                  max={20}
                  value={prefs.servings_default}
                  onChange={(e) =>
                    editPrefs((p) => ({
                      ...p,
                      servings_default: Number(e.target.value),
                    }))
                  }
                />
              </label>
            </div>
          </div>
        </PrefCard>

        <PrefCard card={prefCards[1]}>
          <div className="fd-pref-hard-note">
            🛡️ These are blocked server-side. AI will never suggest a recipe
            containing these ingredients.
          </div>
          {allergies.length > 0 && (
            <div className="fd-pref-active-row">
              {allergies.map((a) => (
                <span key={a} className="fd-tag fd-tag--red">
                  {a}{" "}
                  <button
                    type="button"
                    onClick={() => removeFromList("allergies", a)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="fd-pref-section-label">Quick-add common allergens</p>
          <div className="fd-quickpick-row">
            {COMMON_ALLERGENS.map((a) => {
              const already = allergies.includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  className={`fd-quickpick-chip${already ? " fd-quickpick-chip--on" : ""}`}
                  disabled={already}
                  onClick={() => addToList("allergies", a, () => {})}
                >
                  {already ? "✓ " : "+ "}
                  {a}
                </button>
              );
            })}
          </div>
          <div className="fd-pref-add-row">
            <input
              className="hm-input"
              placeholder="Type custom allergen..."
              value={newAllergy}
              onChange={(e) => setNewAllergy(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addToList("allergies", newAllergy, setNewAllergy);
                }
              }}
            />
            <button
              className="hm-btn hm-btn--primary"
              type="button"
              onClick={() => addToList("allergies", newAllergy, setNewAllergy)}
            >
              Add
            </button>
          </div>
        </PrefCard>

        <PrefCard card={prefCards[2]}>
          {dislikes.length > 0 && (
            <div className="fd-pref-active-row">
              {dislikes.map((d) => (
                <span key={d} className="fd-tag fd-tag--amber">
                  {d}{" "}
                  <button
                    type="button"
                    onClick={() => removeFromList("dislikes", d)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="fd-pref-section-label">Quick-add common dislikes</p>
          <div className="fd-quickpick-row">
            {COMMON_DISLIKES.map((d) => {
              const already = dislikes.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  className={`fd-quickpick-chip${already ? " fd-quickpick-chip--on" : ""}`}
                  disabled={already}
                  onClick={() => addToList("dislikes", d, () => {})}
                >
                  {already ? "✓ " : "+ "}
                  {d}
                </button>
              );
            })}
          </div>
          <div className="fd-pref-add-row">
            <input
              className="hm-input"
              placeholder="Type custom dislike..."
              value={newDislike}
              onChange={(e) => setNewDislike(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addToList("dislikes", newDislike, setNewDislike);
                }
              }}
            />
            <button
              className="hm-btn hm-btn--primary"
              type="button"
              onClick={() => addToList("dislikes", newDislike, setNewDislike)}
            >
              Add
            </button>
          </div>
        </PrefCard>

        <PrefCard card={prefCards[3]}>
          <div className="fd-cuisine-scroll">
            {cuisineRows.map((row, index) => (
              <div key={index} className="fd-cuisine-row">
                {row.map((c) => {
                  const active = cuisines.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`fd-pill${active ? " fd-pill--active" : ""}`}
                      onClick={() => toggleListValue("favorite_cuisines", c)}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </PrefCard>
      </div>

      <div className={`fd-pref-save-row${saved ? " is-saved" : ""}`}>
        <button
          className="hm-btn hm-btn--primary"
          onClick={save}
          disabled={saving}
        >
          {saving
            ? "Saving..."
            : saved
              ? "✓ Preferences saved"
              : "Save preferences"}
        </button>
      </div>
    </div>
  );
}

// ── Scan Modal ─────────────────────────────────────────────────────────────
function ScanModal({ onClose, onSaved, onAiUsage }) {
  const presentation = useFormPresentation();
  const [scanType, setScanType] = useState("receipt");
  const [scanning, setScanning] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [aiWarning, setAiWarning] = useState(false);
  const [scanConsent, setScanConsent] = useState(false);
  const [scanError, setScanError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pets, setPets] = useState([]);
  const [petFoodSupplies, setPetFoodSupplies] = useState([]);
  const showScanning = useMinLoad(scanning, 700);
  const showSaving = useMinLoad(saving, 700);
  const scanUploadRef = useRef(null);
  const scanCameraRef = useRef(null);
  useEffect(() => {
    let active = true;
    petsApi.snapshot().then((payload) => { if (active) { setPets(payload.pets || []); setPetFoodSupplies(payload.pet_food_supplies || []); } }).catch(() => { if (active) { setPets([]); setPetFoodSupplies([]); } });
    return () => { active = false; };
  }, []);

  const SCAN_SAMPLE = [
    {
      name: "Whole milk",
      category: "dairy",
      quantity: "1",
      unit: "gallon",
      expiry_hint: "~7 days",
    },
    {
      name: "Chicken breast",
      category: "meat",
      quantity: "2",
      unit: "lbs",
      expiry_hint: "~2 days",
    },
    {
      name: "Spinach",
      category: "produce",
      quantity: "1",
      unit: "bag",
      expiry_hint: "~5 days",
    },
    {
      name: "Greek yogurt",
      category: "dairy",
      quantity: "2",
      unit: "cups",
      expiry_hint: "~10 days",
    },
  ];

  function normaliseScannedItem(item, idx) {
    const location = item.location || item.section || "fridge";
    const destination = item.destination === "pet_food" ? "pet_food" : "human_pantry";
    const pet_id = destination === "pet_food" && pets.length === 1 ? String(pets[0].id) : "";
    const key = String(item.name || "").trim().toLowerCase();
    const match = petFoodSupplies.find((supply) => String(supply.pet_id) === pet_id && String(supply.name || "").trim().toLowerCase() === key);
    return { ...item, id: idx, location, destination, pet_id, pet_food_supply_id: match ? String(match.id) : "", confirmed: true };
  }

  useEffect(() => {
    setCandidates((current) => current?.map((item) => {
      if (item.destination !== "pet_food") return item;
      const pet_id = item.pet_id || (pets.length === 1 ? String(pets[0].id) : "");
      const key = String(item.name || "").trim().toLowerCase();
      const match = petFoodSupplies.find((supply) => String(supply.pet_id) === String(pet_id) && String(supply.name || "").trim().toLowerCase() === key);
      return { ...item, pet_id, pet_food_supply_id: item.pet_food_supply_id || (match ? String(match.id) : "") };
    }) || current);
  }, [pets, petFoodSupplies]);

  async function handleFile(file, mode = scanType) {
    if (!file) return;
    if (!scanConsent) {
      setScanError("Confirm the scan notice before uploading a photo.");
      return;
    }
    setScanType(mode);
    setScanning(true);
    setCandidates(null);
    setScanError("");
    try {
      const res = await pantryApi.scan(file, mode);
      onAiUsage?.(res.ai_usage);
      if (!res.ai_configured) {
        setAiWarning(true);
        setCandidates(SCAN_SAMPLE.map(normaliseScannedItem));
      } else {
        setAiWarning(false);
        setCandidates((res.items || []).map(normaliseScannedItem));
      }
    } catch (e) {
      setScanError(e.message || "Could not scan this photo.");
    }
    setScanning(false);
  }

  function chooseScanType(nextType) {
    setScanType(nextType);
    setCandidates(null);
    setScanError("");
    setAiWarning(false);
  }

  function toggle(id) {
    setCandidates((prev) =>
      prev.map((i) => (i.id === id ? { ...i, confirmed: !i.confirmed } : i)),
    );
  }
  function update(id, field, val) {
    setCandidates((prev) =>
      prev.map((i) => (i.id === id ? { ...i, [field]: val } : i)),
    );
  }
  function assignPet(id, petId) {
    setCandidates((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      const key = String(item.name || "").trim().toLowerCase();
      const match = petFoodSupplies.find((supply) => String(supply.pet_id) === String(petId) && String(supply.name || "").trim().toLowerCase() === key);
      return { ...item, pet_id: petId, pet_food_supply_id: match ? String(match.id) : "" };
    }));
  }
  function setDestination(id, destination) {
    setCandidates((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      if (destination !== "pet_food") return { ...item, destination, pet_id: "", pet_food_supply_id: "" };
      const petId = item.pet_id || (pets.length === 1 ? String(pets[0].id) : "");
      const key = String(item.name || "").trim().toLowerCase();
      const match = petFoodSupplies.find((supply) => String(supply.pet_id) === petId && String(supply.name || "").trim().toLowerCase() === key);
      return { ...item, destination, pet_id: petId, pet_food_supply_id: match ? String(match.id) : "" };
    }));
  }
  function removeCandidate(id) {
    setCandidates((prev) => prev.filter((i) => i.id !== id));
  }

  async function saveConfirmed() {
    const toSave = candidates.filter((i) => i.confirmed);
    if (!toSave.length) return;
    const unassigned = toSave.find((i) => i.destination === "pet_food" && !i.pet_id);
    if (unassigned) {
      setScanError(`Choose a pet for ${unassigned.name}, or route it to Food Pantry.`);
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        toSave.map((i) => {
          if (i.destination === "pet_food") {
            const quantityMatch = String(i.quantity || "1").match(/\d+(?:\.\d+)?/);
            const unit = i.unit || String(i.quantity || "").replace(quantityMatch?.[0] || "", "").trim() || "units";
            const payload = { name: i.name, quantity: quantityMatch?.[0] || "1", unit, expiry_date: i.expiry_date || null, low_stock_threshold: "" };
            return i.pet_food_supply_id
              ? petsApi.restockFood(i.pet_food_supply_id, payload)
              : petsApi.createFood(i.pet_id, payload);
          }
          return pantryApi.add({
            name: i.name,
            category: i.category || "other",
            quantity: i.quantity || "",
            unit: i.unit || "",
            location: i.location || "fridge",
            expiry_date: i.expiry_date || null,
            brand: i.brand || "",
          });
        }),
      );
      await onSaved?.(toSave.length, toSave.filter((item) => item.destination === "pet_food").length);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const confirmedCount = candidates
    ? candidates.filter((i) => i.confirmed).length
    : 0;
  const detectedCount = candidates?.length || 0;
  const isReceiptScan = scanType === "receipt";
  const scanModeLabel = isReceiptScan ? "receipt" : "fridge / pantry photo";
  const scanTitle = "Scan receipt or pantry photo";
  const scanDropTitle = isReceiptScan
    ? "Upload grocery receipt"
    : "Upload fridge, freezer, or pantry photo";
  const scanDropHelp = isReceiptScan
    ? "Choose Receipt for store receipts. HouseMind extracts grocery line items."
    : "Choose Fridge / pantry for camera photos of shelves, fridge, or freezer items.";
  const uploadButtonLabel = isReceiptScan ? "Upload receipt" : "Upload photo";
  const cameraButtonLabel = isReceiptScan ? "Camera receipt" : "Use camera";
  const detectedTitle = isReceiptScan
    ? "Detected receipt items"
    : "Detected pantry items";
  const hasDetectedItems = !!candidates;

  const scanContent = (
    <section className="hm-panel hm-vault-scan-panel hm-vault-scan-panel--compact fd-food-scan-panel">
      {presentation === "modal" && (
        <div className="fd-modal-head">
          <div>
            <h3>{scanTitle}</h3>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 12,
                color: "var(--hm-muted)",
              }}
            >
              Review every item below before saving — fix names, add expiry
              dates, set locations.
            </p>
          </div>
          <button className="fd-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
      )}

      {!hasDetectedItems && (
        <div className="hm-vault-scan-note">
          Pick Receipt for store receipts, or Fridge / pantry for camera photos
          of food storage. HouseMind will show one review screen and route confirmed pet food to Pets instead of the human Pantry.
          {aiWarning && (
            <>
              <br />
              <em>
                AI scanning is not configured — showing sample items for review.
              </em>
            </>
          )}
        </div>
      )}
      {hasDetectedItems && (
        <div className="fd-food-scan-detected-bar">
          <span>
            <strong>{detectedCount}</strong>{" "}
            {isReceiptScan ? "receipt" : "pantry"} item
            {detectedCount === 1 ? "" : "s"} detected · Review before saving
          </span>
          <button type="button" onClick={() => setCandidates(null)}>
            Rescan
          </button>
        </div>
      )}

      {scanError && <div className="hm-inline-error">{scanError}</div>}

      <div className="hm-vault-scan-grid">
        {!hasDetectedItems && (
          <div className="hm-vault-scan-controls">
            <div className="hm-segmented fd-food-scan-type-picker">
              {[
                ["receipt", "Receipt"],
                ["fridge", "Fridge / pantry"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  className={scanType === v ? "is-selected" : ""}
                  onClick={() => chooseScanType(v)}
                >
                  {l}
                </button>
              ))}
            </div>

            <label className="hm-vault-scan-consent">
              <input
                type="checkbox"
                checked={scanConsent}
                onChange={(e) => setScanConsent(e.target.checked)}
              />
              <span>
                I’ll review each destination before saving to Pantry or Pet Food.
              </span>
            </label>

            <div
              className={`fd-food-scan-drop${!scanConsent ? " is-disabled" : ""}`}
            >
              <input
                ref={scanUploadRef}
                type="file"
                accept="image/*"
                disabled={!scanConsent}
                onChange={(e) => {
                  handleFile(e.target.files?.[0], scanType);
                  e.target.value = "";
                }}
              />
              <input
                ref={scanCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                disabled={!scanConsent}
                onChange={(e) => {
                  handleFile(e.target.files?.[0], scanType);
                  e.target.value = "";
                }}
              />
              <span className="hm-vault-scan-camera">
                {isReceiptScan ? "🧾" : "📸"}
              </span>
              <strong>{scanDropTitle}</strong>
              <span>{scanDropHelp}</span>
              <div className="fd-food-scan-drop-actions">
                <button
                  type="button"
                  onClick={() => scanUploadRef.current?.click()}
                  disabled={!scanConsent || scanning}
                >
                  {uploadButtonLabel}
                </button>
                <button
                  type="button"
                  onClick={() => scanCameraRef.current?.click()}
                  disabled={!scanConsent || scanning}
                >
                  {cameraButtonLabel}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="hm-vault-scan-review">
          <h3>{detectedTitle}</h3>
          {scanning ? (
            <div
              className="hm-vault-empty hm-vault-scanning"
              style={{ marginTop: 0 }}
            >
              <span className="hm-vault-scan-spinner" />
              Reading your {scanModeLabel}...
            </div>
          ) : !candidates ? (
            <div className="hm-vault-empty" style={{ marginTop: 0 }}>
              Upload a food image to see detected pantry items here.
            </div>
          ) : detectedCount === 0 ? (
            <div className="hm-vault-empty" style={{ marginTop: 0 }}>
              No food items detected. Try a clearer image or add pantry items
              manually.
            </div>
          ) : (
            <>
              <div className="hm-vault-scan-candidates">
                {candidates.map((item) => (
                  <article
                    key={item.id}
                    className={`hm-vault-scan-item fd-food-scan-item${item.confirmed ? " is-selected" : ""}`}
                  >
                    <div className="hm-vault-scan-item-check">
                      <input
                        type="checkbox"
                        checked={!!item.confirmed}
                        onChange={() => toggle(item.id)}
                        title={
                          item.confirmed
                            ? "Uncheck to skip this item"
                            : "Check to include"
                        }
                      />
                    </div>
                    <div className="hm-vault-scan-item-fields">
                      <div className="fd-food-scan-item-top">
                        <input
                          className="hm-auth-input"
                          value={item.name}
                          onChange={(e) =>
                            update(item.id, "name", e.target.value)
                          }
                          placeholder="Item name"
                        />
                        <button
                          type="button"
                          className="hm-vault-scan-remove"
                          onClick={() => removeCandidate(item.id)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                      <div className="fd-food-scan-item-meta">
                        <select
                          className="hm-auth-input"
                          aria-label={`Destination for ${item.name}`}
                          value={item.destination || "human_pantry"}
                          onChange={(e) => setDestination(item.id, e.target.value)}
                        >
                          <option value="human_pantry">Food Pantry</option>
                          <option value="pet_food">Pet Food</option>
                        </select>
                        {item.destination === "pet_food" && (
                          <select
                            className="hm-auth-input"
                            aria-label={`Pet for ${item.name}`}
                            value={item.pet_id || ""}
                            onChange={(e) => assignPet(item.id, e.target.value)}
                          >
                            <option value="">Choose pet…</option>
                            {pets.map((pet) => <option key={pet.id} value={pet.id}>{pet.name}</option>)}
                          </select>
                        )}
                        {item.destination === "pet_food" && item.pet_id && (
                          <select className="hm-auth-input" aria-label={`Inventory action for ${item.name}`} value={item.pet_food_supply_id || ""} onChange={(e) => update(item.id, "pet_food_supply_id", e.target.value)}>
                            <option value="">Create new Pet Food item</option>
                            {petFoodSupplies.filter((supply) => String(supply.pet_id) === String(item.pet_id)).map((supply) => <option key={supply.id} value={supply.id}>Restock {supply.name} · {supply.quantity} {supply.unit || "units"} left</option>)}
                          </select>
                        )}
                        {item.destination !== "pet_food" && <>
                        <select
                          className="hm-auth-input"
                          value={item.category || "other"}
                          onChange={(e) =>
                            update(item.id, "category", e.target.value)
                          }
                        >
                          {CATEGORY_OPTS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <select
                          className="hm-auth-input"
                          value={item.location || "fridge"}
                          onChange={(e) =>
                            update(item.id, "location", e.target.value)
                          }
                        >
                          {LOCATION_OPTS.map((l) => (
                            <option key={l} value={l}>
                              {l}
                            </option>
                          ))}
                        </select>
                        </>}
                      </div>
                      <div className="fd-food-scan-item-detail">
                        <input
                          className="hm-auth-input"
                          value={item.quantity || ""}
                          onChange={(e) =>
                            update(item.id, "quantity", e.target.value)
                          }
                          placeholder="Qty / amount"
                        />
                        <input
                          className={`hm-auth-input fd-food-scan-date${item.expiry_date ? " has-value" : ""}`}
                          type="date"
                          value={item.expiry_date || ""}
                          data-placeholder="Expires"
                          onChange={(e) =>
                            update(item.id, "expiry_date", e.target.value)
                          }
                        />
                      </div>
                      {item.expiry_hint && (
                        <span className="fd-food-scan-ai-hint">
                          Shelf estimate · {item.expiry_hint}
                        </span>
                      )}
                      {item.destination === "pet_food" && <span className="fd-food-scan-ai-hint">🐾 Routes to Pet Food only · {item.routing_reason || "Review the pet before saving"}</span>}
                    </div>
                  </article>
                ))}
              </div>
              <div className="fd-food-scan-actions">
                <button
                  className="hm-btn hm-btn--ghost"
                  type="button"
                  onClick={() => setCandidates(null)}
                >
                  Rescan
                </button>
                <button
                  type="button"
                  className="hm-vault-scan-save"
                  onClick={saveConfirmed}
                  disabled={saving || !confirmedCount}
                >
                  {saving
                    ? "Saving..."
                    : `Save ${confirmedCount} reviewed item${confirmedCount !== 1 ? "s" : ""}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <>
      {showScanning && (
        <SavingNotice
          title="Scanning"
          message={`Reading ${scanModeLabel} items...`}
        />
      )}
      {showSaving && (
        <SavingNotice
          title="Saving pantry"
          message="Adding selected items to pantry..."
        />
      )}
      {presentation === "modal" ? (
        <div
          className="fd-modal-backdrop"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <div className="fd-modal fd-modal--wide">{scanContent}</div>
        </div>
      ) : (
        <FoodMobileSheet
          title={scanTitle}
          label={scanTitle}
          onClose={onClose}
          disableBackdropClose
          dragHandleOnly
        >
          <div className="fd-scan-sheet-body">{scanContent}</div>
        </FoodMobileSheet>
      )}
    </>
  );
}

// ── FoodPage ───────────────────────────────────────────────────────────────
function FoodPage() {
  const { user, applyAiUsage } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const appToast = useToast();
  const presentation = useFormPresentation();
  const requestedMealDayId = Number(
    new URLSearchParams(location.search).get("meal_day") || 0,
  ) || null;
  const [tab, setTab] = useState(0);
  const [pantry, setPantry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [shoppingPlanContext, setShoppingPlanContext] = useState(null);
  const [foodStats, setFoodStats] = useState({
    mealPlanDays: 0,
    mealPlanTarget: 7,
    shoppingItems: 0,
  });
  const [pantryFocusFilter, setPantryFocusFilter] = useState(null);
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [usingPantryItems, setUsingPantryItems] = useState(false);
  const [pantryUsePrompt, setPantryUsePrompt] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [shareMembers, setShareMembers] = useState([]);
  const [shareTargetId, setShareTargetId] = useState("");
  const [shareError, setShareError] = useState("");
  const [shareSaving, setShareSaving] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [sharedResourceKeys, setSharedResourceKeys] = useState(() => new Set());
  const pantryUseResolverRef = useRef(null);
  const pantryUseConfirmRef = useRef(null);
  const showUsingPantryWorking = useMinLoad(usingPantryItems, 700);
  const showShareWorking = useMinLoad(shareSaving || shareLoading, 700);

  const currentWorkspace =
    user?.workspaces?.find(
      (workspace) => workspace.id === user?.active_workspace_id,
    ) ||
    user?.workspace ||
    null;
  const hasFamilyAccess =
    user?.plan === "family" ||
    (user?.workspaces || []).some((workspace) => workspace.plan === "family");
  const canShare =
    hasFamilyAccess &&
    (currentWorkspace?.workspace_type || "personal") === "personal";

  const tripCallout = (() => {
    const trips = upcomingTrips
      .map((event) => {
        const departure =
          event.departure_date ||
          event.start_date ||
          event.event_date ||
          event.occasion_date ||
          event.next_occurrence;
        const days = daysUntilDate(departure);
        const type = String(
          event.event_type || event.occasion_type || event.type || "",
        ).toLowerCase();
        const looksLikeTrip =
          type.includes("trip") ||
          Boolean(
            event.destination || event.trip_style || event.departure_date,
          );
        return { event, departure, days, looksLikeTrip };
      })
      .filter(
        (item) =>
          item.looksLikeTrip &&
          item.days !== null &&
          item.days >= 0 &&
          item.days <= 14,
      )
      .sort((a, b) => a.days - b.days);
    const nearest = trips[0];
    if (!nearest) return null;
    const departure = parseDateOnly(nearest.departure);
    const expiringItems = pantry.filter((item) => {
      const expiry = parseDateOnly(item.expiry_date);
      if (!expiry || !departure) return false;
      const diff = Math.abs(Math.round((expiry - departure) / 86400000));
      return diff <= 3;
    });
    if (!expiringItems.length) return null;
    const itemHash = expiringItems
      .map((item) => item.id || `${item.name}:${item.expiry_date}`)
      .sort()
      .join("|");
    const storageKey = `hm_food_trip_pantry_callout_${nearest.event.id}`;
    if (localStorage.getItem(storageKey) === itemHash) return null;
    const title =
      nearest.event.destination ||
      nearest.event.title ||
      nearest.event.name ||
      "Upcoming";
    return {
      tripId: nearest.event.id,
      title,
      daysUntil: nearest.days,
      itemCount: expiringItems.length,
      itemHash,
      storageKey,
      url: `/app/trip/event/${nearest.event.id}?destination=${encodeURIComponent(nearest.event.destination || "")}`,
    };
  })();

  const loadPantry = useCallback(async () => {
    try {
      const items = await pantryApi.list();
      setPantry(items || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadPantry();
  }, [loadPantry]);

  const refreshFoodStats = useCallback(async () => {
    try {
      const [currentPlan, lists, flat] = await Promise.all([
        mealsApi.current().catch(() => null),
        mealsApi.shoppingLists().catch(() => []),
        shoppingApi.list().catch(() => ({ items: [] })),
      ]);
      const todayIso = localISODate();
      const mealPlanDays = (currentPlan?.days || []).filter((day) => {
        if (!day?.meal_name) return false;
        const mealDate = mealPlanDate(currentPlan, day.day_index);
        const mealDateIso = mealDate ? localISODate(mealDate) : "";
        return !mealDateIso || mealDateIso >= todayIso;
      });
      const plannedMealTypes = new Set(
        mealPlanDays.map((day) =>
          String(day.meal_type || "dinner").toLowerCase(),
        ),
      );
      const mealPlanTarget = Math.max(7, plannedMealTypes.size * 7);
      const planWeekStart = currentPlan ? mealPlanWeekStart(currentPlan) : "";
      // MealPlanTab owns the plan the user is actually viewing. In particular,
      // on Sunday a newly generated upcoming-week plan can coexist with the
      // ending current-week plan returned by /meals/current. A stats refresh
      // must not replace the viewed plan context or Shopping will look up the
      // old week's list and appear empty even though generation succeeded.
      setShoppingPlanContext((existing) =>
        existing ||
        (currentPlan
          ? {
              id: currentPlan.id,
              week_start: planWeekStart,
              range: formatMealPlanRange(currentPlan),
              is_current_week: mealPlanCoversIso(currentPlan, todayIso),
            }
          : null),
      );
      const activeLists = (lists || []).filter((list) => list.status === "active");
      const selectedList = currentPlan
        ? activeLists.find(
            (list) => Number(list.meal_plan_id) === Number(currentPlan.id),
          ) ||
          activeLists.find(
            (list) =>
              planWeekStart && list.meal_plan_week_start === planWeekStart,
          )
        : activeLists.find((list) => !list.meal_plan_id);
      let structuredItems = [];
      if (selectedList) {
        const full = await mealsApi
          .getShoppingList(selectedList.id)
          .catch(() => null);
        structuredItems = full?.items || [];
      }
      const shoppingNames = new Set(
        [...structuredItems, ...(flat?.items || [])]
          .map((item) => normalizeFoodMatchName(item?.name))
          .filter(Boolean),
      );
      setFoodStats({
        mealPlanDays: mealPlanDays.length,
        mealPlanTarget,
        shoppingItems: shoppingNames.size,
      });
    } catch {
      setFoodStats({ mealPlanDays: 0, mealPlanTarget: 7, shoppingItems: 0 });
    }
  }, []);

  useEffect(() => {
    refreshFoodStats();
  }, [refreshFoodStats]);

  useEffect(() => {
    eventsApi
      .listPersonal()
      .then((data) =>
        setUpcomingTrips(
          Array.isArray(data) ? data : data?.events || data?.items || [],
        ),
      )
      .catch(() => setUpcomingTrips([]));
  }, []);

  // Load preferences once so MealPlanTab can show nudge correctly
  useEffect(() => {
    mealsApi
      .preferences()
      .then((p) => setPrefs(p))
      .catch(() => {});
  }, []);

  // Listen for tab-switch requests from child components (e.g. "Set up →" nudge)
  useEffect(() => {
    function handler(e) {
      setTab(e.detail);
    }
    window.addEventListener("hm_food_tab", handler);
    return () => window.removeEventListener("hm_food_tab", handler);
  }, []);

  useEffect(() => {
    const requestedTab =
      new URLSearchParams(location.search).get("tab") ||
      location.state?.foodTab;
    const tabMap = {
      pantry: 0,
      "meal-plan": 1,
      recipes: 2,
      shopping: 3,
      preferences: 4,
    };
    if (Object.prototype.hasOwnProperty.call(tabMap, requestedTab))
      setTab(tabMap[requestedTab]);
  }, [location.search, location.state]);

  async function handleAdd(data) {
    const item = await pantryApi.add(data);
    setPantry((prev) => [item, ...prev]);
    return item;
  }

  async function handleRemove(id) {
    await pantryApi.remove(id);
    setPantry((prev) => prev.filter((i) => i.id !== id));
  }

  function matchPantryItemsForIngredients(names = []) {
    const wanted = names
      .map((value) =>
        String(typeof value === "string" ? value : value?.name || "").trim(),
      )
      .filter(Boolean);
    const matched = [];
    const usedNames = new Set();
    for (const item of pantry) {
      const match = wanted.find((name) => foodNamesMatch(item.name, name));
      const matchKey = normalizeFoodMatchName(match);
      if (match && !usedNames.has(matchKey)) {
        matched.push({ ...item, selected: true, matchedIngredient: match });
        usedNames.add(matchKey);
      }
    }
    return { wanted, matched };
  }

  function closePantryUsePrompt(count = null) {
    const resolver = pantryUseResolverRef.current;
    pantryUseResolverRef.current = null;
    pantryUseConfirmRef.current = null;
    setPantryUsePrompt(null);
    resolver?.(count);
  }

  function reviewPantryUse({ names = [], title = "Meal", onConfirm } = {}) {
    const { wanted, matched } = matchPantryItemsForIngredients(names);
    if (!wanted.length) {
      showToast("No pantry ingredients are tracked for this meal yet");
      return Promise.resolve(0);
    }
    if (!matched.length) {
      showToast("Cooked, but no pantry items matched to remove");
      return Promise.resolve(0);
    }
    pantryUseConfirmRef.current = onConfirm || null;
    setPantryUsePrompt({ title, wanted, items: matched });
    return new Promise((resolve) => {
      pantryUseResolverRef.current = resolve;
    });
  }

  async function confirmPantryUsePrompt() {
    const selected = (pantryUsePrompt?.items || []).filter(
      (item) => item.selected,
    );
    if (!selected.length) {
      closePantryUsePrompt(null);
      return;
    }
    setUsingPantryItems(true);
    try {
      if (pantryUseConfirmRef.current) {
        await pantryUseConfirmRef.current(selected);
      } else {
        const updatedItems = [];
        const removedIds = [];
        await Promise.all(
          selected.map(async (item) => {
            const quantity = pantryQuantityNumber(item.quantity);
            if (quantity !== null && quantity > 1) {
              const updated = await pantryApi.update(item.id, {
                quantity: formatPantryQuantity(quantity - 1),
              });
              updatedItems.push(updated);
              return;
            }
            await pantryApi.remove(item.id);
            removedIds.push(item.id);
          }),
        );
        if (updatedItems.length) {
          setPantry((prev) =>
            prev.map(
              (item) =>
                updatedItems.find((updated) => updated.id === item.id) || item,
            ),
          );
        }
        if (removedIds.length) {
          setPantry((prev) =>
            prev.filter((item) => !removedIds.includes(item.id)),
          );
        }
      }
      showToast(
        `✓ ${selected.length} pantry item${selected.length !== 1 ? "s" : ""} marked used`,
      );
      closePantryUsePrompt(selected.length);
    } catch (e) {
      showToast(e.message || "Could not mark pantry items used.", "error");
    } finally {
      setUsingPantryItems(false);
    }
  }

  async function markItemsUsed(names = []) {
    return reviewPantryUse({ names, title: "Meal" });
  }

  async function cookRecipeFromSuggestion(recipe) {
    const pantryTruth = recipePantryTruth(recipe, pantry);
    const names = pantryTruth.available;
    let result = null;
    const count = await reviewPantryUse({
      names,
      title: recipe?.title || "Recipe",
      onConfirm: async (selected) => {
        result = await mealsApi.cookRecipe({
          ...recipe,
          confirmed_pantry_item_ids: selected.map((item) => item.id),
        });
      },
    });
    if (count === null) return null;
    if (!count) {
      result = await mealsApi.cookRecipe({
        ...recipe,
        confirmed_pantry_item_ids: [],
      });
    }
    await loadPantry();
    showToast("Cooked! Added to Recently cooked");
    return result;
  }

  async function cookMealPlanDay(day) {
    if (!day) return { confirmed: false, pantryCount: 0 };
    const ingredients = mealPlanDayIngredients(day, day.recipe);
    const payload = {
      id: day.recipe_id || day.recipe?.id,
      recipe_id: day.recipe_id || day.recipe?.id,
      title: day.meal_name || day.recipe?.title || "Meal",
      source: "meal_plan",
      meal_plan_day_id: day.id,
      meal_type: day.meal_type || "dinner",
      prep_time:
        day.prep_time_minutes ||
        day.cook_time_minutes ||
        Number(String(day.cook_time || "").match(/\d+/)?.[0]) ||
        null,
      difficulty: day.difficulty || day.recipe?.difficulty || "easy",
      ingredients,
      instructions: recipeStepsForDay(day, day.recipe),
      uses_from_pantry: ingredients,
    };
    let result = null;
    const count = await reviewPantryUse({
      names: ingredients,
      title: payload.title,
      onConfirm: async (selected) => {
        result = await mealsApi.cookRecipe({
          ...payload,
          confirmed_pantry_item_ids: selected.map((item) => item.id),
        });
      },
    });
    if (count === null) return { confirmed: false, pantryCount: 0 };
    if (!count) {
      result = await mealsApi.cookRecipe({
        ...payload,
        confirmed_pantry_item_ids: [],
      });
    }
    await loadPantry();
    window.dispatchEvent(new Event("hm_notifications_refresh"));
    showToast(
      count ? "Cooked! Pantry updated" : "Cooked! No pantry items matched",
    );
    return {
      confirmed: true,
      pantryCount: count || 0,
      result,
    };
  }

  async function setCookingTonight(mealName, mealType = "dinner") {
    await familyApi.setCookingTonight(mealName, mealType);
    window.dispatchEvent(new Event("hm_notifications_refresh"));
  }

  function showToast(msg, type = "success") {
    appToast?.(msg, type);
  }

  async function openShare(resourceType, resourceId, title) {
    setShareItem({ resourceType, resourceId, title });
    setShareMembers([]);
    setShareTargetId("");
    setShareError("");
    setShareLoading(true);
    try {
      const data = await sharingApi.members();
      const members = Array.isArray(data.members) ? data.members : [];
      setShareMembers(members);
      setShareTargetId(members[0]?.user_id ? String(members[0].user_id) : "");
      if (members.length === 0)
        setShareError("Add another Family member in Settings before sharing.");
    } catch (e) {
      setShareError(e.message || "Could not load Family members.");
    } finally {
      setShareLoading(false);
    }
  }

  async function handleShare() {
    if (!shareItem || !shareTargetId) return;
    setShareSaving(true);
    setShareError("");
    try {
      await sharingApi.create({
        resource_type: shareItem.resourceType,
        resource_id: shareItem.resourceId,
        recipient_user_id: Number(shareTargetId),
      });
      const member = shareMembers.find(
        (item) => String(item.user_id) === String(shareTargetId),
      );
      setSharedResourceKeys((prev) => {
        const next = new Set(prev);
        next.add(`${shareItem.resourceType}:${shareItem.resourceId}`);
        return next;
      });
      setShareItem(null);
      showToast(`Shared with ${member?.full_name || "Family member"}`);
    } catch (e) {
      setShareError(e.message || "Could not share this item.");
    } finally {
      setShareSaving(false);
    }
  }

  function openFoodTab(index) {
    setTab(index);
  }

  function renderFoodTabContent() {
    if (tab === 0) {
      return (
        <PantryTab
          pantry={pantry}
          loading={loading}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onScan={() => setScanOpen(true)}
          onUpdate={loadPantry}
          onShare={openShare}
          canShare={canShare}
          sharedResourceKeys={sharedResourceKeys}
          onToast={showToast}
          focusFilter={pantryFocusFilter}
          tripCallout={tripCallout}
          onTripCalloutOpen={() =>
            tripCallout?.url && navigate(tripCallout.url)
          }
          onTripCalloutDismiss={() => {
            if (tripCallout?.storageKey)
              localStorage.setItem(
                tripCallout.storageKey,
                tripCallout.itemHash,
              );
            showToast("Trip pantry reminder dismissed");
          }}
        />
      );
    }
    if (tab === 1) {
      return (
        <MealPlanTab
          pantry={pantry}
          prefs={prefs}
          setPrefs={setPrefs}
          workspaceId={currentWorkspace?.id}
          aiProvider={user?.ai_provider}
          onMarkItemsUsed={markItemsUsed}
          onCookMeal={cookMealPlanDay}
          onAiUsage={applyAiUsage}
          onShare={openShare}
          canShare={canShare}
          sharedResourceKeys={sharedResourceKeys}
          onToast={showToast}
          onSetCookingTonight={setCookingTonight}
          onStatsChanged={refreshFoodStats}
          onPantryRefresh={loadPantry}
          onShoppingPlanContext={setShoppingPlanContext}
          requestedMealDayId={requestedMealDayId}
        />
      );
    }
    if (tab === 2)
      return (
        <RecipesTab
          pantry={pantry}
          workspaceId={currentWorkspace?.id}
          onCookRecipe={cookRecipeFromSuggestion}
          onAiUsage={applyAiUsage}
          onToast={showToast}
        />
      );
    if (tab === 3)
      return (
        <ShoppingTab
          pantry={pantry}
          mealPlanContext={shoppingPlanContext}
          onShare={openShare}
          canShare={canShare}
          sharedResourceKeys={sharedResourceKeys}
          onPantryAdded={(items) => {
            if (!items?.length) return;
            setPantry((prev) => {
              const incomingById = new Map(
                items.filter((item) => item?.id).map((item) => [item.id, item]),
              );
              const incomingByName = new Map(
                items
                  .map((item) => [
                    String(item?.name || "")
                      .trim()
                      .toLowerCase(),
                    item,
                  ])
                  .filter(([name]) => name),
              );
              const replaced = prev.map((item) => {
                const byId = incomingById.get(item.id);
                if (byId) return byId;
                const byName = incomingByName.get(
                  String(item.name || "")
                    .trim()
                    .toLowerCase(),
                );
                return byName || item;
              });
              const existingIds = new Set(replaced.map((item) => item.id));
              const existingNames = new Set(
                replaced
                  .map((item) =>
                    String(item.name || "")
                      .trim()
                      .toLowerCase(),
                  )
                  .filter(Boolean),
              );
              const added = items.filter((item) => {
                const name = String(item?.name || "")
                  .trim()
                  .toLowerCase();
                return (
                  !existingIds.has(item.id) &&
                  (!name || !existingNames.has(name))
                );
              });
              return [...added, ...replaced];
            });
          }}
        />
      );
    if (tab === 4) return <PreferencesTab onSaved={setPrefs} />;
    return null;
  }

  function renderFoodAskBar() {
    const FOOD_CHIPS = {
      0: [
        "What should I use first?",
        "Find expiring food",
        "Suggest pantry staples",
      ],
      1: ["What's planned?", "Plan a meal lane", "Any duplicate meals?"],
      2: ["Quick 20-min meal", "Use expiring items", "Vegetarian ideas"],
      3: ["What do I still need?", "Clear bought items", "Generate list"],
      4: [
        "Tune my food preferences",
        "Any allergy conflicts?",
        "Improve meal ideas",
      ],
    };
    const chips = FOOD_CHIPS[tab];
    if (!chips) return null;
    const foodAskTabs = [
      "Pantry",
      "Meal Plan",
      "Recipes",
      "Shopping List",
      "Preferences",
    ];
    const openAsk = (question) =>
      window.dispatchEvent(
        new CustomEvent("hm_open_ask", {
          detail: {
            question,
            context: {
              module: "food",
              module_label: "Food",
              surface: foodAskTabs[tab] || "Food",
            },
          },
        }),
      );
    return (
      <div className="fd-food-askbar">
        <input
          className="fd-food-askbar__input"
          placeholder={`✦ ${chips[0]}`}
          onFocus={() => openAsk("")}
          readOnly
        />
        <div className="fd-food-askbar__chips">
          {chips.map((c) => (
            <button
              key={c}
              type="button"
              className="fd-food-askbar__chip"
              onClick={() => openAsk(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="fd-food-askbar__button"
          onClick={() => openAsk(chips[0])}
        >
          ✦ Ask
        </button>
      </div>
    );
  }

  return (
    <div className="fd-page">
      {showUsingPantryWorking && (
        <SavingNotice
          title="Marking items used"
          message="HouseMind is updating your pantry..."
        />
      )}
      {showShareWorking && (
        <SavingNotice
          title={shareSaving ? "Sharing" : "Loading"}
          message={
            shareSaving ? "Sharing with Family..." : "Loading Family members..."
          }
        />
      )}
      {scanOpen && (
        <ScanModal
          onClose={() => setScanOpen(false)}
          onSaved={async (n, petFoodCount = 0) => {
            await loadPantry();
            const pantryCount = n - petFoodCount;
            showToast(`✓ Saved ${pantryCount ? `${pantryCount} to Pantry` : ""}${pantryCount && petFoodCount ? " and " : ""}${petFoodCount ? `${petFoodCount} to Pet Food` : ""}`);
          }}
          onAiUsage={applyAiUsage}
        />
      )}

      <ModuleHeader
        title="Food"
        subtitle="Pantry · Meal plans · Recipes · Shopping"
        image={IMG_FOOD}
      >
        <div className="fd-hero-stats">
          <button
            type="button"
            className="fd-hero-stat"
            onClick={() => {
              setPantryFocusFilter({ key: Date.now(), filter: "all" });
              openFoodTab(0);
            }}
          >
            <strong>{pantry.length}</strong>
            <span>pantry items</span>
          </button>
          <button
            type="button"
            className="fd-hero-stat"
            onClick={() => {
              setPantryFocusFilter({ key: Date.now(), filter: "expiring" });
              openFoodTab(0);
            }}
          >
            <strong>{pantry.filter((i) => i.level !== "stable").length}</strong>
            <span>expiring soon</span>
          </button>
          <button
            type="button"
            className={`fd-hero-stat ${foodStats.mealPlanDays >= Math.max(5, foodStats.mealPlanTarget - 2) ? "is-good" : "is-warn"}`}
            onClick={() => openFoodTab(1)}
          >
            <strong>
              {foodStats.mealPlanDays}/{foodStats.mealPlanTarget}
            </strong>
            <span>meal plan</span>
          </button>
          <button
            type="button"
            className="fd-hero-stat"
            onClick={() =>
              foodStats.shoppingItems > 0 ? openFoodTab(3) : openFoodTab(2)
            }
          >
            <strong>
              {foodStats.shoppingItems > 0 ? foodStats.shoppingItems : "20m"}
            </strong>
            <span>
              {foodStats.shoppingItems > 0 ? "shopping" : "quick meals"}
            </span>
          </button>
        </div>
      </ModuleHeader>

      <div className="fd-tabs">
        {FOOD_TABS.map((t, i) => (
          <button
            key={t}
            className={`fd-tab${tab === i ? " fd-tab--active" : ""}`}
            onClick={() => openFoodTab(i)}
          >
            {t}
          </button>
        ))}
      </div>

      {renderFoodTabContent()}
      {renderFoodAskBar()}
      {pantryUsePrompt && (
        <FoodMobileSheet
          title="Review pantry used"
          label="Review pantry used"
          onClose={() => closePantryUsePrompt(null)}
          disableBackdropClose
          dragHandleOnly
        >
          <div
            className={`fd-clear-sheet-content fd-pantry-prompt${pantryUsePrompt.items.length > 5 ? " fd-pantry-prompt--many" : ""}`}
          >
            <div className="fd-clear-sheet__head">
              <div className="fd-clear-sheet__warn fd-pantry-prompt__warn">
                Cooked meal
              </div>
              <div className="fd-clear-sheet__sub">
                Confirm what was used for {pantryUsePrompt.title || "this meal"}
                . Unselect anything still in the pantry.
              </div>
            </div>
            <div className="fd-pantry-prompt__list">
              {pantryUsePrompt.items.map((item, idx) => (
                <div
                  key={item.id}
                  className={`fd-pantry-prompt__item${item.selected ? " fd-pantry-prompt__item--on" : ""}`}
                >
                  <button
                    type="button"
                    className="fd-pantry-prompt__check"
                    onClick={() =>
                      setPantryUsePrompt((prev) => ({
                        ...prev,
                        items: prev.items.map((row, rowIndex) =>
                          rowIndex === idx
                            ? { ...row, selected: !row.selected }
                            : row,
                        ),
                      }))
                    }
                    aria-label={
                      item.selected
                        ? `Keep ${item.name}`
                        : `Mark ${item.name} used`
                    }
                  >
                    {item.selected ? "✓" : ""}
                  </button>
                  <div className="fd-pantry-prompt__fields">
                    <span className="fd-pantry-prompt__name">{item.name}</span>
                    <div className="fd-pantry-prompt__grid">
                      <input
                        className="hm-input"
                        value={item.quantity || ""}
                        readOnly
                        placeholder="Qty"
                      />
                      <input
                        className="hm-input"
                        value={item.location || "pantry"}
                        readOnly
                        placeholder="Location"
                      />
                      <input
                        className={`hm-input fd-pantry-prompt__date${item.expiry_date ? " has-value" : ""}`}
                        type="date"
                        value={item.expiry_date || ""}
                        readOnly
                        data-placeholder="Expires"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="fd-clear-sheet__foot">
              <button
                type="button"
                className="fd-clear-cancel"
                onClick={() => closePantryUsePrompt(null)}
                disabled={usingPantryItems}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hm-btn hm-btn--primary fd-clear-confirm"
                disabled={
                  usingPantryItems ||
                  !pantryUsePrompt.items.some((i) => i.selected)
                }
                onClick={confirmPantryUsePrompt}
              >
                Mark {pantryUsePrompt.items.filter((i) => i.selected).length}{" "}
                used
              </button>
            </div>
          </div>
        </FoodMobileSheet>
      )}

      {shareItem &&
        (() => {
          const shareContent = (
            <>
              {presentation === "modal" && (
                <div className="fd-modal-head">
                  <h3>Share with Family</h3>
                  <button
                    className="fd-modal-close"
                    onClick={() => setShareItem(null)}
                  >
                    ×
                  </button>
                </div>
              )}
              <p
                className="text-muted mb-16"
                style={{ fontSize: 13, lineHeight: 1.5 }}
              >
                Only <strong>{shareItem.title}</strong> will be shared.
              </p>
              {shareError && (
                <div className="notice-banner is-error mb-16">
                  <p>{shareError}</p>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Family member</label>
                <select
                  className="form-select"
                  value={shareTargetId}
                  onChange={(e) => setShareTargetId(e.target.value)}
                >
                  {shareMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.full_name} - {member.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fd-modal-foot">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShareItem(null)}
                  disabled={shareSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleShare}
                  disabled={shareSaving || !shareTargetId}
                >
                  {shareSaving ? "Sharing..." : "Share"}
                </button>
              </div>
            </>
          );
          return presentation === "modal" ? (
            <div
              className="fd-modal-backdrop"
              onClick={() => setShareItem(null)}
            >
              <div
                className="fd-modal fd-share-modal"
                onClick={(e) => e.stopPropagation()}
              >
                {shareContent}
              </div>
            </div>
          ) : (
            <FoodMobileSheet
              title="Share with Family"
              label="Share with Family"
              onClose={() => setShareItem(null)}
              disableBackdropClose
              dragHandleOnly
            >
              <div className="fd-share-sheet-body">{shareContent}</div>
            </FoodMobileSheet>
          );
        })()}
    </div>
  );
}

// ── Auth guard ────────────────────────────────────────────────────────────

function SmartThingsOAuthHandoff() {
  useEffect(() => {
    window.location.replace(
      `${API_BASE}/home-ai/integrations/smartthings/oauth/callback${window.location.search}`,
    );
  }, []);
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        color: "var(--hm-muted)",
        fontSize: 14,
      }}
    >
      Finishing SmartThings connection…
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (
    location.pathname === "/app/smart-home" &&
    params.get("smartthings_callback") === "1" &&
    (params.get("code") || params.get("error"))
  ) {
    return <SmartThingsOAuthHandoff />;
  }
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          color: "var(--hm-muted)",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          color: "var(--hm-muted)",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  if (!user)
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  if (!user.is_admin)
    return (
      <Navigate
        to={`/login?admin=1&redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  return children;
}

function ImpersonationHandoff() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    try {
      window.opener = null;
    } catch {}
    if (!token) return;
    setImpersonationAuthToken(token);
    window.history.replaceState(null, "", "/impersonate");
    window.location.replace("/app");
  }, []);

  const hasToken = new URLSearchParams(window.location.search).has("token");
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        color: "var(--hm-text)",
        background: "var(--hm-body-bg)",
      }}
    >
      <div
        style={{
          maxWidth: 420,
          padding: 24,
          border: "1px solid var(--hm-line)",
          borderRadius: 14,
          background: "var(--hm-card-solid)",
          textAlign: "center",
        }}
      >
        <h1 style={{ margin: "0 0 8px", fontSize: 22 }}>
          HouseMind impersonation
        </h1>
        <p style={{ margin: 0, color: "var(--hm-muted)", lineHeight: 1.5 }}>
          {hasToken
            ? "Opening the user session..."
            : "No impersonation token was provided. Generate a new session from the admin dashboard."}
        </p>
      </div>
    </div>
  );
}

function HouseMindLogoIcon() {
  return (
    <span className="app-logo-icon" aria-hidden="true">
      <svg
        viewBox="0 0 80 80"
        width="36"
        height="36"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="80" height="80" rx="18" fill="#5DCBA8" />
        <polyline
          points="12,42 40,16 68,42"
          fill="none"
          stroke="white"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="54" y="20" width="8" height="12" rx="2" fill="white" />
        <path
          d="M38,36 C38,36 28,34 26,40 C24,44 26,48 28,50 C26,52 25,56 27,59 C29,62 33,63 36,62 C37,63 38,64 40,64 L40,36 Z"
          fill="white"
        />
        <path
          d="M42,36 C42,36 52,34 54,40 C56,44 54,48 52,50 C54,52 55,56 53,59 C51,62 47,63 44,62 C43,63 42,64 40,64 L40,36 Z"
          fill="white"
        />
        <line
          x1="40"
          y1="36"
          x2="40"
          y2="64"
          stroke="#5DCBA8"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle
          cx="33"
          cy="43"
          r="2"
          fill="none"
          stroke="#5DCBA8"
          strokeWidth="1.5"
        />
        <circle
          cx="31"
          cy="52"
          r="2"
          fill="none"
          stroke="#5DCBA8"
          strokeWidth="1.5"
        />
        <line
          x1="33"
          y1="43"
          x2="31"
          y2="52"
          stroke="#5DCBA8"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        <circle
          cx="47"
          cy="43"
          r="2"
          fill="none"
          stroke="#5DCBA8"
          strokeWidth="1.5"
        />
        <circle
          cx="49"
          cy="52"
          r="2"
          fill="none"
          stroke="#5DCBA8"
          strokeWidth="1.5"
        />
        <line
          x1="47"
          y1="43"
          x2="49"
          y2="52"
          stroke="#5DCBA8"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function HouseMindShell() {
  const { user, logout, applyAiUsage } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const firstName = user?.full_name?.split(" ")[0] || "Account";
  const userInitials =
    (user?.full_name || user?.email || "HM")
      .split(/\s|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "HM";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [hasConnectedInbox, setHasConnectedInbox] = useState(null); // null=unknown, true=connected, false=not connected
  const [discoveryPendingCount, setDiscoveryPendingCount] = useState(0);
  const inboxCheckDoneRef = useRef(false);

  const [mobileMoreClosing, setMobileMoreClosing] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [askQuestion, setAskQuestion] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState("");
  const [askMessages, setAskMessages] = useState([]);
  const [askContext, setAskContext] = useState(null);
  const [askGlobalFilter, setAskGlobalFilter] = useState("all");
  const [askPlaceholderOverride, setAskPlaceholderOverride] = useState("");
  const [askListening, setAskListening] = useState(false);
  const [askVoiceError, setAskVoiceError] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationSwipeId, setNotificationSwipeId] = useState(null);
  const [notificationSwipeOffset, setNotificationSwipeOffset] = useState(0);
  const [notificationDismissing, setNotificationDismissing] = useState(null);
  const [undoBusyId, setUndoBusyId] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutWorking, setLogoutWorking] = useState(false);
  const [installBannerDismissed, setInstallBannerDismissed] = useState(
    () => localStorage.getItem("housemind_install_banner_dismissed") === "true",
  );
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const {
    canPrompt: canPromptInstall,
    isStandalone: isStandaloneApp,
    installApp,
  } = usePwaInstall();
  const askRecognitionRef = useRef(null);
  const askCloseTimerRef = useRef(null);
  const askPanelRef = useRef(null);
  const askTextareaRef = useRef(null);
  const askMessagesEndRef = useRef(null);
  const askDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const moreCloseTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const moreSheetRef = useRef(null);
  const moreDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const navSheetRef = useRef(null);
  const navDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const notificationPanelRef = useRef(null);
  const notificationDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const notificationGestureRef = useRef({ id: null, startX: 0, startY: 0 });
  const notificationSuppressClickRef = useRef(false);
  const closeNav = () => setMobileNavOpen(false);

  // Check once whether any OAuth inbox is connected — drives the contextual "Connect inbox" sidebar entry
  useEffect(() => {
    if (!mobileNavOpen || inboxCheckDoneRef.current) return;
    inboxCheckDoneRef.current = true;
    importCandidatesApi
      .connections()
      .then((rows) => {
        const connected =
          Array.isArray(rows) &&
          rows.some(
            (r) =>
              (r.source_type === "oauth" ||
                r.source_type === "calendar_oauth") &&
              r.permission_status === "connected",
          );
        setHasConnectedInbox(connected);
      })
      .catch(() => setHasConnectedInbox(true)); // on error assume connected so we don't show the nudge incorrectly
  }, [mobileNavOpen]);

  const closeAskSheet = useCallback(() => {
    if (askCloseTimerRef.current) window.clearTimeout(askCloseTimerRef.current);
    askCloseTimerRef.current = null;
    if (askPanelRef.current) {
      askPanelRef.current.style.transform = "";
      askPanelRef.current.style.transition = "";
      askPanelRef.current.style.animation = "";
    }
    askDragRef.current = { active: false, startY: 0, currentY: 0 };
    setAskOpen(false);
  }, []);

  useEffect(() => {
    if (!askOpen) return undefined;

    function handleAskOutsidePointer(event) {
      if (!askPanelRef.current || askPanelRef.current.contains(event.target))
        return;
      closeAskSheet();
    }

    document.addEventListener("pointerdown", handleAskOutsidePointer, true);
    return () =>
      document.removeEventListener(
        "pointerdown",
        handleAskOutsidePointer,
        true,
      );
  }, [askOpen, closeAskSheet]);

  useEffect(() => {
    if (canPromptInstall) {
      setInstallBannerDismissed(false);
      localStorage.removeItem("housemind_install_banner_dismissed");
    }
  }, [canPromptInstall]);

  useEffect(() => {
    if (!isStandaloneApp) return;
    setInstallBannerDismissed(true);
    localStorage.setItem("housemind_install_banner_dismissed", "true");
  }, [isStandaloneApp]);

  async function handleInstallApp() {
    if (canPromptInstall) {
      await installApp();
      return;
    }
    setShowInstallHelp(true);
  }

  async function handleMenuInstallApp() {
    if (isStandaloneApp) {
      toast("HouseMind is already installed on this device", "info");
      return;
    }
    if (canPromptInstall) {
      await installApp();
      toast("Install prompt opened", "success");
      setMobileNavOpen(false);
      return;
    }
    setMobileNavOpen(false);
    setShowInstallHelp(true);
  }

  async function handleMenuShareApp() {
    const url = `${window.location.origin}${APP_SHARE_PATH}`;
    const shareData = {
      title: "HouseMind",
      text: "Check out HouseMind — your household's AI brain for food, bills, subscriptions, vehicles and more 🏠",
      url,
    };

    try {
      if (
        navigator.share &&
        (!navigator.canShare || navigator.canShare(shareData))
      ) {
        await navigator.share(shareData);
        setMobileNavOpen(false);
        return;
      }
      if (!navigator.clipboard?.writeText) {
        toast(`Copy this app link: ${url}`, "info");
        return;
      }
      await navigator.clipboard.writeText(url);
      toast("HouseMind app link copied", "success");
      setMobileNavOpen(false);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast("Could not open sharing on this device.", "error");
      }
    }
  }

  function dismissInstallBanner() {
    setInstallBannerDismissed(true);
    localStorage.setItem("housemind_install_banner_dismissed", "true");
  }

  function closeMobileMore(animated = true) {
    if (!mobileMoreOpen) {
      setMobileMoreClosing(false);
      return;
    }
    if (!animated) {
      if (moreCloseTimerRef.current)
        window.clearTimeout(moreCloseTimerRef.current);
      moreCloseTimerRef.current = null;
      if (moreSheetRef.current) {
        moreSheetRef.current.style.transform = "";
        moreSheetRef.current.style.transition = "";
        moreSheetRef.current.style.animation = "";
      }
      setMobileMoreClosing(false);
      setMobileMoreOpen(false);
      return;
    }
    setMobileMoreClosing(true);
    if (moreCloseTimerRef.current)
      window.clearTimeout(moreCloseTimerRef.current);
    moreCloseTimerRef.current = window.setTimeout(() => {
      setMobileMoreOpen(false);
      setMobileMoreClosing(false);
      moreCloseTimerRef.current = null;
      if (moreSheetRef.current) {
        moreSheetRef.current.style.transform = "";
        moreSheetRef.current.style.transition = "";
        moreSheetRef.current.style.animation = "";
      }
    }, 220);
  }

  function startMoreDrag(event) {
    if (!mobileMoreOpen || mobileMoreClosing) return;
    moreDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (moreSheetRef.current) {
      moreSheetRef.current.style.animation = "none";
      moreSheetRef.current.style.transition = "none";
    }
  }

  function moveMoreDrag(event) {
    if (!moreDragRef.current.active || !moreSheetRef.current) return;
    const delta = Math.max(0, event.clientY - moreDragRef.current.startY);
    moreDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    moreSheetRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endMoreDrag(event) {
    if (!moreDragRef.current.active || !moreSheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = moreDragRef.current.currentY;
    moreDragRef.current = { active: false, startY: 0, currentY: 0 };

    if (delta > 72) {
      if (moreCloseTimerRef.current)
        window.clearTimeout(moreCloseTimerRef.current);
      setMobileMoreClosing(true);
      moreSheetRef.current.style.transition =
        "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      moreSheetRef.current.style.transform = "translateY(100%)";
      moreCloseTimerRef.current = window.setTimeout(() => {
        setMobileMoreOpen(false);
        setMobileMoreClosing(false);
        moreCloseTimerRef.current = null;
        if (moreSheetRef.current) {
          moreSheetRef.current.style.transform = "";
          moreSheetRef.current.style.transition = "";
          moreSheetRef.current.style.animation = "";
        }
      }, 210);
      return;
    }

    moreSheetRef.current.style.transition =
      "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    moreSheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!moreSheetRef.current || moreDragRef.current.active) return;
      moreSheetRef.current.style.transition = "";
      moreSheetRef.current.style.transform = "";
      moreSheetRef.current.style.animation = "";
    }, 190);
  }

  function startAskDrag(event) {
    if (!askOpen || askBusy) return;
    if (typeof window !== "undefined" && window.innerWidth > 560) return;
    if (
      event.target?.closest?.(
        "button, a, input, textarea, select, [role='button']",
      )
    )
      return;
    askDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (askPanelRef.current) {
      askPanelRef.current.style.animation = "none";
      askPanelRef.current.style.transition = "none";
    }
  }

  function moveAskDrag(event) {
    if (!askDragRef.current.active || !askPanelRef.current) return;
    const delta = Math.max(0, event.clientY - askDragRef.current.startY);
    askDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    askPanelRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endAskDrag(event) {
    if (!askDragRef.current.active || !askPanelRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = askDragRef.current.currentY;
    askDragRef.current = { active: false, startY: 0, currentY: 0 };

    if (delta > 72) {
      if (askCloseTimerRef.current)
        window.clearTimeout(askCloseTimerRef.current);
      askPanelRef.current.style.transition =
        "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      askPanelRef.current.style.transform = "translateY(100%)";
      askCloseTimerRef.current = window.setTimeout(() => {
        closeAskSheet();
      }, 210);
      return;
    }

    askPanelRef.current.style.transition =
      "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    askPanelRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!askPanelRef.current || askDragRef.current.active) return;
      askPanelRef.current.style.transition = "";
      askPanelRef.current.style.transform = "";
      askPanelRef.current.style.animation = "";
    }, 190);
  }

  function startNotificationDrag(event) {
    if (!notificationsOpen) return;
    if (!event.target.closest?.(".hm-notification-head")) return;
    notificationDragRef.current = {
      active: true,
      startY: event.clientY,
      currentY: 0,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (notificationPanelRef.current) {
      notificationPanelRef.current.style.transition = "none";
    }
  }

  function moveNotificationDrag(event) {
    if (!notificationDragRef.current.active || !notificationPanelRef.current)
      return;
    const delta = Math.min(
      0,
      event.clientY - notificationDragRef.current.startY,
    );
    notificationDragRef.current.currentY = delta;
    if (delta < -4) event.preventDefault();
    notificationPanelRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endNotificationDrag(event) {
    if (!notificationDragRef.current.active || !notificationPanelRef.current)
      return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = notificationDragRef.current.currentY;
    notificationDragRef.current = { active: false, startY: 0, currentY: 0 };

    if (delta < -54) {
      notificationPanelRef.current.style.transition =
        "transform .18s ease, opacity .18s ease";
      notificationPanelRef.current.style.transform = "translateY(-110%)";
      notificationPanelRef.current.style.opacity = "0";
      window.setTimeout(() => {
        setNotificationsOpen(false);
        if (notificationPanelRef.current) {
          notificationPanelRef.current.style.transition = "";
          notificationPanelRef.current.style.transform = "";
          notificationPanelRef.current.style.opacity = "";
        }
      }, 190);
      return;
    }

    notificationPanelRef.current.style.transition =
      "transform .16s cubic-bezier(0.2, 0, 0, 1)";
    notificationPanelRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!notificationPanelRef.current || notificationDragRef.current.active)
        return;
      notificationPanelRef.current.style.transition = "";
      notificationPanelRef.current.style.transform = "";
    }, 170);
  }

  function startNavDrag(event) {
    if (!mobileNavOpen || !navSheetRef.current) return;
    navDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    navSheetRef.current.style.transition = "none";
  }

  function moveNavDrag(event) {
    if (!navDragRef.current.active || !navSheetRef.current) return;
    const delta = Math.max(0, event.clientY - navDragRef.current.startY);
    navDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    navSheetRef.current.style.transform = `translateY(${delta}px)`;
  }

  function startNavHandleDrag(event) {
    event.preventDefault();
    event.stopPropagation();
    startNavDrag(event);
  }

  function moveNavHandleDrag(event) {
    event.stopPropagation();
    moveNavDrag(event);
  }

  function endNavHandleDrag(event) {
    event.stopPropagation();
    endNavDrag(event);
  }

  function endNavDrag(event) {
    if (!navDragRef.current.active || !navSheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = navDragRef.current.currentY;
    navDragRef.current = { active: false, startY: 0, currentY: 0 };

    if (delta > 56) {
      navSheetRef.current.style.transition =
        "transform .18s ease, opacity .18s ease";
      navSheetRef.current.style.transform = "translateY(110%)";
      navSheetRef.current.style.opacity = "0";
      window.setTimeout(() => {
        setMobileNavOpen(false);
        if (navSheetRef.current) {
          navSheetRef.current.style.transition = "";
          navSheetRef.current.style.transform = "";
          navSheetRef.current.style.opacity = "";
        }
      }, 190);
      return;
    }

    navSheetRef.current.style.transition =
      "transform .16s cubic-bezier(0.2, 0, 0, 1)";
    navSheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!navSheetRef.current || navDragRef.current.active) return;
      navSheetRef.current.style.transition = "";
      navSheetRef.current.style.transform = "";
    }, 170);
  }

  const closeMobileSurfaces = () => {
    setMobileNavOpen(false);
    closeMobileMore(false);
    setNotificationsOpen(false);
  };

  function requestLogout() {
    setShowLogoutConfirm(true);
    setMobileNavOpen(false);
    closeMobileMore(false);
    setNotificationsOpen(false);
  }

  function confirmLogout() {
    if (logoutWorking) return;
    setShowLogoutConfirm(false);
    setLogoutWorking(true);
    logoutTimerRef.current = window.setTimeout(() => {
      logout();
      navigate("/login", { replace: true });
    }, 650);
  }

  const location = useLocation();
  useEffect(() => {
    let active = true;
    const loadDiscoveryCount = () =>
      importCandidatesApi
        .count()
        .then((result) => {
          if (active)
            setDiscoveryPendingCount(Number(result?.pending_count || 0));
        })
        .catch(() => {});
    loadDiscoveryCount();
    window.addEventListener("focus", loadDiscoveryCount);
    return () => {
      active = false;
      window.removeEventListener("focus", loadDiscoveryCount);
    };
  }, [location.pathname, location.search]);
  const hideGlobalAsk =
    location.pathname === "/app/family" ||
    location.pathname.startsWith("/shared/");
  useEffect(() => {
    setMobileNavOpen(false);
    setMobileMoreOpen(false);
    setMobileMoreClosing(false);
    setNotificationsOpen(false);
    if (moreCloseTimerRef.current) {
      window.clearTimeout(moreCloseTimerRef.current);
      moreCloseTimerRef.current = null;
    }
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    "hm-legacy-nav-link" + (isActive ? " is-active" : "");
  const outcomeNavClass = (paths) =>
    `hm-legacy-nav-link${paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`)) ? " is-active" : ""}`;

  const askPrompts = [
    "What needs attention this week?",
    "What can I handle before a trip?",
    "Where can we save money?",
    "What should I add to make HouseMind smarter?",
  ];
  const contextualAskPrompts = {
    food: [
      "What should I use first?",
      "What meals fit my pantry?",
      "What groceries do I still need?",
      "Any allergy conflicts?",
    ],
    people: [
      "Who has an occasion coming up?",
      "What gift ideas fit saved interests?",
      "What should I budget for gifts?",
      "What person details should I add?",
    ],
    vault: [
      "What warranties expire soon?",
      "What should I maintain?",
      "What should I add to Home Vault?",
      "What records need dates?",
    ],
    events: [
      "What needs attention next?",
      "What should I plan before this event?",
      "What am I missing for this trip?",
      "What should I handle before leaving?",
    ],
    vehicles: [
      "What service is due soon?",
      "What vehicle records are missing?",
      "Any renewals or recalls to check?",
      "What should I log next?",
    ],
    subscriptions: [
      "What should I cancel or review?",
      "Where can we consolidate?",
      "What renewals are coming soon?",
      "Where are we overspending?",
    ],
    utilities: [
      "What bills are due soon?",
      "Why is my bill up?",
      "How can I save?",
      "What usage should I compare?",
    ],
    home_care: [
      "What maintenance is due?",
      "What repair should I handle next?",
      "Show upcoming home work",
      "Which professional should I contact?",
    ],
    property_profile: [
      "Tell me about my property profile",
      "Which rooms or home facts are missing?",
      "Where is my equipment placed?",
      "What coverage needs attention?",
    ],
    emergency_safety: [
      "How ready is my household?",
      "What safety checks are due?",
      "Which emergency details are missing?",
      "Where are my shutoffs?",
    ],
    pets: [
      "What pet care is due?",
      "Which pet details are missing?",
      "What should I plan before a trip?",
      "Show upcoming vaccinations or visits",
    ],
    budget: [
      "How is this month's budget?",
      "Where are we overspending?",
      "What costs are coming up?",
      "Which amounts are missing?",
    ],
    home: [
      "Tell me about my property profile",
      "What needs attention at home?",
      "What maintenance or safety work is next?",
      "Which home records are incomplete?",
    ],
    plans: [
      "What is coming up?",
      "What should I prepare for?",
      "Does pet care conflict with a trip?",
      "Which occasions need action?",
    ],
    money: [
      "How is this month's budget?",
      "What bills or renewals are next?",
      "Where can I save?",
      "Which planned costs need amounts?",
    ],
  };
  const activeModule = location.pathname.startsWith("/app/admin")
    ? "Admin"
    : location.pathname.startsWith("/app/smart-home")
      ? "Smart Home AI"
      : location.pathname === "/app/home"
        ? "Home"
        : location.pathname === "/app/plans"
          ? "People & Plans"
          : location.pathname.startsWith("/app/money/overview")
            ? "Household Budget"
            : location.pathname === "/app/money"
              ? "Money"
              : MODULES.find((m) => location.pathname.startsWith(m.path))
                  ?.name || "Dashboard";
  const routeAskContext = location.pathname.startsWith("/app/home-vault")
    ? { module: "vault", module_label: "Home Vault", surface: "Home Vault" }
    : location.pathname.startsWith("/app/property-profile")
      ? {
          module: "property_profile",
          module_label: "Property Profile",
          surface: "Property Profile",
        }
      : location.pathname.startsWith("/app/emergency-safety")
        ? {
            module: "emergency_safety",
            module_label: "Emergency & Safety",
            surface: "Emergency & Safety",
          }
        : null;
  const dashboardGlobalAsk =
    !askContext && !routeAskContext && activeModule === "Dashboard";
  const globalAskFilterContext = useMemo(
    () =>
      dashboardGlobalAsk ? globalAskContextFromKey(askGlobalFilter) : null,
    [askGlobalFilter, dashboardGlobalAsk],
  );
  const effectiveAskContext =
    askContext || routeAskContext || globalAskFilterContext;
  const effectiveAskModule = dashboardGlobalAsk
    ? "Dashboard"
    : effectiveAskContext?.module_label || activeModule;
  const effectiveAskPrompts =
    contextualAskPrompts[effectiveAskContext?.module] || askPrompts;
  const effectiveAskPlaceholder = askPlaceholderOverride || "Ask HouseMind...";
  const mobilePrimaryNav = [
    { label: "Today", path: "/app", icon: "🌅", paths: ["/app"], exact: true },
    {
      label: "Home",
      path: "/app/home",
      icon: "🏡",
      paths: [
        "/app/home",
        "/app/food",
        "/app/home-vault",
        "/app/home-care",
        "/app/property-profile",
        "/app/emergency-safety",
        "/app/vehicles",
      ],
    },
    {
      label: "Plans",
      path: "/app/plans",
      icon: "🗓",
      paths: [
        "/app/plans",
        "/app/people",
        "/app/events",
        "/app/timeline",
        "/app/pets",
        "/app/trip",
        "/app/event-plan",
      ],
    },
    {
      label: "Money",
      path: "/app/money",
      icon: "💰",
      paths: ["/app/money", "/app/utilities", "/app/subscriptions"],
    },
  ];
  const isMobilePrimaryItemActive = (item) =>
    item.paths.some((path) =>
      item.exact
        ? location.pathname === path &&
          !new URLSearchParams(location.search).has("view")
        : location.pathname === path ||
          location.pathname.startsWith(`${path}/`),
    );
  const mobilePrimaryActive = mobilePrimaryNav.some(isMobilePrimaryItemActive);
  const mobileMoreActive = mobileMoreOpen || !mobilePrimaryActive;
  const speechRecognitionSupported =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const hasFamilyPlan =
    user?.plan === "family" ||
    user?.workspace?.plan === "family" ||
    user?.workspaces?.some((workspace) => workspace.plan === "family");

  const moduleEmoji = {
    food: "🥬",
    gifts: "🎁",
    utilities: "💡",
    vehicles: "🚗",
    subs: "🔁",
    subscriptions: "🔁",
    vault: "🏡",
    trips: "✈️",
    events: "✈️",
    sharing: "👥",
    smart_home: "⌂",
    dashboard: "☀",
  };

  function groupNotifications(items) {
    const today = new Date();
    const todayKey = today.toDateString();
    const isToday = (item) =>
      item.created_at && new Date(item.created_at).toDateString() === todayKey;
    const needsAction = (item) =>
      ["urgent", "attention", "warning", "critical", "error"].includes(
        String(item.type || "").toLowerCase(),
      );
    return {
      action: items.filter((item) => isToday(item) && needsAction(item)),
      updates: items.filter((item) => isToday(item) && !needsAction(item)),
      earlier: items.filter((item) => !isToday(item)),
    };
  }

  function notificationModuleLabel(value) {
    const key = String(value || "HouseMind").toLowerCase();
    return (
      { smart_home: "Smart Home", subs: "Subscriptions" }[key] ||
      key.replaceAll("_", " ")
    ).replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function relativeNotificationTime(value) {
    if (!value) return "";
    const raw = String(value).trim();
    const timestamp = new Date(
      /[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`,
    ).getTime();
    if (!Number.isFinite(timestamp)) return "";
    const diffMs = Math.max(0, Date.now() - timestamp);
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  function normalizeNotificationTarget(url) {
    const value = String(url || "");
    const marker = "/event/";
    if (!value.includes(marker)) return value;
    const slug = value
      .split(marker)[1]
      ?.split(/[?#]/)[0]
      ?.replace(/^\/+|\/+$/g, "");
    return slug ? `/app/events?rsvp_slug=${encodeURIComponent(slug)}` : value;
  }

  async function refreshNotificationCount() {
    try {
      const result = await notificationApi.count();
      setNotificationCount(result?.count || 0);
    } catch {
      setNotificationCount(0);
    }
  }

  async function openNotifications() {
    if (notificationsOpen) {
      setNotificationsOpen(false);
      return;
    }
    setNotificationsOpen(true);
    setMobileNavOpen(false);
    closeMobileMore(false);
    setNotificationsBusy(true);
    try {
      const feed = await notificationApi.feed();
      setNotifications(Array.isArray(feed?.items) ? feed.items : []);
      setNotificationCount(feed?.count || 0);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsBusy(false);
    }
  }

  async function openNotification(item) {
    let targetUrl = normalizeNotificationTarget(item.action_url);
    setNotifications((items) =>
      items.filter((existing) => existing.id !== item.id),
    );
    if (!item.is_read) {
      setNotificationCount((count) => Math.max(0, count - 1));
    }
    try {
      const updated = await notificationApi.readOne(item.id);
      targetUrl = normalizeNotificationTarget(updated?.action_url || targetUrl);
      await refreshNotificationCount();
    } catch {}
    setNotificationsOpen(false);
    if (targetUrl) navigate(targetUrl);
  }

  function removeNotificationFromView(item) {
    setNotifications((items) =>
      items.filter((existing) => existing.id !== item.id),
    );
    if (!item.is_read) {
      setNotificationCount((count) => Math.max(0, count - 1));
    }
  }

  async function dismissNotification(item, direction = 1) {
    if (!item?.id) return;
    setNotificationDismissing({ id: item.id, direction });
    setNotificationSwipeId(null);
    setNotificationSwipeOffset(0);
    window.setTimeout(() => {
      removeNotificationFromView(item);
      setNotificationDismissing((current) =>
        current?.id === item.id ? null : current,
      );
    }, 180);
    try {
      await notificationApi.dismissOne(item.id);
      await refreshNotificationCount();
    } catch {
      try {
        const feed = await notificationApi.feed();
        setNotifications(Array.isArray(feed?.items) ? feed.items : []);
        setNotificationCount(feed?.count || 0);
      } catch {}
    }
  }

  function startNotificationGesture(event, item) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.stopPropagation();
    notificationSuppressClickRef.current = false;
    notificationGestureRef.current = {
      id: item.id,
      startX: event.clientX,
      startY: event.clientY,
    };
    setNotificationSwipeOffset(0);
  }

  function moveNotificationGesture(event) {
    const gesture = notificationGestureRef.current;
    if (!gesture.id) return;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (Math.abs(deltaX) > 18 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      event.preventDefault();
      event.stopPropagation();
      notificationSuppressClickRef.current = true;
      setNotificationSwipeId(gesture.id);
      setNotificationSwipeOffset(Math.max(-92, Math.min(92, deltaX)));
    }
  }

  function endNotificationGesture(event, item) {
    event.stopPropagation();
    const gesture = notificationGestureRef.current;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    notificationGestureRef.current = { id: null, startX: 0, startY: 0 };
    if (
      gesture.id === item.id &&
      Math.abs(deltaX) > 70 &&
      Math.abs(deltaX) > Math.abs(deltaY) * 1.2
    ) {
      notificationSuppressClickRef.current = true;
      dismissNotification(item, deltaX < 0 ? -1 : 1);
      return;
    }
    setNotificationSwipeId(null);
    setNotificationSwipeOffset(0);
  }

  async function clearNotifications() {
    try {
      await notificationApi.clear();
      setNotifications([]);
      setNotificationCount(0);
    } catch {}
  }

  function trustedUpdateIdFor(item) {
    if (item?.type !== "trusted_update" || !item.rule_id) return null;
    const match = /^trusted_update:(\d+)$/.exec(item.rule_id);
    return match ? Number(match[1]) : null;
  }

  async function undoTrustedUpdate(item, event) {
    event?.stopPropagation?.();
    const updateId = trustedUpdateIdFor(item);
    if (!updateId || undoBusyId) return;
    setUndoBusyId(item.id);
    try {
      await importCandidatesApi.undoTrustedUpdate(updateId);
      await notificationApi.readOne(item.id);
      setNotifications((items) =>
        items.map((existing) =>
          existing.id === item.id
            ? {
                ...existing,
                is_read: true,
                body: `${existing.body || ""} (undone)`.trim(),
              }
            : existing,
        ),
      );
      await refreshNotificationCount();
    } catch {
      // Leave the notification as-is so the user can retry, or fix the record directly.
    } finally {
      setUndoBusyId(null);
    }
  }

  function handleNotificationClick(item) {
    if (notificationSuppressClickRef.current) {
      notificationSuppressClickRef.current = false;
      return;
    }
    openNotification(item);
  }

  useEffect(() => {
    let alive = true;
    const loadCount = () => {
      const onDashboard = window.location.pathname === "/app" || window.location.pathname === "/app/";
      (onDashboard ? dashboardApi.bootstrap() : notificationApi.count())
        .then((result) => {
          if (alive) setNotificationCount(result?.notification_count ?? result?.count ?? 0);
        })
        .catch(() => {
          if (alive) setNotificationCount(0);
        });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") loadCount();
    };
    const onDashboardBootstrap = (event) => {
      if (alive) setNotificationCount(event.detail?.notification_count || 0);
    };
    loadCount();
    window.addEventListener("hm_notifications_refresh", loadCount);
    window.addEventListener("hm_dashboard_bootstrap", onDashboardBootstrap);
    window.addEventListener("focus", loadCount);
    document.addEventListener("visibilitychange", onVisible);
    // Focus/visibility and explicit refresh events keep the badge responsive.
    // A five-minute fallback avoids continuously rebuilding notification state
    // against the remote database while the app sits open.
    const timer = window.setInterval(loadCount, 5 * 60 * 1000);
    return () => {
      alive = false;
      window.removeEventListener("hm_notifications_refresh", loadCount);
      window.removeEventListener("hm_dashboard_bootstrap", onDashboardBootstrap);
      window.removeEventListener("focus", loadCount);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!notificationsOpen) return undefined;

    const closeWhenOutside = (event) => {
      if (notificationPanelRef.current?.contains(event.target)) return;
      setNotificationsOpen(false);
    };
    let touchStart = null;
    const rememberTouchStart = (event) => {
      const touch = event.touches?.[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    };
    const closeOnVerticalTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch || !touchStart) return;
      if (notificationPanelRef.current?.contains(event.target)) return;
      const deltaX = Math.abs(touch.clientX - touchStart.x);
      const deltaY = Math.abs(touch.clientY - touchStart.y);
      if (deltaY > 6 && deltaY > deltaX) setNotificationsOpen(false);
    };

    document.addEventListener("pointerdown", closeWhenOutside, true);
    document.addEventListener("touchstart", rememberTouchStart, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchmove", closeOnVerticalTouchMove, {
      capture: true,
      passive: true,
    });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside, true);
      document.removeEventListener("touchstart", rememberTouchStart, true);
      document.removeEventListener("touchmove", closeOnVerticalTouchMove, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [notificationsOpen]);

  useEffect(
    () => () => {
      if (askRecognitionRef.current) {
        askRecognitionRef.current.stop();
        askRecognitionRef.current = null;
      }
      if (moreCloseTimerRef.current) {
        window.clearTimeout(moreCloseTimerRef.current);
        moreCloseTimerRef.current = null;
      }
      if (logoutTimerRef.current) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    },
    [],
  );

  function openAskHouseMind() {
    setAskContext(null);
    setAskQuestion("");
    setAskPlaceholderOverride("");
    const context =
      activeModule === "Dashboard"
        ? globalAskContextFromKey(askGlobalFilter)
        : null;
    setAskMessages(readAskMessages(context));
    setAskOpen(true);
    setMobileNavOpen(false);
    closeMobileMore(false);
    setNotificationsOpen(false);
  }

  useEffect(() => {
    const openFromModule = (event) => {
      const question = event.detail?.question;
      const nextContext = event.detail?.context || null;
      setAskContext(nextContext);
      if (
        event.detail?.prefill === true &&
        Object.prototype.hasOwnProperty.call(event.detail || {}, "question")
      ) {
        setAskQuestion(question || "");
        setAskPlaceholderOverride("");
      } else {
        setAskQuestion("");
        setAskPlaceholderOverride("");
      }
      setAskMessages(readAskMessages(nextContext));
      setAskError("");
      setAskOpen(true);
      setMobileNavOpen(false);
      setMobileMoreOpen(false);
      setMobileMoreClosing(false);
      setNotificationsOpen(false);
    };
    window.addEventListener("hm_open_ask", openFromModule);
    return () => window.removeEventListener("hm_open_ask", openFromModule);
  }, []);

  useEffect(() => {
    if (!askOpen || !dashboardGlobalAsk) return;
    setAskPlaceholderOverride("");
    setAskMessages(readAskMessages(globalAskFilterContext));
    setAskError("");
  }, [askGlobalFilter, askOpen, dashboardGlobalAsk, globalAskFilterContext]);

  useEffect(() => {
    if (!askOpen) return;
    askMessagesEndRef.current?.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });
  }, [askMessages, askBusy, askOpen]);

  const resizeAskTextarea = useCallback(() => {
    const textarea = askTextareaRef.current;
    if (!textarea) return;
    const maxHeight = 112;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    if (!askOpen) return;
    resizeAskTextarea();
  }, [askQuestion, askOpen, resizeAskTextarea]);

  async function askHouseMind(nextQuestion = askQuestion) {
    const clean = String(nextQuestion || "").trim();
    if (!clean || askBusy) return;
    if (askRecognitionRef.current) {
      askRecognitionRef.current.stop();
      askRecognitionRef.current = null;
      setAskListening(false);
    }
    const createdAt = new Date().toISOString();
    const contextForRequest = effectiveAskContext || null;
    const historyForRequest = askHistoryPayload(askMessages);
    const userMessage = userMessageFromQuestion(
      clean,
      contextForRequest,
      createdAt,
    );
    setAskMessages((current) => {
      const nextMessages = [...current, userMessage].filter(Boolean).slice(-20);
      writeAskMessages(contextForRequest, nextMessages);
      return nextMessages;
    });
    setAskQuestion("");
    setAskBusy(true);
    setAskError("");
    try {
      const result = await dashboardApi.ask({
        question: clean,
        location: effectiveAskModule,
        context: contextForRequest,
        module_filter: dashboardGlobalAsk ? askGlobalFilter : null,
        history: historyForRequest,
      });
      applyAiUsage(result.ai_usage);
      const assistantMessage = assistantMessageFromAskResult(
        {
          ...result,
          question: clean,
          context: contextForRequest,
          saved_at: new Date().toISOString(),
        },
        contextForRequest,
      );
      setAskMessages((current) => {
        const withReply = [...current, assistantMessage]
          .filter(Boolean)
          .slice(-20);
        writeAskMessages(contextForRequest, withReply);
        return withReply;
      });
    } catch (err) {
      const message = err.message || "HouseMind could not answer right now.";
      if (message === "ai_not_configured") {
        setAskError("AI is not configured for this environment.");
      } else {
        setAskError(message);
      }
    } finally {
      setAskBusy(false);
    }
  }

  function startNewAskThread() {
    if (askBusy) return;
    const contextForThread = effectiveAskContext || null;
    clearAskMessages(contextForThread);
    setAskMessages([]);
    setAskQuestion("");
    if (dashboardGlobalAsk) setAskPlaceholderOverride("");
    setAskError("");
  }

  function toggleAskVoice() {
    setAskVoiceError("");
    if (!speechRecognitionSupported) {
      setAskVoiceError(
        "Voice input is not supported in this browser. You can still type your question.",
      );
      return;
    }

    if (askListening && askRecognitionRef.current) {
      askRecognitionRef.current.stop();
      askRecognitionRef.current = null;
      setAskListening(false);
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    const baseQuestion = askQuestion.trim();
    let finalTranscript = "";

    recognition.lang = navigator.language || "en-US";
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => setAskListening(true);
    recognition.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) {
          finalTranscript = `${finalTranscript} ${transcript}`.trim();
        } else {
          interimTranscript = `${interimTranscript} ${transcript}`.trim();
        }
      }
      const spokenQuestion = [baseQuestion, finalTranscript, interimTranscript]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (spokenQuestion) setAskQuestion(spokenQuestion);
    };
    recognition.onerror = (event) => {
      const message =
        event.error === "not-allowed"
          ? "Microphone permission was blocked. Allow microphone access to use voice input."
          : "Voice input stopped. You can try again or type your question.";
      setAskVoiceError(message);
      setAskListening(false);
    };
    recognition.onend = () => {
      askRecognitionRef.current = null;
      setAskListening(false);
    };

    askRecognitionRef.current = recognition;
    recognition.start();
  }

  function runAskAction(action) {
    if (!action?.url) return;
    navigate(action.url);
    setAskOpen(false);
  }

  return (
    <main className="hm-app">
      <style>{`
        @media (min-width: 769px) {
          .hm-notification-btn--desktop {
            position: relative;
            width: 100%;
            min-height: 38px;
            display: grid;
            grid-template-columns: 24px minmax(0, 1fr);
            align-items: center;
            gap: 10px;
            padding: 0 12px;
            border: 1px solid var(--hm-line);
            border-radius: 11px;
            background: var(--hm-surface);
            color: var(--hm-text);
            cursor: pointer;
            text-align: left;
            box-shadow: 0 5px 14px rgba(28, 26, 22, .08);
          }
          .hm-brand {
            align-items: stretch;
            flex-direction: column;
            gap: 10px;
          }
          .hm-brand-link {
            align-self: flex-start;
          }
          .hm-notification-btn--desktop span {
            font-size: 17px;
            line-height: 1;
          }
          .hm-notification-btn--desktop em {
            position: absolute;
            top: 10px;
            right: 12px;
            min-width: 16px;
            height: 16px;
            display: grid;
            place-items: center;
            padding: 0 4px;
            border-radius: 999px;
            background: var(--hm-red);
            color: #fff;
            font-size: 9px;
            font-style: normal;
            font-weight: 900;
            line-height: 1;
          }
          .hm-notification-scrim--desktop {
            position: fixed;
            inset: 0;
            z-index: 225;
            display: block;
            background: rgba(28, 26, 22, .08);
          }
          .hm-notification-popover--desktop {
            position: fixed;
            top: 72px;
            left: 252px;
            z-index: 230;
            width: 390px;
            max-width: calc(100vw - 292px);
            max-height: min(72vh, 620px);
            overflow-y: auto;
            display: grid;
            gap: 10px;
            padding: 14px;
            border: 1px solid var(--hm-line);
            border-radius: 18px;
            background: var(--hm-card-solid);
            box-shadow: 0 18px 46px rgba(28, 26, 22, .2);
          }
          .hm-notification-popover--desktop .hm-notification-head {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 14px;
            padding-bottom: 10px;
            border-bottom: 1px solid var(--hm-line);
          }
          .hm-notification-popover--desktop .hm-notification-head span {
            display: block;
            color: var(--hm-muted);
            font-size: 10px;
            font-weight: 900;
            letter-spacing: .08em;
            line-height: 1;
            text-transform: uppercase;
          }
          .hm-notification-popover--desktop .hm-notification-head strong {
            display: block;
            margin-top: 4px;
            color: var(--hm-text);
            font-size: 17px;
            font-weight: 900;
            line-height: 1.15;
          }
          .hm-notification-popover--desktop .hm-notification-actions {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .hm-notification-popover--desktop .hm-notification-actions button {
            min-height: 30px;
            border: 1px solid var(--hm-line);
            border-radius: 999px;
            background: #fff;
            color: var(--hm-text);
            cursor: pointer;
            font-size: 12px;
            font-weight: 800;
            line-height: 1;
            padding: 0 10px;
          }
          .hm-notification-popover--desktop .hm-notification-actions button:last-child {
            width: 30px;
            padding: 0;
            color: var(--hm-muted);
          }
          .hm-notification-popover--desktop .hm-notification-actions button:disabled {
            cursor: not-allowed;
            opacity: .45;
          }
          .hm-notification-popover--desktop .hm-notification-empty {
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 82px;
            justify-content: center;
            color: var(--hm-muted);
            font-size: 13px;
            font-weight: 700;
            text-align: center;
          }
          .hm-notification-popover--desktop .hm-notification-group {
            display: grid;
            gap: 7px;
          }
          .hm-notification-popover--desktop .hm-notification-group h3 {
            margin: 0;
            color: var(--hm-muted);
            font-size: 10px;
            font-weight: 900;
            letter-spacing: .08em;
            line-height: 1;
            text-transform: uppercase;
          }
          .hm-notification-popover--desktop .hm-notification-item {
            box-sizing: border-box;
            min-width: 0;
            overflow: hidden;
            width: 100%;
            display: grid;
            grid-template-columns: 30px minmax(0, 1fr) 18px;
            align-items: center;
            gap: 10px;
            border: 1px solid var(--hm-line);
            border-radius: 13px;
            background: #fff;
            box-shadow: 0 1px 2px rgba(28, 26, 22, .04);
            color: var(--hm-text);
            cursor: pointer;
            padding: 10px;
            text-align: left;
          }
          .hm-notification-popover--desktop .hm-notification-item.is-read {
            opacity: .72;
          }
          .hm-notification-popover--desktop .hm-notification-icon {
            width: 30px;
            height: 30px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid var(--hm-line);
            border-radius: 9px;
            background: var(--hm-surface);
            font-size: 15px;
            line-height: 1;
          }
          .hm-notification-popover--desktop .hm-notification-copy {
            min-width: 0;
            display: grid;
            gap: 3px;
          }
          .hm-notification-popover--desktop .hm-notification-copy > span {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            gap: 6px;
            min-width: 0;
          }
          .hm-notification-popover--desktop .hm-notification-copy strong {
            min-width: 0;
            overflow: hidden;
            color: var(--hm-text);
            font-size: 13px;
            font-weight: 900;
            line-height: 1.25;
            overflow-wrap: anywhere;
            white-space: normal;
          }
          .hm-notification-popover--desktop .hm-notification-copy em {
            flex-shrink: 0;
            color: var(--hm-muted);
            font-size: 10px;
            font-style: normal;
            font-weight: 800;
          }
          .hm-notification-popover--desktop .hm-notification-copy time {
            flex-shrink: 0;
            color: var(--hm-muted);
            font-size: 10px;
            font-weight: 700;
          }
          .hm-notification-popover--desktop .hm-notification-copy small {
            color: var(--hm-muted);
            display: -webkit-box;
            font-size: 12px;
            line-height: 1.35;
            overflow: hidden;
            overflow-wrap: anywhere;
            word-break: break-word;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
          }
          .hm-notification-popover--desktop .hm-notification-repeat {
            justify-self: start;
            width: fit-content;
            padding: 2px 7px;
            border-radius: 999px;
            background: #eef6f3;
            color: #0f766e;
            font-size: 9px;
            font-weight: 850;
          }
          .hm-notification-popover--desktop .hm-notification-undo {
            justify-self: start;
            width: fit-content;
            padding: 3px 10px;
            margin-top: 2px;
            border: 1px solid #a7d9cd;
            border-radius: 999px;
            background: #eef6f3;
            color: #0f766e;
            font-size: 11px;
            font-weight: 850;
            cursor: pointer;
          }
          .hm-notification-popover--desktop .hm-notification-undo:hover {
            background: #dcefe8;
          }
          .hm-notification-popover--desktop .hm-notification-arrow {
            color: var(--hm-muted);
            font-size: 14px;
            font-weight: 900;
            line-height: 1;
            text-align: right;
          }
        }
      `}</style>
      {/* Mobile top bar */}
      <div className="hm-mobile-topbar">
        <button
          className="hm-notification-btn"
          type="button"
          onClick={openNotifications}
          aria-expanded={notificationsOpen}
          aria-label="Open notifications"
        >
          <span>🔔</span>
          {notificationCount > 0 && (
            <em>{notificationCount > 9 ? "9+" : notificationCount}</em>
          )}
        </button>
        <NavLink
          to="/app"
          className="hm-mobile-brand hm-brand-link"
          onClick={closeMobileSurfaces}
          aria-label="HouseMind dashboard"
        >
          <HouseMindLogoIcon />
          <strong className="hm-brand-word hm-mobile-brand-word">
            House<span>Mind</span>
          </strong>
          <span className="hm-mobile-module-label">{activeModule}</span>
        </NavLink>
        <div className="hm-mobile-actions">
          <button
            className="hm-hamburger"
            onClick={() => {
              setNotificationsOpen(false);
              closeMobileMore(false);
              setMobileNavOpen((o) => !o);
            }}
            aria-label={
              mobileNavOpen ? "Close account menu" : "Open account menu"
            }
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {!isStandaloneApp && !installBannerDismissed && (
        <div className="hm-install-nudge">
          <div>
            <strong>Install HouseMind</strong>
            <span>
              {canPromptInstall
                ? "Ready to add to your home screen."
                : "Use this if Chrome does not show the install prompt."}
            </span>
          </div>
          <button type="button" onClick={handleInstallApp}>
            {canPromptInstall ? "Install" : "How"}
          </button>
          <button
            type="button"
            className="hm-install-nudge__close"
            onClick={dismissInstallBanner}
            aria-label="Dismiss install prompt"
          >
            ×
          </button>
        </div>
      )}

      {showInstallHelp && (
        <div
          className="hm-install-help"
          role="dialog"
          aria-modal="true"
          aria-label="Install HouseMind"
        >
          <button
            type="button"
            className="hm-install-help__scrim"
            onClick={() => setShowInstallHelp(false)}
            aria-label="Close install help"
          />
          <div className="hm-install-help__card">
            <h3>Install HouseMind</h3>
            <p>
              In Chrome on Android, open the three-dot menu and choose{" "}
              <strong>Install app</strong> or{" "}
              <strong>Add to Home screen</strong>. If it still does not appear,
              clear site data for housemindos.com, reopen <strong>/app</strong>,
              and refresh once.
            </p>
            <button type="button" onClick={() => setShowInstallHelp(false)}>
              Got it
            </button>
          </div>
        </div>
      )}

      {notificationsOpen && (
        <>
          <div
            className="hm-notification-scrim hm-notification-scrim--desktop"
            onClick={() => setNotificationsOpen(false)}
          />
          <div
            className="hm-notification-popover hm-notification-popover--desktop"
            role="dialog"
            aria-label="Notifications"
            ref={notificationPanelRef}
            onPointerDown={startNotificationDrag}
            onPointerMove={moveNotificationDrag}
            onPointerUp={endNotificationDrag}
            onPointerCancel={endNotificationDrag}
          >
            <div className="hm-notification-head">
              <div>
                <span>Notifications</span>
                <strong>
                  {notificationCount
                    ? `${notificationCount} unread`
                    : "All caught up"}
                </strong>
              </div>
              <div className="hm-notification-actions">
                <button
                  type="button"
                  onClick={clearNotifications}
                  disabled={!notifications.length}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setNotificationsOpen(false)}
                  aria-label="Close notifications"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="hm-notification-scroll">
            {notificationsBusy ? (
              <div className="hm-notification-empty">
                <span className="spinner" /> Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="hm-notification-empty">
                No notifications right now.
              </div>
            ) : (
              (() => {
                const grouped = groupNotifications(notifications);
                return (
                  <>
                    {["action", "updates", "earlier"].map(
                      (key) =>
                        grouped[key].length > 0 && (
                          <section key={key} className="hm-notification-group">
                            <h3>
                              {key === "action"
                                ? "Needs action"
                                : key === "updates"
                                  ? "Updates"
                                  : "Earlier"}
                            </h3>
                            {grouped[key].map((item) => {
                              const undoId = trustedUpdateIdFor(item);
                              const alreadyUndone = /\(undone\)\s*$/.test(
                                item.body || "",
                              );
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  className={`hm-notification-item${item.is_read ? " is-read" : ""}${notificationSwipeId === item.id ? " is-swiping" : ""}${notificationDismissing?.id === item.id ? ` is-dismissing-${notificationDismissing.direction < 0 ? "left" : "right"}` : ""}`}
                                  style={notificationSwipeId === item.id ? { "--notification-swipe-x": `${notificationSwipeOffset}px` } : undefined}
                                  onPointerDown={(event) => startNotificationGesture(event, item)}
                                  onPointerMove={moveNotificationGesture}
                                  onPointerUp={(event) => endNotificationGesture(event, item)}
                                  onPointerCancel={(event) => endNotificationGesture(event, item)}
                                  onClick={() => handleNotificationClick(item)}
                                >
                                  <span className="hm-notification-icon">
                                    {moduleEmoji[item.source_module] || "•"}
                                  </span>
                                  <span className="hm-notification-copy">
                                    <span className="hm-notification-meta">
                                      <em>
                                        {notificationModuleLabel(
                                          item.source_module,
                                        )}
                                      </em>
                                      <time>
                                        {relativeNotificationTime(
                                          item.created_at,
                                        )}
                                      </time>
                                    </span>
                                    <strong>{item.title}</strong>
                                    {item.body && <small>{item.body}</small>}
                                    {item.repeat_count > 1 && (
                                      <span className="hm-notification-repeat">
                                        {item.repeat_count} identical alerts
                                        grouped
                                      </span>
                                    )}
                                    {undoId && !alreadyUndone && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        className="hm-notification-undo"
                                        onPointerDown={(event) =>
                                          event.stopPropagation()
                                        }
                                        onClick={(event) =>
                                          undoTrustedUpdate(item, event)
                                        }
                                        onKeyDown={(event) => {
                                          if (
                                            event.key === "Enter" ||
                                            event.key === " "
                                          ) {
                                            event.preventDefault();
                                            undoTrustedUpdate(item, event);
                                          }
                                        }}
                                      >
                                        {undoBusyId === item.id
                                          ? "Undoing…"
                                          : "Undo this update"}
                                      </span>
                                    )}
                                  </span>
                                  <span className="hm-notification-arrow">
                                    {item.is_read ? "✓" : "›"}
                                  </span>
                                </button>
                              );
                            })}
                          </section>
                        ),
                    )}
                  </>
                );
              })()
            )}
            </div>
          </div>
        </>
      )}

      {/* Backdrop */}
      {mobileNavOpen && <div className="hm-nav-backdrop" onClick={closeNav} />}

      {/* Sidebar */}
      <aside
        className={`hm-sidebar${mobileNavOpen ? " hm-sidebar--open" : ""}`}
        ref={navSheetRef}
        onPointerDown={startNavDrag}
        onPointerMove={moveNavDrag}
        onPointerUp={endNavDrag}
        onPointerCancel={endNavDrag}
      >
        <button
          type="button"
          className="hm-sidebar-edge-handle"
          onPointerDown={startNavHandleDrag}
          onPointerMove={moveNavHandleDrag}
          onPointerUp={endNavHandleDrag}
          onPointerCancel={endNavHandleDrag}
          aria-label="Drag down to close menu"
        />
        <div className="hm-brand">
          <NavLink to="/app" className="hm-brand-link" onClick={closeNav}>
            <HouseMindLogoIcon />
            <strong className="hm-brand-word">
              House<span>Mind</span>
            </strong>
          </NavLink>
          <button
            type="button"
            className="hm-sidebar-close"
            onClick={closeNav}
            aria-label="Close menu"
          >
            ×
          </button>
          <button
            className="hm-notification-btn--desktop"
            type="button"
            onClick={openNotifications}
            aria-expanded={notificationsOpen}
            aria-label="Open notifications"
          >
            <span>🔔</span>
            Notifications
            {notificationCount > 0 && (
              <em>{notificationCount > 9 ? "9+" : notificationCount}</em>
            )}
          </button>
        </div>

        <div className="hm-sidebar-profile">
          <span>{userInitials}</span>
          <div>
            <strong>{firstName}</strong>
            <small>{user?.email || ""}</small>
          </div>
        </div>

        <nav className="hm-module-nav hm-sidebar-module-nav">
          <small>Household</small>
          <NavLink
            to="/app"
            end
            className={() =>
              `hm-legacy-nav-link${location.pathname === "/app" && !new URLSearchParams(location.search).has("view") ? " is-active" : ""}`
            }
            onClick={closeNav}
          >
            <span className="hm-nav-icon">◈</span>
            Today
          </NavLink>
          <small>Home</small>
          <NavLink
            to="/app/home"
            className={() =>
              outcomeNavClass([
                "/app/home",
                "/app/food",
                "/app/home-vault",
                "/app/home-care",
                "/app/property-profile",
                "/app/emergency-safety",
                "/app/vehicles",
              ])
            }
            onClick={closeNav}
          >
            <span className="hm-nav-icon">⌂</span>Home
          </NavLink>
          <small>People & plans</small>
          <NavLink
            to="/app/plans"
            className={() =>
              outcomeNavClass([
                "/app/plans",
                "/app/people",
                "/app/events",
                "/app/timeline",
                "/app/pets",
                "/app/trip",
                "/app/event-plan",
              ])
            }
            onClick={closeNav}
          >
            <span className="hm-nav-icon">👥</span>People & plans
          </NavLink>
          <small>Money</small>
          <NavLink
            to="/app/money"
            className={() =>
              outcomeNavClass([
                "/app/money",
                "/app/utilities",
                "/app/subscriptions",
              ])
            }
            onClick={closeNav}
          >
            <span className="hm-nav-icon">$</span>Money
          </NavLink>
          {hasFamilyPlan && (
            <>
              <small>Family</small>
              <NavLink
                to="/app/family"
                className={navLinkClass}
                onClick={closeNav}
              >
                <span className="hm-nav-icon">👥</span>
                Family sharing
              </NavLink>
            </>
          )}
          {user?.is_admin && (
            <>
              <small>Future home</small>
              <NavLink
                to="/app/smart-home"
                className={navLinkClass}
                onClick={closeNav}
              >
                <span className="hm-nav-icon">⌁</span>
                Smart Home AI
              </NavLink>
              <NavLink
                to="/app/admin"
                className={navLinkClass}
                onClick={closeNav}
              >
                <span className="hm-nav-icon">▣</span>
                Admin
              </NavLink>
            </>
          )}
        </nav>

        <div className="hm-sidebar-actions">
          {/* ── Group 1: Primary tools ── */}
          <button
            type="button"
            className="hm-legacy-nav-link hm-ask-nav-button"
            onClick={openAskHouseMind}
          >
            <span className="hm-nav-icon hm-nav-icon--teal hm-ask-star">✦</span>
            Ask HouseMind
          </button>
          <NavLink
            to="/app?view=discoveries"
            end
            className={() =>
              `hm-legacy-nav-link${location.pathname === "/app" && new URLSearchParams(location.search).get("view") === "discoveries" ? " is-active" : ""}`
            }
            onClick={closeNav}
          >
            <span className="hm-nav-icon hm-nav-icon--blue">🔍</span>
            Discoveries
            {discoveryPendingCount > 0 && (
              <em className="hm-nav-attention-badge">
                {discoveryPendingCount > 99 ? "99+" : discoveryPendingCount}
              </em>
            )}
          </NavLink>
          <NavLink
            to="/app/connections"
            className={navLinkClass}
            onClick={closeNav}
          >
            <span className="hm-nav-icon hm-nav-icon--purple">🔗</span>
            Connections
          </NavLink>

          <div className="hm-sidebar-group-divider" />

          {/* ── Group 2: Account ── */}
          <NavLink
            to="/app/settings"
            className={navLinkClass}
            onClick={closeNav}
          >
            <span className="hm-nav-icon hm-nav-icon--gray">⚙️</span>
            Settings
          </NavLink>
          <NavLink to="/app/guide" className={navLinkClass} onClick={closeNav}>
            <span className="hm-nav-icon hm-nav-icon--amber">📖</span>
            Guide
          </NavLink>
          <button
            type="button"
            className="hm-legacy-nav-link hm-sidebar-share-tool"
            onClick={handleMenuShareApp}
          >
            <span className="hm-nav-icon hm-nav-icon--gray">↗</span>
            Share HouseMind
          </button>
          {hasConnectedInbox === false && (
            <NavLink
              to="/app/settings#connectors"
              className={navLinkClass}
              onClick={closeNav}
              style={{ position: "relative" }}
            >
              <span className="hm-nav-icon hm-nav-icon--gray">📧</span>
              Connect inbox
              <span
                style={{
                  position: "absolute",
                  top: 6,
                  right: 8,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--teal, #2A7B6F)",
                  boxShadow: "0 0 0 2px var(--teal, #2A7B6F)",
                  animation: "hm-pulse 2s infinite",
                }}
              />
            </NavLink>
          )}

          {(user?.is_admin || !isStandaloneApp) && (
            <div className="hm-sidebar-group-divider" />
          )}

          {/* ── Group 3: Technical ── */}
          {!isStandaloneApp && (
            <button
              type="button"
              className="hm-legacy-nav-link hm-sidebar-share-tool"
              onClick={handleMenuInstallApp}
            >
              <span className="hm-nav-icon hm-nav-icon--gray">⌂</span>
              Install app
            </button>
          )}
          {user?.is_admin && (
            <NavLink
              to="/app/admin"
              className={({ isActive }) =>
                `hm-legacy-nav-link hm-sidebar-action-admin${isActive ? " is-active" : ""}`
              }
              onClick={closeNav}
            >
              <span className="hm-nav-icon hm-nav-icon--gray">▣</span>
              Admin
            </NavLink>
          )}

          <div className="hm-sidebar-signout">
            <span>{firstName}</span>
            <button
              type="button"
              onClick={requestLogout}
              disabled={logoutWorking}
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <section className="hm-main">
        <Outlet />
      </section>

      <nav
        className="hm-mobile-bottom-nav"
        aria-label="Primary mobile navigation"
      >
        {mobilePrimaryNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            className={() =>
              `hm-mobile-bottom-item${isMobilePrimaryItemActive(item) ? " is-active" : ""}`
            }
            onClick={closeMobileSurfaces}
          >
            <span className="hm-mobile-bottom-icon">{item.icon}</span>
            <span className="hm-mobile-bottom-label">{item.label}</span>
            {item.label === "Today" && discoveryPendingCount > 0 && (
              <span
                className="hm-mobile-nav-badge"
                aria-label={`${discoveryPendingCount} discoveries awaiting review`}
              >
                {discoveryPendingCount > 9 ? "9+" : discoveryPendingCount}
              </span>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          className={`hm-mobile-bottom-item hm-mobile-bottom-more${mobileMoreActive ? " is-active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMobileNavOpen(false);
            if (mobileMoreOpen) {
              closeMobileMore(true);
            } else {
              if (moreCloseTimerRef.current)
                window.clearTimeout(moreCloseTimerRef.current);
              moreCloseTimerRef.current = null;
              setMobileMoreClosing(false);
              setMobileMoreOpen(true);
            }
          }}
          aria-expanded={mobileMoreOpen}
          aria-label={
            mobileMoreOpen ? "Close more modules" : "Open more modules"
          }
        >
          <span
            className="hm-mobile-bottom-icon hm-mobile-bottom-more-icon"
            aria-hidden="true"
          >
            <span className="hm-mobile-more-grid">🧭</span>
            <span className="hm-mobile-more-close">⌄</span>
          </span>
          <span className="hm-mobile-bottom-label">More</span>
        </button>
      </nav>

      {/* ── Mobile More bottom sheet ── */}
      {mobileMoreOpen && (
        <div
          className={`hm-more-overlay${mobileMoreClosing ? " is-closing" : ""}`}
          onClick={() => closeMobileMore(true)}
        >
          <div
            className="hm-more-sheet"
            ref={moreSheetRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hm-more-heading"
          >
            <div
              className="hm-app-sheet-topbar"
              onPointerDown={startMoreDrag}
              onPointerMove={moveMoreDrag}
              onPointerUp={endMoreDrag}
              onPointerCancel={endMoreDrag}
            >
              <div className="hm-app-sheet-handle" />
              <button
                type="button"
                className="hm-app-sheet-close"
                onClick={() => closeMobileMore(true)}
                aria-label="Close more modules"
              >
                x
              </button>
            </div>
            <div className="hm-app-sheet-head hm-more-title">
              <div>
                <span>Explore</span>
                <h2 id="hm-more-heading">Where do you want to go?</h2>
              </div>
            </div>
            <div className="hm-more-list">
              {[
                {
                  label: "Home",
                  items: [
                    {
                      label: "Food & pantry",
                      path: "/app/food",
                      icon: "🥬",
                      accent: "#d97706",
                      light: "#fef9e7",
                      detail: "Meals, recipes, and shopping",
                    },
                    {
                      label: "Home Vault",
                      path: "/app/home-vault",
                      icon: "🏡",
                      accent: "#0d9488",
                      light: "#e8faf8",
                      detail: "Warranties, documents, and equipment",
                    },
                    {
                      label: "Home Care",
                      path: "/app/home-care",
                      icon: "🛠️",
                      accent: "#15806d",
                      light: "#eaf8f4",
                      detail: "Maintenance, repairs, projects, and pros",
                    },
                    {
                      label: "Property Profile",
                      path: "/app/property-profile",
                      icon: "🏠",
                      accent: "#16715f",
                      light: "#e9f7f3",
                      detail: "Rooms, systems, materials, and home facts",
                    },
                    {
                      label: "Emergency & Safety",
                      path: "/app/emergency-safety",
                      icon: "🛡️",
                      accent: "#a64d3e",
                      light: "#fff1ed",
                      detail: "Plans, shutoffs, safety checks, and contacts",
                    },
                    {
                      label: "Vehicles",
                      path: "/app/vehicles",
                      icon: "🚗",
                      accent: "#3b82f6",
                      light: "#eff6ff",
                      detail: "Service, mileage, and ownership",
                    },
                  ],
                },
                {
                  label: "People & plans",
                  items: [
                    {
                      label: "People & gifts",
                      path: "/app/people",
                      icon: "👥",
                      accent: "#e7614a",
                      light: "#fef2ef",
                      detail: "People, occasions, and gift planning",
                    },
                    {
                      label: "Events & trips",
                      path: "/app/events",
                      icon: "✈️",
                      accent: "#8b5cf6",
                      light: "#f5f3ff",
                      detail: "Plans, itineraries, and readiness",
                    },
                    {
                      label: "Timeline",
                      path: "/app/timeline",
                      icon: "🗓️",
                      accent: "#7c3aed",
                      light: "#f5f3ff",
                      detail: "All household dates in one view",
                    },
                    {
                      label: "Pets",
                      path: "/app/pets",
                      icon: "🐾",
                      accent: "#b65f7c",
                      light: "#faedf2",
                      detail: "Profiles, routines, appointments, and care",
                    },
                    ...(hasFamilyPlan
                      ? [
                          {
                            label: "Family sharing",
                            path: "/app/family",
                            icon: "🤝",
                            accent: "#db2777",
                            light: "#fdf2f8",
                            detail: "Household access and collaboration",
                          },
                        ]
                      : []),
                  ],
                },
                {
                  label: "Money",
                  items: [
                    {
                      label: "Household Budget",
                      path: "/app/money/overview",
                      icon: "📊",
                      accent: "#1f6f4a",
                      light: "#e4efe6",
                      detail: "Spending, budgets, trends, and categories",
                    },
                    {
                      label: "Utilities",
                      path: "/app/utilities",
                      icon: "⚡",
                      accent: "#f59e0b",
                      light: "#fffbeb",
                      detail: "Bills, usage, rates, and due dates",
                    },
                    {
                      label: "Subscriptions",
                      path: "/app/subscriptions",
                      icon: "🔁",
                      accent: "#10b981",
                      light: "#ecfdf5",
                      detail: "Renewals, increases, and decisions",
                    },
                  ],
                },
                ...(user?.is_admin
                  ? [
                      {
                        label: "Future home",
                        items: [
                          {
                            label: "Smart Home AI",
                            path: "/app/smart-home",
                            icon: "🏠",
                            accent: "#0891b2",
                            light: "#ecfeff",
                            detail:
                              "Connected-home intelligence · admin preview",
                          },
                        ],
                      },
                    ]
                  : []),
              ].map((group) => (
                <section className="hm-more-group" key={group.label}>
                  <h3>{group.label}</h3>
                  <div className="hm-more-group__items">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                          `hm-more-item${isActive ? " is-active" : ""}`
                        }
                        style={{
                          "--more-accent": item.accent,
                          "--more-light": item.light,
                        }}
                        onClick={() => closeMobileMore(false)}
                      >
                        <span className="hm-more-item-icon">{item.icon}</span>
                        <span className="hm-more-item-body">
                          <span className="hm-more-item-label">
                            {item.label}
                          </span>
                          <span className="hm-more-item-detail">
                            {item.detail}
                          </span>
                        </span>
                        <span className="hm-more-item-arrow">›</span>
                      </NavLink>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div
          className="fd-clear-overlay hm-logout-sheet-overlay"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="fd-clear-sheet hm-logout-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm sign out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fd-clear-sheet__chrome">
              <button
                className="fd-sheet-handle"
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                aria-label="Close sign out confirmation"
              />
              <button
                className="fd-clear-sheet__close"
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                aria-label="Close sign out confirmation"
              >
                ×
              </button>
            </div>
            <div className="fd-clear-sheet__head">
              <div className="fd-clear-sheet__warn">⚠ Sign out</div>
              <div className="fd-clear-sheet__title">
                Sign out of HouseMind?
              </div>
              <div className="fd-clear-sheet__sub">
                You'll need your email and password to sign back in.
              </div>
            </div>
            <div className="fd-clear-sheet__foot">
              <button
                type="button"
                className="fd-clear-cancel hm-logout-cancel"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={logoutWorking}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fd-clear-confirm hm-logout-confirm"
                onClick={confirmLogout}
                disabled={logoutWorking}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {logoutWorking && (
        <SavingNotice
          title="Signing out"
          message="Ending your HouseMind session..."
        />
      )}

      {!hideGlobalAsk && askOpen && (
        <div className="hm-global-ask is-open" onClick={closeAskSheet}>
          {askOpen && (
            <div
              className="hm-global-ask-panel"
              ref={askPanelRef}
              role="dialog"
              aria-label="Ask HouseMind"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="hm-app-sheet-topbar hm-global-ask-topbar"
                onPointerDown={startAskDrag}
                onPointerMove={moveAskDrag}
                onPointerUp={endAskDrag}
                onPointerCancel={endAskDrag}
              >
                <div className="hm-app-sheet-handle" />
                <button
                  type="button"
                  className="hm-app-sheet-close"
                  onClick={closeAskSheet}
                  aria-label="Close Ask HouseMind"
                >
                  x
                </button>
              </div>
              <div className="hm-app-sheet-head hm-global-ask-head">
                <div>
                  <span>Ask HouseMind</span>
                  <strong>{effectiveAskModule}</strong>
                </div>
                <button
                  type="button"
                  className="hm-global-ask-new"
                  onClick={startNewAskThread}
                  disabled={askBusy || askMessages.length === 0}
                >
                  New chat
                </button>
              </div>
              {dashboardGlobalAsk && (
                <div
                  className="hm-global-ask-contexts"
                  aria-label="Ask HouseMind context"
                >
                  {GLOBAL_ASK_CONTEXTS.map((context) => (
                    <button
                      key={context.key}
                      type="button"
                      className={
                        askGlobalFilter === context.key ? "is-active" : ""
                      }
                      onClick={() => setAskGlobalFilter(context.key)}
                      disabled={askBusy}
                    >
                      {context.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="hm-global-ask-thread" aria-live="polite">
                {askMessages.length === 0 && (
                  <div className="hm-global-ask-empty">
                    <div className="hm-global-ask-empty-orb" aria-hidden="true">
                      ✶
                    </div>
                    <strong>What can HouseMind help with?</strong>
                    <p>
                      Ask about your saved household details, where to find a
                      feature, or what needs attention next.
                    </p>
                  </div>
                )}

                {askMessages.map((message) => {
                  const result = message.result || {};
                  const suggestedActions =
                    result.suggested_actions || result.actions || [];
                  return (
                    <div
                      key={message.id}
                      className={`hm-global-ask-message hm-global-ask-message--${message.role}`}
                    >
                      <div className="hm-global-ask-bubble">
                        <p>{message.text}</p>
                        {message.role === "assistant" && (
                          <>
                            {!!result.highlights?.length && (
                              <div className="hm-global-ask-bubble-section">
                                <span>Highlights</span>
                                <ul>
                                  {result.highlights
                                    .slice(0, 4)
                                    .map((item, idx) => (
                                      <li key={idx}>{item}</li>
                                    ))}
                                </ul>
                              </div>
                            )}
                            {!!result.next_steps?.length && (
                              <div className="hm-global-ask-bubble-section">
                                <span>Next steps</span>
                                <ul>
                                  {result.next_steps
                                    .slice(0, 4)
                                    .map((item, idx) => (
                                      <li key={idx}>{item}</li>
                                    ))}
                                </ul>
                              </div>
                            )}
                            {!!suggestedActions.length && (
                              <div className="hm-global-ask-actions">
                                <span>Suggested actions</span>
                                <div>
                                  {suggestedActions
                                    .slice(0, 2)
                                    .map((action, idx) => (
                                      <button
                                        key={`${action.url}-${idx}`}
                                        type="button"
                                        onClick={() => runAskAction(action)}
                                      >
                                        <strong>{action.label} ↗</strong>
                                        {action.why && (
                                          <small>{action.why}</small>
                                        )}
                                      </button>
                                    ))}
                                </div>
                              </div>
                            )}
                            {!!result.modules?.length && (
                              <div className="hm-global-ask-modules">
                                {result.modules.slice(0, 5).map((module) => (
                                  <em key={module}>{module}</em>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {askBusy && (
                  <div className="hm-global-ask-message hm-global-ask-message--assistant">
                    <div className="hm-global-ask-bubble hm-global-ask-bubble--typing">
                      <div
                        className="hm-global-ask-loading"
                        aria-label="HouseMind is writing a response"
                      >
                        <span />
                        <span />
                        <span />
                      </div>
                      <small>
                        HouseMind is checking your household context...
                      </small>
                    </div>
                  </div>
                )}
                <div ref={askMessagesEndRef} />
              </div>

              <div className="hm-global-ask-prompts">
                {effectiveAskPrompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => askHouseMind(prompt)}
                    disabled={askBusy}
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              <form
                className="hm-global-ask-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  askHouseMind();
                }}
              >
                <textarea
                  ref={askTextareaRef}
                  value={askQuestion}
                  onChange={(e) => {
                    setAskQuestion(e.target.value);
                    window.requestAnimationFrame(resizeAskTextarea);
                  }}
                  placeholder={effectiveAskPlaceholder}
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!askBusy && askQuestion.trim()) askHouseMind();
                    }
                  }}
                />
                <button
                  className={`hm-global-ask-action${askQuestion.trim() ? " is-send" : " is-mic"}${askListening ? " is-listening" : ""}`}
                  type={askQuestion.trim() ? "submit" : "button"}
                  onClick={askQuestion.trim() ? undefined : toggleAskVoice}
                  disabled={askBusy}
                  title={
                    askQuestion.trim()
                      ? "Send message"
                      : speechRecognitionSupported
                        ? "Speak your question"
                        : "Voice input is not supported in this browser"
                  }
                  aria-label={
                    askQuestion.trim() ? "Send message" : "Speak your question"
                  }
                  aria-pressed={!askQuestion.trim() && askListening}
                >
                  {askBusy
                    ? "…"
                    : askQuestion.trim()
                      ? ">"
                      : askListening
                        ? "●"
                        : "Mic"}
                </button>
              </form>
              {askVoiceError && (
                <div className="hm-global-ask-voice-note">{askVoiceError}</div>
              )}

              {askError && (
                <div className="hm-global-ask-error">{askError}</div>
              )}
            </div>
          )}
        </div>
      )}
    </main>
  );
}

function App() {
  useGlobalSheetScrollLock();
  const isImpersonationHandoff =
    typeof window !== "undefined" &&
    window.location.pathname === "/impersonate";

  if (isImpersonationHandoff) {
    return <ImpersonationHandoff />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/impersonate" element={<ImpersonationHandoff />} />
            <Route
              path="/family/invite/:token"
              element={<FamilyInvitePage />}
            />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/event/:slug" element={<PublicEventPage />} />
            <Route path="/lost-pet/:token" element={<LostPetPublicPage />} />
            <Route
              path="/shared/:shareId"
              element={
                <RequireAuth>
                  <HouseMindShell />
                </RequireAuth>
              }
            >
              <Route index element={<SharedContentViewer />} />
            </Route>
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <HouseMindShell />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="home" element={<NavigationAreaPage area="home" />} />
              <Route
                path="plans"
                element={<NavigationAreaPage area="plans" />}
              />
              <Route
                path="money"
                element={<NavigationAreaPage area="money" />}
              />
              <Route path="money/overview" element={<MoneyOverviewPage />} />
              <Route path="food" element={<FoodPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="timeline" element={<TimelinePage />} />
              <Route path="connections" element={<ConnectionsPage />} />
              <Route path="trip/:eventType/:eventId" element={<TripPage />} />
              <Route
                path="event-plan/:eventType/:eventId"
                element={<EventPlanPage />}
              />
              <Route path="people" element={<PeoplePage />} />
              <Route
                path="people/:personId/gifts/:occasionId"
                element={<GiftSuggestPage />}
              />
              <Route path="people/:personId" element={<PersonDetail />} />
              <Route path="home-vault" element={<HomeVaultPage />} />
              <Route path="home-care" element={<HomeCarePage />} />
              <Route
                path="property-profile"
                element={<PropertyProfilePage />}
              />
              <Route
                path="emergency-safety"
                element={<EmergencySafetyPage />}
              />
              <Route path="pets" element={<PetsPage />} />
              <Route path="vehicles" element={<VehiclesPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="utilities" element={<UtilitiesPage />} />
              <Route path="family" element={<FamilyPage />} />
              <Route path="guide" element={<GuidePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="admin"
                element={
                  <RequireAdmin>
                    <AdminDashboard />
                  </RequireAdmin>
                }
              />
              <Route
                path="smart-home"
                element={
                  <RequireAdmin>
                    <SmartHomePage />
                  </RequireAdmin>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <VersionAutoUpdater />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
